import type {
  TesterAnwendung,
  TesterAnwendungWrite,
  TesterReviewer,
  TesterSlug,
  TesterUebersicht,
  TesterZugang,
  TesterZugangWrite,
} from "@/types/tester"
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios"
import type {
  Organization,
  OrganizationListItem,
  OrganizationMapMarker,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationNinoxTeam,
  OrganizationNinoxTeamCreate,
  OrganizationRelationship,
  OrganizationRelationshipCreate,
  OrganizationRelationshipUpdate,
  OrganizationRelationshipTypeCreate,
  OrganizationRelationshipTypeItem,
  OrganizationRelationshipTypeUpdate,
  Contact,
  ContactListItem,
  ContactCreate,
  ContactUpdate,
  Address,
  AddressCreate,
  Communication,
  CommunicationCreate,
  CommunicationUpdate,
  CommunicationDimensions,
  CommunicationMedium,
  CommunicationChannel,
  CommunicationContext,
  CommunicationDevice,
  PaginatedResponse,
  ApiError,
  UserListItem,
  UserDetail,
  UserCreate,
  UserUpdate,
  Role,
  ProviderTestResult,
  AIProviderPromptTestResult,
  AIProviderStatus,
  AIServiceAssignment,
  Tag,
  TagCreate,
  TagUpdate,
  MatrixAdminUser,
  MatrixAdminUserList,
  MatrixAdminUserCreate,
  MatrixAdminUserUpdate,
  MatrixAdminRoomList,
  MatrixRoomDetail,
  MatrixServerInfo,
  CompanySize,
  CompanySizeCreate,
  CompanySizeUpdate,
  LegalForm,
  LegalFormCreate,
  LegalFormUpdate,
  Industry,
  IndustryCreate,
  IndustryUpdate,
  ContactType,
  ContactTypeCreate,
  ContactTypeUpdate,
  ProfessionCategory,
  ProfessionCategoryCreate,
  ProfessionCategoryUpdate,
  Title,
  TitleCreate,
  TitleUpdate,
  ContactRole,
  ContactRoleCreate,
  ContactRoleUpdate,
  DepartmentTemplate,
  DepartmentTemplateCreate,
  DepartmentTemplateUpdate,
  FunctionTemplate,
  FunctionTemplateCreate,
  FunctionTemplateUpdate,
  OrganizationDepartment,
  OrganizationDepartmentCreate,
  OrganizationDepartmentUpdate,
  OrganizationFunction,
  OrganizationFunctionCreate,
  OrganizationFunctionUpdate,
  OrganizationContactLink,
  // Ninox Types (Server-Registry)
  NinoxServer,
  ContactNinoxTeams,
  NinoxServerListItem,
  NinoxServerCreate,
  NinoxServerUpdate,
  NinoxConnectionTestResult,
  PublishedPage,
  PublishedPageFileItem,
  PublishedPageListItem,
  PublishedPagePermission,
  PublishedPageCreate,
  PublishedPageUpdate,
  PublishedPageUploadResponse,
} from "@/types"
import type { User, TenantInfo } from "@/store/authStore"
import type { BulkImportResult } from "@/types/ai-import"
import type {
  Ticket,
  TicketListItem,
  TicketCreate,
  TicketUpdate,
  TicketMessage,
  TicketMessageCreate,
  TicketLogEntry,
  KanbanData,
  UnreadCount,
} from "@/types/tickets"
import type {
  ForgejoConnectionTest,
  LinkedTask,
  TaskBoard as TaskBoardItem,
  TaskCard as TaskBoardCard,
  TaskColumn as TaskBoardColumn,
  TaskComment as TaskBoardComment,
  TaskCreatePayload,
  TaskDetail as TaskBoardDetail,
  TaskKanban,
  TaskTakeoverPayload,
  TaskTakeoverResult,
  TaskUpdatePayload,
} from "@/types/tasks"

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || "/api/v1"

export interface OrganizationContactAiImportItem {
  id?: string | null
  name: string
  action: "created" | "merged" | "linked" | "skipped"
  linked: boolean
  communicationsCreated: number
  addressesCreated: number
  addressesUpdated: number
  duplicateConfidence?: number | null
  warnings: string[]
}

export interface OrganizationContactAiImportComparison {
  field: string
  label: string
  importedValue: string
  existingValue: string
  status: "same" | "different" | "missing_existing" | "missing_import"
}

export interface OrganizationContactAiImportCommunication {
  mediumName?: string | null
  mediumId?: string | null
  channelName?: string | null
  channelId?: string | null
  deviceName?: string | null
  deviceId?: string | null
  contextIds: string[]
  contextNames: string[]
  value?: string | null
  label?: string | null
  notes?: string | null
  isPrimary: boolean
  allowContact: boolean
}

export interface OrganizationContactAiImportAddress {
  entityType: "organization" | "contact"
  addressTypeId?: string | null
  addressTypeIds: string[]
  addressTypeName?: string | null
  addressTypeNames: string[]
  street?: string | null
  houseNumber?: string | null
  additionalInfo?: string | null
  postalCode?: string | null
  city?: string | null
  district?: string | null
  county?: string | null
  state?: string | null
  stateCode?: string | null
  country?: string | null
  countryCode?: string | null
  countryCodeAlpha3?: string | null
  isPrimary: boolean
}

export interface OrganizationContactAiImportContactPayload {
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  salutation?: string | null
  title?: string | null
  titleId?: string | null
  position?: string | null
  department?: string | null
  notes?: string | null
  contactTypeId?: string | null
  professionCategoryId?: string | null
  professionTitle?: string | null
  organizationLink: {
    role?: string | null
    roleIds: string[]
    roleNames: string[]
    department?: string | null
    departmentId?: string | null
    departmentName?: string | null
    functionId?: string | null
    functionName?: string | null
    isPrimary: boolean
    isMatrixManager: boolean
  }
  communications: OrganizationContactAiImportCommunication[]
  addresses: OrganizationContactAiImportAddress[]
}

export interface OrganizationContactAiImportExistingContact {
  id: string
  name: string
  salutation?: string | null
  title?: string | null
  firstName?: string | null
  lastName?: string | null
  position?: string | null
  department?: string | null
  communications: OrganizationContactAiImportCommunication[]
}

export interface OrganizationContactAiImportPreviewContact {
  tempId: string
  name: string
  imported: OrganizationContactAiImportContactPayload
  existing?: OrganizationContactAiImportExistingContact | null
  duplicateConfidence?: number | null
  comparisons: OrganizationContactAiImportComparison[]
  warnings: string[]
}

export interface OrganizationContactAiImportResult {
  success: boolean
  provider?: string | null
  model?: string | null
  createdCount: number
  mergedCount: number
  linkedCount: number
  skippedCount: number
  communicationsCreated: number
  addressesCreated: number
  addressesUpdated: number
  contacts: OrganizationContactAiImportItem[]
  previewContacts: OrganizationContactAiImportPreviewContact[]
  warnings: string[]
  errors: string[]
  dbStructure?: Record<string, unknown>
}

export interface QuickSearchResult {
  id: string
  type: "organization" | "contact" | "ticket" | "project" | string
  title: string
  subtitle?: string | null
  href: string
  score: number
}

// Token Provider für Logto-Integration
let _tokenProvider: (() => Promise<string | undefined>) | null = null

export function setTokenProvider(fn: () => Promise<string | undefined>) {
  _tokenProvider = fn
}

export function getTokenProvider() {
  return _tokenProvider
}

// Auth Store importieren (lazy um zirkuläre Deps zu vermeiden)
const getAuthStore = () => import("@/store/authStore").then((m) => m.useAuthStore)

function normalizeUserPreferences(raw: (UserPreferences & { sidebar_favorites?: string[]; grid_views?: Record<string, GridView[]> }) | null | undefined): UserPreferences {
  const preferences = raw ?? {}
  return {
    ...preferences,
    sidebarFavorites: Array.isArray(preferences.sidebarFavorites)
      ? preferences.sidebarFavorites
      : Array.isArray(preferences.sidebar_favorites)
        ? preferences.sidebar_favorites
        : undefined,
    gridViews: preferences.gridViews ?? preferences.grid_views ?? undefined,
  }
}

class ApiService {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    })

    // Request interceptor: Logto token + Tenant-ID
    this.client.interceptors.request.use(async (config) => {
      try {
        // Token von Logto SDK holen
        if (_tokenProvider) {
          const token = await _tokenProvider()
          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
        }

        // Tenant-ID aus Auth Store
        const store = await getAuthStore()
        const tenantId = store.getState().tenantId
        if (tenantId) {
          config.headers['X-Tenant-Id'] = tenantId
        }
      } catch {
        // Ignore errors during SSR or before store is ready
      }
      return config
    })

    // Response interceptor for error handling
    // KEIN automatischer Logout bei 401 - das verursacht Redirect-Loops
    // Der AuthProvider handhabt den Auth-State über Logto SDK
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        return Promise.reject(error)
      }
    )
  }

  // =========================================================================
  // AUTH ENDPOINTS
  // =========================================================================

  auth = {
    getMe: async (): Promise<User> => {
      return this.request({
        method: "GET",
        url: "/auth/me",
      })
    },

    getMyTenants: async (): Promise<TenantInfo[]> => {
      return this.request({
        method: "GET",
        url: "/auth/me/tenants",
      })
    },

    getProfile: async (): Promise<UserProfile> => {
      return this.request({
        method: "GET",
        url: "/auth/me",
      })
    },

    updateProfile: async (data: {
      firstName?: string
      lastName?: string
      timezone?: string | null
      gmailMonitoringEnabled?: boolean
      meetUrl?: string | null
    }): Promise<UserProfile> => {
      const payload: Record<string, unknown> = {}
      if (data.firstName !== undefined) payload.first_name = data.firstName
      if (data.lastName !== undefined) payload.last_name = data.lastName
      if (data.timezone !== undefined) payload.timezone = data.timezone
      if (data.gmailMonitoringEnabled !== undefined) payload.gmail_monitoring_enabled = data.gmailMonitoringEnabled
      if (data.meetUrl !== undefined) payload.meet_url = data.meetUrl
      return this.request({
        method: "PATCH",
        url: "/auth/me",
        data: payload,
      })
    },

    getEmailAccounts: async (): Promise<UserEmailAccount[]> => {
      return this.request({
        method: "GET",
        url: "/auth/me/email-accounts",
      })
    },

    getSshAccess: async (): Promise<ProjectbaseSshAccess> => {
      return this.request({
        method: "GET",
        url: "/auth/me/ssh-access",
      })
    },

    activateSshAccess: async (): Promise<ProjectbaseSshAccess> => {
      return this.request({
        method: "POST",
        url: "/auth/me/ssh-access",
      })
    },

    testSshAccess: async (): Promise<ProjectbaseSshAccessTest> => {
      return this.request({
        method: "POST",
        url: "/auth/me/ssh-access/test",
      })
    },

    addSshKey: async (data: { label: string; publicKey: string }): Promise<ProjectbaseSshKey> => {
      return this.request({
        method: "POST",
        url: "/auth/me/ssh-keys",
        data,
      })
    },

    deleteSshKey: async (keyId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/auth/me/ssh-keys/${keyId}`,
      })
    },

    addDelegatedEmailAccount: async (email: string): Promise<UserEmailAccount> => {
      return this.request({
        method: "POST",
        url: "/auth/me/email-accounts/delegates",
        data: { email },
      })
    },

    deleteEmailAccount: async (accountId: string): Promise<{ success: boolean }> => {
      return this.request({
        method: "DELETE",
        url: `/auth/me/email-accounts/${accountId}`,
      })
    },

    getApiTokens: async (): Promise<ApiTokenItem[]> => {
      return this.request({
        method: "GET",
        url: "/auth/me/api-tokens",
      })
    },

    createApiToken: async (data: { name: string; expiresAt?: string | null }): Promise<ApiTokenCreateResponse> => {
      return this.request({
        method: "POST",
        url: "/auth/me/api-tokens",
        data: {
          name: data.name,
          expires_at: data.expiresAt || null,
        },
      })
    },

    revokeApiToken: async (tokenId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/auth/me/api-tokens/${tokenId}`,
      })
    },

    getEmailSummaryGoogleSetup: async (): Promise<EmailSummaryGoogleSetupStatus> => {
      return this.request({
        method: "GET",
        url: "/auth/me/email-summary-google-setup",
      })
    },

    setupEmailSummaryGoogleSheet: async (): Promise<EmailSummaryGoogleSetupStatus> => {
      return this.request({
        method: "POST",
        url: "/auth/me/email-summary-google-setup",
      })
    },

    getPreferences: async (): Promise<UserPreferences> => {
      const preferences = await this.request<UserPreferences & { sidebar_favorites?: string[] }>({
        method: "GET",
        url: "/auth/me/preferences",
      })
      return normalizeUserPreferences(preferences)
    },

    updatePreferences: async (data: {
      filters?: Record<string, FilterState>
      sidebarFavorites?: string[]
      gridViews?: Record<string, GridView[]>
    }): Promise<UserPreferences> => {
      const preferences = await this.request<UserPreferences & { sidebar_favorites?: string[] }>({
        method: "PATCH",
        url: "/auth/me/preferences",
        data,
      })
      return normalizeUserPreferences(preferences)
    },

    getMatrixStatus: async (): Promise<MatrixOnboardingStatus> => {
      return this.request({
        method: "GET",
        url: "/auth/me/matrix",
      })
    },

    activateMatrix: async (): Promise<MatrixOnboardingStatus> => {
      return this.request({
        method: "POST",
        url: "/auth/me/matrix/activate",
      })
    },

    getKsuiteStatus: async (): Promise<{ isConnected: boolean; displayName: string | null; email: string | null }> => {
      return this.request({
        method: "GET",
        url: "/auth/me/ksuite",
      })
    },

    saveKsuiteToken: async (apiToken: string): Promise<{ isConnected: boolean; displayName: string | null; email: string | null }> => {
      return this.request({
        method: "PUT",
        url: "/auth/me/ksuite",
        data: { apiToken },
      })
    },

    deleteKsuiteToken: async (): Promise<{ success: boolean }> => {
      return this.request({
        method: "DELETE",
        url: "/auth/me/ksuite",
      })
    },

    getMailAppPasswords: async (): Promise<MailAppPasswordStatus> => {
      return this.request({
        method: "GET",
        url: "/auth/me/mail-app-passwords",
      })
    },

    createMailAppPassword: async (description: string): Promise<MailAppPasswordCreateResponse> => {
      return this.request({
        method: "POST",
        url: "/auth/me/mail-app-passwords",
        data: { description },
      })
    },

    deleteMailAppPassword: async (id: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/auth/me/mail-app-passwords/${id}`,
      })
    },
  }

  opencami = {
    createSession: async (): Promise<{ url: string; expires_at: string; session_key?: string | null; chat_mode?: "separate" | "shared" }> => {
      return this.request({
        method: "POST",
        url: "/opencami/session",
      })
    },

    deleteSession: async (): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: "/opencami/session",
      })
    },

    getIdentity: async (): Promise<{
      user_id: string
      email: string
      chat_mode: "separate" | "shared"
      opencami_session_key: string
      active_session_key: string
      signal_sender_id: string | null
      signal_session_key: string | null
      shared_active: boolean
    }> => {
      return this.request({
        method: "GET",
        url: "/opencami/identity",
      })
    },

    updateIdentity: async (data: { chat_mode: "separate" | "shared" }): Promise<{
      user_id: string
      email: string
      chat_mode: "separate" | "shared"
      opencami_session_key: string
      active_session_key: string
      signal_sender_id: string | null
      signal_session_key: string | null
      shared_active: boolean
    }> => {
      return this.request({
        method: "PATCH",
        url: "/opencami/identity",
        data,
      })
    },

    getSignalStatus: async (): Promise<{
      account: string | null
      registered: boolean
      accounts: string[]
      devices_output: string | null
      link_uri: string | null
      link_active: boolean
      log_tail: string | null
    }> => {
      return this.request({
        method: "GET",
        url: "/opencami/signal/status",
      })
    },

    startSignalLink: async (): Promise<{
      started: boolean
      account: string | null
      registered: boolean
      accounts: string[]
      devices_output: string | null
      link_uri: string | null
      link_active: boolean
      log_tail: string | null
    }> => {
      return this.request({
        method: "POST",
        url: "/opencami/signal/link",
      })
    },

    registerAccount: async (data: { account: string; voice?: boolean; captcha?: string }): Promise<{
      account: string | null
      registered: boolean
      accounts: string[]
      devices_output: string | null
      link_uri: string | null
      link_active: boolean
      log_tail: string | null
      output: string | null
    }> => {
      return this.request({
        method: "POST",
        url: "/opencami/signal/register",
        data,
      })
    },

    verifyAccount: async (data: { account: string; code: string; pin?: string }): Promise<{
      account: string | null
      registered: boolean
      accounts: string[]
      devices_output: string | null
      link_uri: string | null
      link_active: boolean
      log_tail: string | null
      output: string | null
    }> => {
      return this.request({
        method: "POST",
        url: "/opencami/signal/verify",
        data,
      })
    },

    resetAccount: async (data: { account: string; unregister?: boolean; confirm: string }): Promise<{
      account: string | null
      registered: boolean
      accounts: string[]
      devices_output: string | null
      link_uri: string | null
      link_active: boolean
      log_tail: string | null
      reset: boolean
      unregistered: boolean
      output: string | null
    }> => {
      return this.request({
        method: "POST",
        url: "/opencami/signal/reset",
        data,
      })
    },

    getPairingRequests: async (): Promise<{
      channel: string
      requests: Record<string, unknown>[]
      output: string | null
    }> => {
      return this.request({
        method: "GET",
        url: "/opencami/signal/pairings",
      })
    },

    approvePairing: async (data: { code: string; notify?: boolean; link_to_current_user?: boolean; chat_mode?: "separate" | "shared" }): Promise<{
      channel: string
      requests: Record<string, unknown>[]
      output: string | null
      approved: boolean
    }> => {
      return this.request({
        method: "POST",
        url: "/opencami/signal/pairings/approve",
        data,
      })
    },

    sendInvite: async (data: { recipient: string; message?: string; expires_hours?: number }): Promise<{
      recipient: string
      invite_url: string
      expires_at: string
      output: string | null
    }> => {
      return this.request({
        method: "POST",
        url: "/opencami/signal/invites",
        data,
      })
    },
  }

  // Generic request method
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.request<T>(config)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { detail?: unknown; message?: unknown } | undefined
        const detail = data?.detail
        const message = typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((item: any) => item?.msg || item?.message || JSON.stringify(item)).join("; ")
            : typeof data?.message === "string"
              ? data.message
              : error.message
        const apiError = new Error(message) as Error & {
          response?: typeof error.response
          status?: number
        }
        apiError.response = error.response
        apiError.status = error.response?.status
        throw apiError
      }
      throw error
    }
  }

  // Public branding configuration
  branding = {
    get: async (): Promise<BrandingConfig> => {
      return this.request({
        method: "GET",
        url: "/branding",
      })
    },
  }

  async quickSearch(q: string, limit = 20): Promise<QuickSearchResult[]> {
    return this.request({
      method: "GET",
      url: "/search/quick",
      params: { q, limit },
    })
  }

  // Organizations
  // App-Switcher (Header): rechte-gefilterte Liste externer Anwendungen
  async getApps(): Promise<{ label: string; url: string }[]> {
    return this.request({ method: "GET", url: "/apps" })
  }

  async getOrganizations(params?: {
    skip?: number
    limit?: number
    search?: string
    companySizeId?: string
    legalFormId?: string
    industryId?: string
    type?: string
    isActive?: boolean
    tagIds?: string[]
    sortBy?: string
    sortDir?: "asc" | "desc"
  }): Promise<PaginatedResponse<OrganizationListItem>> {
    return this.request({
      method: "GET",
      url: "/organizations",
      params: params ? {
        skip: params.skip,
        limit: params.limit,
        search: params.search,
        company_size_id: params.companySizeId,
        legal_form_id: params.legalFormId,
        industry_id: params.industryId,
        type: params.type,
        is_active: params.isActive,
        tag_ids: params.tagIds,
        sort_by: params.sortBy,
        sort_dir: params.sortDir,
      } : undefined,
    })
  }

  async getOrganizationMapMarkers(params?: {
    isActive?: boolean | null
  }): Promise<OrganizationMapMarker[]> {
    return this.request({
      method: "GET",
      url: "/organizations/map-markers",
      params: params ? { is_active: params.isActive } : undefined,
    })
  }

  async getOrganization(id: string): Promise<Organization> {
    return this.request({
      method: "GET",
      url: `/organizations/${id}`,
    })
  }

  async createOrganization(data: OrganizationCreate): Promise<Organization> {
    return this.request({
      method: "POST",
      url: "/organizations",
      data,
    })
  }

  async updateOrganization(
    id: string,
    data: OrganizationUpdate
  ): Promise<Organization> {
    return this.request({
      method: "PUT",
      url: `/organizations/${id}`,
      data,
    })
  }

  async listOrganizationRelationships(id: string): Promise<OrganizationRelationship[]> {
    return this.request({
      method: "GET",
      url: `/organizations/${id}/relationships`,
    })
  }

  async createOrganizationRelationship(
    id: string,
    data: OrganizationRelationshipCreate
  ): Promise<OrganizationRelationship> {
    return this.request({
      method: "POST",
      url: `/organizations/${id}/relationships`,
      data,
    })
  }

  async updateOrganizationRelationship(
    id: string,
    relationshipId: string,
    data: OrganizationRelationshipUpdate
  ): Promise<OrganizationRelationship> {
    return this.request({
      method: "PUT",
      url: `/organizations/${id}/relationships/${relationshipId}`,
      data,
    })
  }

  async deleteOrganizationRelationship(id: string, relationshipId: string): Promise<void> {
    return this.request({
      method: "DELETE",
      url: `/organizations/${id}/relationships/${relationshipId}`,
    })
  }

  async listActiveOrganizationRelationshipTypes(): Promise<OrganizationRelationshipTypeItem[]> {
    return this.request({
      method: "GET",
      url: "/organizations/relationship-types",
    })
  }

  async getDokubaseFreigaben(contactId: string): Promise<DokubaseFreigaben> {
    return this.request({
      method: "GET",
      url: `/contacts/${contactId}/dokubase-freigaben`,
    })
  }

  async setDokubaseFreigaben(
    contactId: string,
    freigaben: { typ: "regal" | "buch"; itemId: number }[],
    einladen: boolean,
  ): Promise<DokubaseFreigaben> {
    return this.request({
      method: "PUT",
      url: `/contacts/${contactId}/dokubase-freigaben`,
      data: { freigaben, einladen },
    })
  }

  async getUploadLinks(contactId: string): Promise<UploadLinkInfo[]> {
    return this.request({
      method: "GET",
      url: `/contacts/${contactId}/upload-links`,
    })
  }

  async createUploadLink(
    contactId: string,
    data: { email: string; nachricht?: string; tage?: number; einladen: boolean },
  ): Promise<{ id: string; url: string; expiresAt: string; emailSent: boolean | null }> {
    return this.request({
      method: "POST",
      url: `/contacts/${contactId}/upload-links`,
      data,
    })
  }

  async revokeUploadLink(contactId: string, linkId: string): Promise<void> {
    return this.request({
      method: "DELETE",
      url: `/contacts/${contactId}/upload-links/${linkId}`,
    })
  }

  async retryUploadVorgang(contactId: string, vorgangId: string): Promise<void> {
    return this.request({
      method: "POST",
      url: `/contacts/${contactId}/upload-vorgaenge/${vorgangId}/erneut`,
    })
  }

  async getContactNinoxTeams(contactId: string): Promise<ContactNinoxTeams> {
    return this.request({
      method: "GET",
      url: `/contacts/${contactId}/ninox-teams`,
    })
  }

  async updateContactNinoxTeams(
    contactId: string,
    ninoxTeamIds: string[],
    allTickets?: boolean,
  ): Promise<ContactNinoxTeams> {
    return this.request({
      method: "PUT",
      url: `/contacts/${contactId}/ninox-teams`,
      data: { ninoxTeamIds, ...(allTickets === undefined ? {} : { allTickets }) },
    })
  }

  async listOrganizationNinoxTeams(id: string): Promise<OrganizationNinoxTeam[]> {
    return this.request({
      method: "GET",
      url: `/organizations/${id}/ninox-teams`,
    })
  }

  async createOrganizationNinoxTeam(
    id: string,
    data: OrganizationNinoxTeamCreate
  ): Promise<OrganizationNinoxTeam> {
    return this.request({
      method: "POST",
      url: `/organizations/${id}/ninox-teams`,
      data,
    })
  }

  async deleteOrganizationNinoxTeam(id: string, teamLinkId: string): Promise<void> {
    return this.request({
      method: "DELETE",
      url: `/organizations/${id}/ninox-teams/${teamLinkId}`,
    })
  }

  async analyzeOrganizationPublicInfoGoogle(id: string, mode: "quick" | "deep" = "quick"): Promise<{
    publicInfoMarkdown: string
    provider: string
    model: string
    mode: string
    sources: string[]
    searchQueries: string[]
    grounded: boolean
  }> {
    return this.request({
      method: "POST",
      url: `/organizations/${id}/ai-public-info-google`,
      params: { mode },
      timeout: mode === "deep" ? 180000 : 60000,
    })
  }

  async deleteOrganization(id: string): Promise<void> {
    return this.request({
      method: "DELETE",
      url: `/organizations/${id}`,
    })
  }

  async getOrganizationContacts(id: string): Promise<Contact[]> {
    return this.request({
      method: "GET",
      url: `/organizations/${id}/contacts`,
    })
  }

  async getOrganizationAddresses(id: string): Promise<Address[]> {
    return this.request({
      method: "GET",
      url: `/organizations/${id}/addresses`,
    })
  }

  async addOrganizationContact(
    orgId: string,
    data: {
      contactId: string
      role?: string
      department?: string
      departmentId?: string
      functionId?: string
      isPrimary?: boolean
    }
  ): Promise<unknown> {
    return this.request({
      method: "POST",
      url: `/organizations/${orgId}/contacts`,
      data: {
        contact_id: data.contactId,
        role: data.role,
        department: data.department,
        department_id: data.departmentId,
        function_id: data.functionId,
        is_primary: data.isPrimary,
      },
    })
  }

  async removeOrganizationContact(orgId: string, contactId: string): Promise<void> {
    return this.request({
      method: "DELETE",
      url: `/organizations/${orgId}/contacts/${contactId}`,
    })
  }

  async updateOrganizationContact(
    orgId: string,
    contactId: string,
    data: {
      departmentId?: string | null
      functionId?: string | null
      isPrimary?: boolean
    }
  ): Promise<OrganizationContactLink> {
    return this.request({
      method: "PUT",
      url: `/organizations/${orgId}/contacts/${contactId}`,
      data: {
        department_id: data.departmentId,
        function_id: data.functionId,
        is_primary: data.isPrimary,
      },
    })
  }

  async mergeOrganizationContacts(
    orgId: string,
    winnerContactId: string,
    loserContactIds: string[],
  ): Promise<{ success: boolean; winnerContactId: string; mergedContactIds: string[] }> {
    return this.request({
      method: "POST",
      url: `/organizations/${orgId}/contacts/merge`,
      data: {
        winnerContactId,
        loserContactIds,
      },
    })
  }

  async importOrganizationContactsAi(
    orgId: string,
    data: { sourceText: string; autoMerge?: boolean },
  ): Promise<OrganizationContactAiImportResult> {
    return this.request({
      method: "POST",
      url: `/organizations/${orgId}/contacts/ai-import`,
      data,
      timeout: 180000,
    })
  }

  async previewOrganizationContactsAi(
    orgId: string,
    data: { sourceText: string; autoMerge?: boolean },
  ): Promise<OrganizationContactAiImportResult> {
    return this.request({
      method: "POST",
      url: `/organizations/${orgId}/contacts/ai-preview`,
      data,
      timeout: 180000,
    })
  }

  // Contacts
  async getContacts(params?: {
    skip?: number
    limit?: number
    search?: string
    contactTypeId?: string
    organizationId?: string
    isActive?: boolean
    sortBy?: string
    sortDir?: "asc" | "desc"
  }): Promise<PaginatedResponse<ContactListItem>> {
    return this.request({
      method: "GET",
      url: "/contacts",
      params: params ? {
        skip: params.skip,
        limit: params.limit,
        search: params.search,
        contact_type_id: params.contactTypeId,
        organization_id: params.organizationId,
        is_active: params.isActive,
        sort_by: params.sortBy,
        sort_dir: params.sortDir,
      } : undefined,
    })
  }

  async getContact(id: string): Promise<Contact> {
    return this.request({
      method: "GET",
      url: `/contacts/${id}`,
    })
  }

  async createContact(data: ContactCreate): Promise<Contact> {
    return this.request({
      method: "POST",
      url: "/contacts",
      data,
    })
  }

  async updateContact(id: string, data: ContactUpdate): Promise<Contact> {
    return this.request({
      method: "PUT",
      url: `/contacts/${id}`,
      data,
    })
  }

  async deleteContact(id: string): Promise<void> {
    return this.request({
      method: "DELETE",
      url: `/contacts/${id}`,
    })
  }

  async promoteContactToUser(
    contactId: string,
    data: { roleIds: string[]; email?: string }
  ): Promise<{ userId: string; email: string; temporaryPassword: string; contactId: string }> {
    return this.request({
      method: "POST",
      url: `/contacts/${contactId}/promote-to-user`,
      data,
    })
  }

  async createContactPortalAccess(
    contactId: string,
    data: { email?: string; organizationId?: string; aiAccessEnabled?: boolean; ninoxTeamIds?: string[] }
  ): Promise<{
    portalCustomerId: string
    email: string
    contactId: string
    organizationId: string | null
    aiAccessEnabled: boolean
    ninoxTeamIds: string[]
    nxtoolsAccountCreated: boolean | null
    nxtoolsRecoveryUrl: string | null
    nxtoolsEmailSent: boolean
    nxtoolsWarnings: string[]
  }> {
    return this.request({
      method: "POST",
      url: `/contacts/${contactId}/portal-access`,
      data,
    })
  }

  // ---------------------------------------------------------------------
  // Testerwerk (Zielbild zentrale Erfassung, testerwerk#61): das ERP führt
  // Organisationen (Slug), Anwendungen und Tester-Zugänge.
  // ---------------------------------------------------------------------
  testerwerk = {
    setzeSlug: (organizationId: string, testerSlug: string) =>
      this.request<TesterSlug>({
        method: "PUT",
        url: `/organizations/${organizationId}/tester-slug`,
        data: { testerSlug },
      }),
    listAnwendungen: (organizationId: string) =>
      this.request<TesterAnwendung[]>({
        method: "GET",
        url: `/organizations/${organizationId}/anwendungen`,
      }),
    createAnwendung: (organizationId: string, data: TesterAnwendungWrite) =>
      this.request<TesterAnwendung>({
        method: "POST",
        url: `/organizations/${organizationId}/anwendungen`,
        data,
      }),
    updateAnwendung: (organizationId: string, id: string, data: TesterAnwendungWrite) =>
      this.request<TesterAnwendung>({
        method: "PUT",
        url: `/organizations/${organizationId}/anwendungen/${id}`,
        data,
      }),
    deaktiviereAnwendung: (organizationId: string, id: string) =>
      this.request<void>({
        method: "DELETE",
        url: `/organizations/${organizationId}/anwendungen/${id}`,
      }),
    getZugang: (contactId: string) =>
      this.request<TesterZugang>({ method: "GET", url: `/contacts/${contactId}/tester-zugang` }),
    saveZugang: (contactId: string, data: TesterZugangWrite) =>
      this.request<TesterZugang>({
        method: "POST",
        url: `/contacts/${contactId}/tester-zugang`,
        data,
      }),
    einladen: (contactId: string) =>
      this.request<TesterZugang>({
        method: "POST",
        url: `/contacts/${contactId}/tester-zugang/einladen`,
      }),
    sperren: (contactId: string) =>
      this.request<TesterZugang>({
        method: "POST",
        url: `/contacts/${contactId}/tester-zugang/sperren`,
      }),
    entsperren: (contactId: string) =>
      this.request<TesterZugang>({
        method: "POST",
        url: `/contacts/${contactId}/tester-zugang/entsperren`,
      }),
    entfernen: (contactId: string) =>
      this.request<TesterZugang>({
        method: "DELETE",
        url: `/contacts/${contactId}/tester-zugang`,
      }),
    uebersicht: (pruefen = false) =>
      this.request<TesterUebersicht>({
        method: "GET",
        url: "/admin/tester/uebersicht",
        params: pruefen ? { pruefen: true } : undefined,
      }),
    listReviewer: () =>
      this.request<TesterReviewer[]>({ method: "GET", url: "/admin/tester/reviewer" }),
    setReviewerRolle: (email: string, rolle: TesterReviewer["rolle"] | "keine") =>
      this.request<TesterReviewer>({
        method: "PUT",
        url: "/admin/tester/reviewer",
        data: { email, rolle },
      }),
  }

  async exportContactGDPR(id: string): Promise<Record<string, unknown>> {
    return this.request({
      method: "POST",
      url: `/contacts/${id}/gdpr-export`,
    })
  }

  // Addresses
  async createAddress(data: AddressCreate): Promise<Address> {
    return this.request({
      method: "POST",
      url: "/addresses",
      data,
    })
  }

  async getAddress(id: string): Promise<Address> {
    return this.request({
      method: "GET",
      url: `/addresses/${id}`,
    })
  }

  async updateAddress(
    id: string,
    data: Partial<AddressCreate>
  ): Promise<Address> {
    return this.request({
      method: "PUT",
      url: `/addresses/${id}`,
      data,
    })
  }

  async deleteAddress(id: string): Promise<void> {
    return this.request({
      method: "DELETE",
      url: `/addresses/${id}`,
    })
  }

  async validateAddress(id: string): Promise<{
    isValid: boolean
    confidence: number
    message?: string
    formattedAddress?: string
    latitude?: number
    longitude?: number
  }> {
    return this.request({
      method: "POST",
      url: `/addresses/${id}/validate`,
    })
  }

  async searchNearbyAddresses(params: {
    latitude: number
    longitude: number
    radiusKm?: number
    limit?: number
  }): Promise<{ address: Address; distanceKm: number }[]> {
    return this.request({
      method: "GET",
      url: "/addresses/search/nearby",
      params,
    })
  }

  // Communications
  async getCommunications(params?: {
    entityType?: "organization" | "contact"
    entityId?: string
  }): Promise<Communication[]> {
    // Backend filtert über snake_case-Query-Params (entity_type/entity_id).
    return this.request({
      method: "GET",
      url: "/communications",
      params: params
        ? { entity_type: params.entityType, entity_id: params.entityId }
        : undefined,
    })
  }

  async getCommunicationDimensions(): Promise<CommunicationDimensions> {
    return this.request({
      method: "GET",
      url: "/communications/dimensions",
    })
  }

  // -------------------------------------------------------------- Secure Share
  async createSecureSecret(formData: FormData): Promise<{
    id: string
    url: string
    expiresAt: string
    maxViews: number
  }> {
    const response = await this.client.post("/secure/secrets", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    return response.data
  }

  async sendSecureSecretMail(
    secretId: string,
    data: { to: string; url: string; message?: string },
  ): Promise<{ sent: boolean }> {
    return this.request({
      method: "POST",
      url: `/secure/secrets/${secretId}/send-mail`,
      data,
    })
  }

  async getSecureShareConfig(): Promise<SecureShareConfig> {
    return this.request({ method: "GET", url: "/admin/settings/secure-share" })
  }

  async updateSecureShareConfig(
    data: Partial<SecureShareConfig>,
  ): Promise<SecureShareConfig> {
    return this.request({ method: "PUT", url: "/admin/settings/secure-share", data })
  }

  async createSecureInvite(data: {
    label?: string
    recipientEmail?: string
    ttlHours?: number
    maxUses?: number
    allowFiles?: boolean
  }): Promise<{ id: string; url: string; expiresAt: string }> {
    return this.request({ method: "POST", url: "/secure/invites", data })
  }

  async listSecureInvites(): Promise<SecureInvite[]> {
    return this.request({ method: "GET", url: "/secure/invites" })
  }

  async revokeSecureInvite(id: string): Promise<{ revoked: boolean }> {
    return this.request({ method: "DELETE", url: `/secure/invites/${id}` })
  }

  async sendSecureInviteMail(
    id: string,
    data: { to: string; url: string; message?: string },
  ): Promise<{ sent: boolean }> {
    return this.request({ method: "POST", url: `/secure/invites/${id}/send-mail`, data })
  }

  async getCommunicationChannelsByMedium(mediumId: string): Promise<CommunicationChannel[]> {
    return this.request({
      method: "GET",
      url: "/communications/channels",
      params: { medium_id: mediumId },
    })
  }

  async createCommunication(data: CommunicationCreate): Promise<Communication> {
    return this.request({
      method: "POST",
      url: "/communications",
      data,
    })
  }

  async updateCommunication(
    id: string,
    data: CommunicationUpdate
  ): Promise<Communication> {
    return this.request({
      method: "PUT",
      url: `/communications/${id}`,
      data,
    })
  }

  async deleteCommunication(id: string): Promise<void> {
    return this.request({
      method: "DELETE",
      url: `/communications/${id}`,
    })
  }

  // Address Types (für Formulare - benötigt nur addresses:read)
  async getAddressTypes(): Promise<{ id: string; name: string; icon?: string; description?: string; isActive?: boolean; sortOrder?: number }[]> {
    return this.request({
      method: "GET",
      url: "/addresses/types",
    })
  }

  // Communication Dimensions (Admin)
  communicationMedia = {
    list: async (params?: { includeInactive?: boolean }): Promise<CommunicationMedium[]> => {
      return this.request({
        method: "GET",
        url: "/admin/communication-media",
        params: params ? { include_inactive: params.includeInactive } : undefined,
      })
    },
    get: async (id: string): Promise<CommunicationMedium> => {
      return this.request({
        method: "GET",
        url: `/admin/communication-media/${id}`,
      })
    },
    create: async (data: Partial<CommunicationMedium>): Promise<CommunicationMedium> => {
      return this.request({
        method: "POST",
        url: "/admin/communication-media",
        data,
      })
    },
    update: async (id: string, data: Partial<CommunicationMedium>): Promise<CommunicationMedium> => {
      return this.request({
        method: "PUT",
        url: `/admin/communication-media/${id}`,
        data,
      })
    },
    delete: async (id: string, permanent?: boolean): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/communication-media/${id}`,
        params: { permanent },
      })
    },
    reorder: async (itemIds: string[]): Promise<CommunicationMedium[]> => {
      return this.request({
        method: "POST",
        url: "/admin/communication-media/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  communicationChannels = {
    list: async (params?: { includeInactive?: boolean; mediumId?: string }): Promise<CommunicationChannel[]> => {
      return this.request({
        method: "GET",
        url: "/admin/communication-channels",
        params: params ? { include_inactive: params.includeInactive, medium_id: params.mediumId } : undefined,
      })
    },
    get: async (id: string): Promise<CommunicationChannel> => {
      return this.request({
        method: "GET",
        url: `/admin/communication-channels/${id}`,
      })
    },
    create: async (data: Partial<CommunicationChannel>): Promise<CommunicationChannel> => {
      return this.request({
        method: "POST",
        url: "/admin/communication-channels",
        data,
      })
    },
    update: async (id: string, data: Partial<CommunicationChannel>): Promise<CommunicationChannel> => {
      return this.request({
        method: "PUT",
        url: `/admin/communication-channels/${id}`,
        data,
      })
    },
    delete: async (id: string, permanent?: boolean): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/communication-channels/${id}`,
        params: { permanent },
      })
    },
    reorder: async (itemIds: string[]): Promise<CommunicationChannel[]> => {
      return this.request({
        method: "POST",
        url: "/admin/communication-channels/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  communicationContexts = {
    list: async (params?: { includeInactive?: boolean }): Promise<CommunicationContext[]> => {
      return this.request({
        method: "GET",
        url: "/admin/communication-contexts",
        params: params ? { include_inactive: params.includeInactive } : undefined,
      })
    },
    get: async (id: string): Promise<CommunicationContext> => {
      return this.request({
        method: "GET",
        url: `/admin/communication-contexts/${id}`,
      })
    },
    create: async (data: Partial<CommunicationContext>): Promise<CommunicationContext> => {
      return this.request({
        method: "POST",
        url: "/admin/communication-contexts",
        data,
      })
    },
    update: async (id: string, data: Partial<CommunicationContext>): Promise<CommunicationContext> => {
      return this.request({
        method: "PUT",
        url: `/admin/communication-contexts/${id}`,
        data,
      })
    },
    delete: async (id: string, permanent?: boolean): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/communication-contexts/${id}`,
        params: { permanent },
      })
    },
    reorder: async (itemIds: string[]): Promise<CommunicationContext[]> => {
      return this.request({
        method: "POST",
        url: "/admin/communication-contexts/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  communicationDevices = {
    list: async (params?: { includeInactive?: boolean }): Promise<CommunicationDevice[]> => {
      return this.request({
        method: "GET",
        url: "/admin/communication-devices",
        params: params ? { include_inactive: params.includeInactive } : undefined,
      })
    },
    get: async (id: string): Promise<CommunicationDevice> => {
      return this.request({
        method: "GET",
        url: `/admin/communication-devices/${id}`,
      })
    },
    create: async (data: Partial<CommunicationDevice>): Promise<CommunicationDevice> => {
      return this.request({
        method: "POST",
        url: "/admin/communication-devices",
        data,
      })
    },
    update: async (id: string, data: Partial<CommunicationDevice>): Promise<CommunicationDevice> => {
      return this.request({
        method: "PUT",
        url: `/admin/communication-devices/${id}`,
        data,
      })
    },
    delete: async (id: string, permanent?: boolean): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/communication-devices/${id}`,
        params: { permanent },
      })
    },
    reorder: async (itemIds: string[]): Promise<CommunicationDevice[]> => {
      return this.request({
        method: "POST",
        url: "/admin/communication-devices/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // Scraping & AI
  async scrapeUrl(
    url: string,
    validateAddresses: boolean = true,
    provider?: string,
    model?: string
  ): Promise<ScrapingResult> {
    return this.request({
      method: "POST",
      url: "/scrape/url",
      data: { url, validate_addresses: validateAddresses, provider, model },
    })
  }

  async parseSignature(
    signature: string,
    validateAddresses: boolean = true,
    provider?: string,
    model?: string
  ): Promise<ScrapingResult> {
    return this.request({
      method: "POST",
      url: "/scrape/signature",
      data: { signature, validate_addresses: validateAddresses, provider, model },
    })
  }

  async parseFreetext(
    text: string,
    validateAddresses: boolean = true,
    provider?: string,
    model?: string
  ): Promise<ScrapingResult> {
    return this.request({
      method: "POST",
      url: "/scrape/freetext",
      data: { text, validate_addresses: validateAddresses, provider, model },
    })
  }

  async bulkImportOrganization(data: {
    name: string
    organizationTypeId?: string
    description?: string
    website?: string
    categoryId?: string
    communications: Array<{
      mediumName: string
      mediumId?: string
      channelId?: string
      value: string
      label?: string
      isPrimary?: boolean
    }>
    addresses: Array<{
      street?: string
      houseNumber?: string
      additionalInfo?: string
      postalCode?: string
      city?: string
      district?: string
      county?: string
      state?: string
      stateCode?: string
      country?: string
      countryCode?: string
      countryCodeAlpha3?: string
      addressTypeId?: string
      latitude?: number
      longitude?: number
      herePlaceId?: string
      isValidated?: boolean
      validationScore?: number
      formattedAddress?: string
      isPrimary?: boolean
    }>
    contacts: Array<{
      firstName: string
      lastName: string
      salutation?: string
      title?: string
      position?: string
      department?: string
      role?: string
      communications: Array<{
        mediumName: string
        mediumId?: string
        channelId?: string
        value: string
        label?: string
        isPrimary?: boolean
      }>
      addresses: Array<{
        street?: string
        houseNumber?: string
        additionalInfo?: string
        postalCode?: string
        city?: string
        district?: string
        county?: string
        state?: string
        stateCode?: string
        country?: string
        countryCode?: string
        countryCodeAlpha3?: string
        addressTypeId?: string
        latitude?: number
        longitude?: number
        herePlaceId?: string
        isValidated?: boolean
        validationScore?: number
        formattedAddress?: string
        isPrimary?: boolean
      }>
    }>
    autoMerge?: boolean
    // EasyBill Integration
    easybillCustomerId?: number
    syncToEasybill?: boolean
  }): Promise<BulkImportResult> {
    return this.request({
      method: "POST",
      url: "/organizations/import",
      data,
    })
  }

  async importContact(data: {
    firstName: string
    lastName: string
    salutation?: string
    title?: string
    position?: string
    department?: string
    role?: string
    organizationName?: string
    organizationId?: string
    communications: Array<{
      mediumName: string
      mediumId?: string
      channelId?: string
      value: string
      label?: string
      isPrimary?: boolean
    }>
    addresses: Array<{
      street?: string
      houseNumber?: string
      additionalInfo?: string
      postalCode?: string
      city?: string
      district?: string
      county?: string
      state?: string
      stateCode?: string
      country?: string
      countryCode?: string
      countryCodeAlpha3?: string
      addressTypeId?: string
      latitude?: number
      longitude?: number
      herePlaceId?: string
      isValidated?: boolean
      validationScore?: number
      formattedAddress?: string
      isPrimary?: boolean
    }>
    autoMerge?: boolean
  }): Promise<BulkImportResult> {
    return this.request({
      method: "POST",
      url: "/contacts/import",
      data,
    })
  }

  async addressAutosuggest(params: {
    query: string
    latitude?: number
    longitude?: number
    country?: string
    limit?: number
  }): Promise<AddressSuggestion[]> {
    return this.request({
      method: "POST",
      url: "/scrape/address/autosuggest",
      data: params,
    })
  }

  async addressLookup(placeId: string): Promise<AddressLookupResult> {
    return this.request({
      method: "GET",
      url: `/scrape/address/lookup/${encodeURIComponent(placeId)}`,
    })
  }

  async addressValidate(params: {
    street?: string
    houseNumber?: string
    postalCode?: string
    city?: string
    country?: string
  }): Promise<AddressValidationResult> {
    return this.request({
      method: "POST",
      url: "/addresses/validate",
      data: params,
    })
  }

  async getAIProviders(): Promise<AIProvidersInfo> {
    return this.request({
      method: "GET",
      url: "/scrape/providers",
    })
  }

  // Health Check
  async healthCheck(): Promise<{
    status: "healthy" | "degraded" | "unhealthy"
    timestamp: string
    version: string
    services: Record<string, {
      name: string
      status: "up" | "down" | "unknown"
      latencyMs?: number | null
      message?: string | null
    }>
  }> {
    return this.request({
      method: "GET",
      url: "/health",
    })
  }

  async getServerLoad(): Promise<{
    status: "low" | "medium" | "high" | "critical"
    publicIp: string | null
    load1M: number
    load5M: number
    load15M: number
    cpuCount: number
    loadPercent: number
    memoryPercent: number | null
    diskPercent: number | null
    timestamp: string
    history: Array<{
      timestamp: string
      loadPercent: number
      memoryPercent: number | null
    }>
  }> {
    return this.request({
      method: "GET",
      url: "/health/load",
    })
  }

  async ensureTvKioskAccess(resetPin = false): Promise<{
    code: string
    pin: string | null
    shortUrl: string
    targetUrl: string
    pinWasReset: boolean
  }> {
    return this.request({
      method: "POST",
      url: "/health/tv-kiosk/access",
      params: { resetPin },
    })
  }

  async getTvKioskStatus(code: string, pin: string): Promise<{
    health: Awaited<ReturnType<ApiService["healthCheck"]>>
    load: Awaited<ReturnType<ApiService["getServerLoad"]>>
  }> {
    return this.request({
      method: "POST",
      url: `/health/tv-kiosk/${code}`,
      data: { pin },
    })
  }

  // Dashboard
  async getDashboard(): Promise<{
    stats: {
      organizationsTotal: number
      organizationsActive: number
      contactsTotal: number
      contactsActive: number
      addressesTotal: number
      addressesValidated: number
      activitiesLast7Days: number
      activitiesLast30Days: number
    }
    recentActivities: Array<{
      id: string
      entityType: string
      entityId: string
      action: string
      userName: string | null
      createdAt: string
      description: string
    }>
  }> {
    return this.request({
      method: "GET",
      url: "/dashboard",
    })
  }

  // =========================================================================
  // USER MANAGEMENT (Admin only)
  // =========================================================================

  users = {
    list: async (params?: {
      skip?: number
      limit?: number
      search?: string
      role?: string
      isActive?: boolean
    }): Promise<PaginatedResponse<UserListItem>> => {
      return this.request({
        method: "GET",
        url: "/users",
        params,
      })
    },

    get: async (id: string): Promise<UserDetail> => {
      return this.request({
        method: "GET",
        url: `/users/${id}`,
      })
    },

    create: async (data: UserCreate): Promise<UserDetail> => {
      return this.request({
        method: "POST",
        url: "/users",
        data,
      })
    },

    update: async (id: string, data: UserUpdate): Promise<UserDetail> => {
      return this.request({
        method: "PATCH",
        url: `/users/${id}`,
        data,
      })
    },

    delete: async (id: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/users/${id}`,
      })
    },

    hardDelete: async (id: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/users/${id}/permanent`,
      })
    },

    updateRoles: async (id: string, roleIds: string[]): Promise<UserDetail> => {
      return this.request({
        method: "PUT",
        url: `/users/${id}/roles`,
        data: { roleIds },
      })
    },

    resendWelcome: async (id: string): Promise<{ message: string }> => {
      return this.request({
        method: "POST",
        url: `/users/${id}/resend-welcome`,
      })
    },

    getRoles: async (): Promise<Role[]> => {
      const response = await this.request<{ items: Role[] }>({
        method: "GET",
        url: "/auth/roles",
      })
      return response.items
    },
  }

  // =========================================================================
  // SYSTEM SETTINGS (Admin only)
  // =========================================================================

  tester = {
    // Registry-Verwaltung (Admin)
    listInstanzen: async (): Promise<TesterInstanz[]> => {
      return this.request({ method: "GET", url: "/admin/tester/instanzen" })
    },
    createInstanz: async (data: TesterInstanzCreate): Promise<TesterInstanz> => {
      return this.request({ method: "POST", url: "/admin/tester/instanzen", data })
    },
    updateInstanz: async (slug: string, data: TesterInstanzUpdate): Promise<TesterInstanz> => {
      return this.request({ method: "PUT", url: `/admin/tester/instanzen/${slug}`, data })
    },
    deleteInstanz: async (slug: string): Promise<{ ok: boolean }> => {
      return this.request({ method: "DELETE", url: `/admin/tester/instanzen/${slug}` })
    },
    testInstanz: async (slug: string): Promise<TesterTestResult> => {
      return this.request({ method: "POST", url: `/admin/tester/instanzen/${slug}/test` })
    },
    // Aggregierte Sicht (ERP-UI)
    dashboard: async (instanz?: string): Promise<TesterDashboard> => {
      return this.request({
        method: "GET",
        url: "/tester/dashboard",
        params: instanz ? { instanz } : undefined,
      })
    },
    meldungen: async (params?: {
      instanz?: string
      status?: string
      size?: number
      /** Tester-Slug der Organisation (zentrale Instanz, testerwerk#61) */
      organisation?: string
    }): Promise<TesterMeldungenPage> => {
      return this.request({ method: "GET", url: "/tester/meldungen", params })
    },
    suche: async (q: string, instanz?: string): Promise<TesterMeldung[]> => {
      return this.request({
        method: "GET",
        url: "/tester/suche",
        params: { q, ...(instanz ? { instanz } : {}) },
      })
    },
  }

  settings = {
    list: async (category?: string): Promise<ProviderSettings[]> => {
      return this.request({
        method: "GET",
        url: "/admin/settings",
        params: category ? { category } : undefined,
      })
    },

    get: async (category: string, provider: string): Promise<ProviderSettings> => {
      return this.request({
        method: "GET",
        url: `/admin/settings/${category}/${provider}`,
      })
    },

    update: async (category: string, provider: string, data: ProviderSettingsUpdate): Promise<ProviderSettings> => {
      return this.request({
        method: "PUT",
        url: `/admin/settings/${category}/${provider}`,
        data,
      })
    },

    test: async (category: string, provider: string): Promise<ProviderTestResult> => {
      return this.request({
        method: "POST",
        url: `/admin/settings/${category}/${provider}/test`,
      })
    },

    getTicketEmail: async (): Promise<{ autoCreateTickets: boolean }> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/tickets/email-intake",
      })
    },

    updateTicketEmail: async (autoCreateTickets: boolean): Promise<{ autoCreateTickets: boolean }> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/tickets/email-intake",
        data: { autoCreateTickets },
      })
    },

    getAIProviders: async (): Promise<AIProviderStatus[]> => {
      return this.request({
        method: "GET",
        url: "/admin/ai-providers",
      })
    },

    // KI-Service-Zuweisungen
    getAIServiceAssignments: async (): Promise<AIServiceAssignment[]> => {
      return this.request({
        method: "GET",
        url: "/admin/ai-service-assignments",
      })
    },

    updateAIServiceAssignment: async (
      serviceId: string,
      provider: string | null
    ): Promise<AIServiceAssignment> => {
      return this.request({
        method: "PUT",
        url: `/admin/ai-service-assignments/${serviceId}`,
        data: { provider },
      })
    },

    // Live-Modelle vom Provider abrufen
    getProviderModels: async (
      provider: string
    ): Promise<{ id: string; name: string; created?: string }[]> => {
      return this.request({
        method: "GET",
        url: `/admin/ai-providers/${provider}/models`,
      })
    },

    testAIProviderPrompt: async (
      provider: string,
      data: { question: string; model?: string | null }
    ): Promise<AIProviderPromptTestResult> => {
      return this.request({
        method: "POST",
        url: `/admin/ai-providers/${provider}/prompt-test`,
        data,
      })
    },

    // SMTP spezifisch
    getSmtp: async (): Promise<ProviderSettings> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/email/smtp",
      })
    },

    updateSmtp: async (settings: Record<string, string>): Promise<ProviderSettings> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/email/smtp",
        data: settings,
      })
    },

    testSmtpConnection: async (): Promise<ProviderTestResult> => {
      return this.request({
        method: "POST",
        url: "/admin/settings/email/smtp/test",
      })
    },

    testEmail: async (toEmail: string): Promise<{ success: boolean; message: string }> => {
      return this.request({
        method: "POST",
        url: "/admin/settings/email/smtp/test-send",
        data: { to_email: toEmail },
      })
    },

    // System-Einstellungen (Timezone, Locale, etc.)
    getSystem: async (): Promise<{ timezone: string; dateFormat: string; timeFormat: string }> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/system/general",
      })
    },

    updateSystem: async (data: { timezone?: string; dateFormat?: string; timeFormat?: string }): Promise<void> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/system/general",
        data: {
          settings: {
            timezone: data.timezone,
            date_format: data.dateFormat,
            time_format: data.timeFormat,
          },
        },
      })
    },

    // HERE.com spezifisch
    getHere: async (): Promise<ProviderSettings> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/geodata/here",
      })
    },

    updateHere: async (data: ProviderSettingsUpdate): Promise<ProviderSettings> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/geodata/here",
        data,
      })
    },

    testHere: async (): Promise<ProviderTestResult> => {
      return this.request({
        method: "POST",
        url: "/admin/settings/geodata/here/test",
      })
    },

    // Easybill spezifisch
    getEasybill: async (): Promise<ProviderSettings> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/billing/easybill",
      })
    },

    updateEasybill: async (data: ProviderSettingsUpdate): Promise<ProviderSettings> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/billing/easybill",
        data,
      })
    },

    testEasybill: async (): Promise<ProviderTestResult> => {
      return this.request({
        method: "POST",
        url: "/admin/settings/billing/easybill/test",
      })
    },

    getForgejo: async (): Promise<ProviderSettings> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/integrations/forgejo",
      })
    },

    updateForgejo: async (data: ProviderSettingsUpdate): Promise<ProviderSettings> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/integrations/forgejo",
        data,
      })
    },

    getInvoiceGmailPolling: async (): Promise<InvoiceGmailPollingSettings> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/billing/invoice-gmail/polling",
      })
    },

    updateInvoiceGmailPolling: async (
      data: InvoiceGmailPollingSettingsUpdate
    ): Promise<InvoiceGmailPollingSettings> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/billing/invoice-gmail/polling",
        data,
      })
    },

    getInvoiceGmailPollStatus: async (): Promise<InvoiceGmailPollStatus> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/billing/invoice-gmail/polling/status",
      })
    },

    getInvoiceGmailLabels: async (mailbox?: string): Promise<InvoiceGmailLabelsResponse> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/billing/invoice-gmail/labels",
        params: mailbox ? { mailbox } : undefined,
      })
    },

    triggerInvoiceGmailPoll: async (): Promise<InvoiceGmailPollTriggerResponse> => {
      return this.request({
        method: "POST",
        url: "/admin/settings/billing/invoice-gmail/polling/trigger",
      })
    },

    getInvoiceOcr: async (): Promise<InvoiceOcrSettings> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/billing/invoice-ocr",
      })
    },

    updateInvoiceOcr: async (
      data: InvoiceOcrSettingsUpdate
    ): Promise<InvoiceOcrSettings> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/billing/invoice-ocr",
        data,
      })
    },

    // OCR-Prompt-Profile
    listOcrProfiles: async (): Promise<OcrPromptProfilesResponse> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/invoice-ocr/profiles",
      })
    },

    createOcrProfile: async (
      data: OcrPromptProfileCreate
    ): Promise<OcrPromptProfile> => {
      return this.request({
        method: "POST",
        url: "/admin/settings/invoice-ocr/profiles",
        data,
      })
    },

    updateOcrProfile: async (
      id: string,
      data: OcrPromptProfileUpdate
    ): Promise<OcrPromptProfile> => {
      return this.request({
        method: "PUT",
        url: `/admin/settings/invoice-ocr/profiles/${id}`,
        data,
      })
    },

    deleteOcrProfile: async (id: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/settings/invoice-ocr/profiles/${id}`,
      })
    },

    activateOcrProfile: async (id: string): Promise<OcrPromptProfile> => {
      return this.request({
        method: "POST",
        url: `/admin/settings/invoice-ocr/profiles/${id}/activate`,
      })
    },

    setActiveOcrEngine: async (
      engine: string
    ): Promise<OcrPromptProfilesResponse> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/invoice-ocr/active-engine",
        data: { engine },
      })
    },

    testOcr: async (
      file: File,
      engine: string,
      model: string,
      prompt: string
    ): Promise<OcrTestResult> => {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("engine", engine)
      formData.append("model", model)
      formData.append("prompt", prompt)
      return this.request({
        method: "POST",
        url: "/admin/settings/invoice-ocr/test",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      })
    },

    // OCR-Engine/Modell-Katalog (KI-Dienste)
    getOcrEngineCatalog: async (): Promise<OcrEngineCatalog> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/ai-services/ocr-engine-catalog",
      })
    },

    saveOcrEngineCatalog: async (data: {
      defaultEngine: string
      engines: OcrCatalogEngine[]
    }): Promise<OcrEngineCatalog> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/ai-services/ocr-engine-catalog",
        data,
      })
    },

    resetOcrEngineCatalog: async (): Promise<OcrEngineCatalog> => {
      return this.request({
        method: "POST",
        url: "/admin/settings/ai-services/ocr-engine-catalog/reset",
      })
    },

    // Rechnungs-Absender-Sperrliste
    getInvoiceSenderBlocklist: async (): Promise<{ patterns: string[] }> => {
      return this.request({
        method: "GET",
        url: "/admin/settings/ai-services/invoice-sender-blocklist",
      })
    },
    saveInvoiceSenderBlocklist: async (
      patterns: string[]
    ): Promise<{ patterns: string[] }> => {
      return this.request({
        method: "PUT",
        url: "/admin/settings/ai-services/invoice-sender-blocklist",
        data: { patterns },
      })
    },
    resetInvoiceSenderBlocklist: async (): Promise<{ patterns: string[] }> => {
      return this.request({
        method: "POST",
        url: "/admin/settings/ai-services/invoice-sender-blocklist/reset",
      })
    },

    // Branding / Logos
    getBranding: async (): Promise<BrandingConfig> => {
      return this.request({ method: "GET", url: "/admin/settings/branding" })
    },
    updateBranding: async (data: BrandingUpdate): Promise<BrandingConfig> => {
      return this.request({ method: "PUT", url: "/admin/settings/branding", data })
    },
    syncBranding: async (targets?: string[]): Promise<BrandingSyncResponse> => {
      return this.request({
        method: "POST",
        url: "/admin/settings/branding/sync",
        data: targets ? { targets } : {},
      })
    },
    getLogos: async (): Promise<Record<string, BrandingLogo>> => {
      return this.request({ method: "GET", url: "/admin/settings/branding/logos" })
    },
    uploadLogo: async (slot: string, file: File): Promise<{ slot: string; key: string; url: string; label: string }> => {
      const formData = new FormData()
      formData.append("file", file)
      const response = await this.client.post(`/admin/settings/branding/logos/${slot}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      return response.data
    },
    deleteLogo: async (slot: string): Promise<{ slot: string; deleted: boolean }> => {
      return this.request({ method: "DELETE", url: `/admin/settings/branding/logos/${slot}` })
    },
    disableLogo: async (slot: string): Promise<{ slot: string; disabled: boolean }> => {
      return this.request({ method: "POST", url: `/admin/settings/branding/logos/${slot}/disable` })
    },
    resetLogo: async (slot: string): Promise<{ slot: string; reset: boolean }> => {
      return this.request({ method: "POST", url: `/admin/settings/branding/logos/${slot}/reset` })
    },

    // Mandant (Tenant) Info
    getTenant: async (): Promise<any> => {
      return this.request({ method: "GET", url: "/admin/tenant" })
    },
    updateTenant: async (data: any): Promise<any> => {
      return this.request({ method: "PUT", url: "/admin/tenant", data })
    },

    // Mandant-Organisation (Tenant's own Organization)
    getTenantOrganization: async (): Promise<{ organizationId: string | null; organizationName: string | null }> => {
      return this.request({ method: "GET", url: "/admin/tenant/organization" })
    },
    setTenantOrganization: async (organizationId: string): Promise<{ organizationId: string | null; organizationName: string | null }> => {
      return this.request({ method: "PUT", url: "/admin/tenant/organization", data: { organizationId } })
    },

    syncContactsToUsers: async (): Promise<{
      created: number
      linked: number
      skipped: number
      errors: number
      details: Array<{
        contact: string
        email?: string
        status: string
        reason?: string
        temporaryPassword?: string
      }>
    }> => {
      return this.request({ method: "POST", url: "/admin/tenant/sync-contacts-to-users" })
    },
  }

  // =========================================================================
  // MODULE MANAGEMENT (Enable/Disable)
  // =========================================================================

  modules = {
    getEnabled: async (): Promise<string[]> => {
      return this.request({ method: "GET", url: "/admin/modules/enabled" })
    },

    getAll: async (): Promise<ModuleStatus[]> => {
      return this.request({ method: "GET", url: "/admin/modules" })
    },

    toggle: async (slug: string, isEnabled: boolean): Promise<ModuleStatus> => {
      return this.request({
        method: "PUT",
        url: `/admin/modules/${slug}`,
        data: { isEnabled },
      })
    },
  }

  // =========================================================================
  // AI SERVICE CONFIGS (Per-Service KI-Konfiguration)
  // =========================================================================

  aiServices = {
    list: async (): Promise<any[]> => {
      return this.request({ method: "GET", url: "/admin/ai-service-configs" })
    },
    get: async (serviceId: string): Promise<any> => {
      return this.request({ method: "GET", url: `/admin/ai-service-configs/${serviceId}` })
    },
    update: async (serviceId: string, data: any): Promise<any> => {
      return this.request({ method: "PUT", url: `/admin/ai-service-configs/${serviceId}`, data })
    },
    resetPrompt: async (serviceId: string): Promise<any> => {
      return this.request({ method: "POST", url: `/admin/ai-service-configs/${serviceId}/reset-prompt` })
    },
    test: async (serviceId: string): Promise<any> => {
      return this.request({ method: "POST", url: `/admin/ai-service-configs/${serviceId}/test` })
    },
  }

  // =========================================================================
  // MEET RING (Klingel-Links pro Organisation)
  // =========================================================================

  meetRing = {
    getToken: async (orgId: string): Promise<MeetRingToken | null> => {
      return this.request({
        method: "GET",
        url: `/organizations/${orgId}/ring-token`,
      })
    },
    createOrRotateToken: async (orgId: string): Promise<MeetRingToken> => {
      return this.request({
        method: "POST",
        url: `/organizations/${orgId}/ring-token`,
      })
    },
    deleteToken: async (orgId: string): Promise<{ success: boolean }> => {
      return this.request({
        method: "DELETE",
        url: `/organizations/${orgId}/ring-token`,
      })
    },
    publicInfo: async (token: string): Promise<MeetRingInfo> => {
      return this.request({
        method: "GET",
        url: `/public/ring/${token}`,
      })
    },
    publicRing: async (token: string, name: string, contactId?: string): Promise<MeetRingCallResponse> => {
      return this.request({
        method: "POST",
        url: `/public/ring/${token}`,
        data: { name, contactId },
      })
    },
  }

  // =========================================================================
  // TEAM MEET CALLS (Kunden-Meet-Anfragen fuer die ERP-Kopfzeile)
  // =========================================================================

  meetCalls = {
    active: async (): Promise<MeetCall[]> => {
      return this.request({
        method: "GET",
        url: "/meet-calls/active",
      })
    },
    publicActive: async (): Promise<PublicMeetCall[]> => {
      return this.request({
        method: "GET",
        url: "/public/meet-calls/active",
      })
    },
    join: async (callId: string): Promise<MeetCall> => {
      return this.request({
        method: "POST",
        url: `/meet-calls/${callId}/join`,
      })
    },
    finish: async (callId: string): Promise<MeetCall> => {
      return this.request({
        method: "POST",
        url: `/meet-calls/${callId}/finish`,
      })
    },
  }

  // =========================================================================
  // EASYBILL SYNC & MATCHING
  // =========================================================================

  easybill = {
    // Customer Search
    searchCustomers: async (
      query?: string,
      emailDomain?: string,
      limit?: number
    ): Promise<EasybillCustomerSearchResult[]> => {
      const params: Record<string, string | number> = { limit: limit || 10 }
      if (query) params.query = query
      if (emailDomain) params.email_domain = emailDomain
      return this.request({
        method: "GET",
        url: "/easybill/customers/search",
        params,
      })
    },

    // Sync Operations
    pushToEasybill: async (
      organizationId: string,
      options?: {
        force?: boolean
        addressId?: string
        includeAddress?: boolean
        includeCommunications?: boolean
        includeTaxData?: boolean
        includeBankData?: boolean
      },
    ): Promise<EasybillSyncResponse> => {
      return this.request({
        method: "POST",
        url: `/easybill/sync/push/${organizationId}`,
        params: options?.force ? { force: true } : undefined,
        data: options ? {
          addressId: options.addressId,
          includeAddress: options.includeAddress,
          includeCommunications: options.includeCommunications,
          includeTaxData: options.includeTaxData,
          includeBankData: options.includeBankData,
        } : undefined,
      })
    },

    pullFromEasybill: async (
      easybillCustomerId: number,
      organizationId?: string,
      force?: boolean
    ): Promise<EasybillSyncResponse> => {
      return this.request({
        method: "POST",
        url: `/easybill/sync/pull/${easybillCustomerId}`,
        params: {
          ...(organizationId && { organization_id: organizationId }),
          ...(force && { force: true }),
        },
      })
    },

    syncOrganizationInvoices: async (
      organizationId: string,
      maxPages?: number,
    ): Promise<EasybillInvoiceSyncResponse> => {
      return this.request({
        method: "POST",
        url: `/easybill/organizations/${organizationId}/sync-invoices`,
        params: maxPages ? { max_pages: maxPages } : undefined,
      })
    },

    // Matching
    getMatchingData: async (): Promise<EasybillMatchingData> => {
      return this.request({
        method: "GET",
        url: "/easybill/matching",
      })
    },

    linkOrganization: async (
      organizationId: string,
      easybillCustomerId: number
    ): Promise<EasybillSyncResponse> => {
      return this.request({
        method: "POST",
        url: "/easybill/matching/link",
        data: {
          organization_id: organizationId,
          easybill_customer_id: easybillCustomerId,
        },
      })
    },

    unlinkOrganization: async (organizationId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/easybill/matching/unlink/${organizationId}`,
      })
    },

    // Conflicts
    getConflicts: async (): Promise<EasybillConflict[]> => {
      return this.request({
        method: "GET",
        url: "/easybill/conflicts",
      })
    },

    resolveConflict: async (
      organizationId: string,
      winner: "netzcrm" | "easybill"
    ): Promise<EasybillSyncResponse> => {
      return this.request({
        method: "POST",
        url: `/easybill/conflicts/${organizationId}/resolve`,
        data: { winner },
      })
    },

    // Logs
    getSyncLogs: async (params?: {
      organizationId?: string
      status?: string
      limit?: number
    }): Promise<EasybillSyncLog[]> => {
      return this.request({
        method: "GET",
        url: "/easybill/logs",
        params: {
          organization_id: params?.organizationId,
          status: params?.status,
          limit: params?.limit,
        },
      })
    },
  }

  // =========================================================================
  // MATRIX ADMIN
  // =========================================================================

  matrixAdmin = {
    // Server Info
    getServerInfo: async (): Promise<MatrixServerInfo> => {
      return this.request({
        method: "GET",
        url: "/admin/matrix-admin/server-info",
      })
    },

    // Benutzer
    listUsers: async (params?: {
      offset?: number
      limit?: number
      search?: string
      includeDeactivated?: boolean
    }): Promise<MatrixAdminUserList> => {
      return this.request({
        method: "GET",
        url: "/admin/matrix-admin/users",
        params: {
          offset: params?.offset,
          limit: params?.limit,
          search: params?.search,
          include_deactivated: params?.includeDeactivated,
        },
      })
    },

    getUser: async (userId: string): Promise<MatrixAdminUser> => {
      return this.request({
        method: "GET",
        url: `/admin/matrix-admin/users/${encodeURIComponent(userId)}`,
      })
    },

    createUser: async (data: MatrixAdminUserCreate): Promise<MatrixAdminUser> => {
      return this.request({
        method: "POST",
        url: "/admin/matrix-admin/users",
        data,
      })
    },

    updateUser: async (userId: string, data: MatrixAdminUserUpdate): Promise<MatrixAdminUser> => {
      return this.request({
        method: "PUT",
        url: `/admin/matrix-admin/users/${encodeURIComponent(userId)}`,
        data,
      })
    },

    deactivateUser: async (userId: string, erase?: boolean): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/matrix-admin/users/${encodeURIComponent(userId)}`,
        params: erase ? { erase: true } : undefined,
      })
    },

    resetPassword: async (userId: string, newPassword: string): Promise<void> => {
      return this.request({
        method: "POST",
        url: `/admin/matrix-admin/users/${encodeURIComponent(userId)}/reset-password`,
        data: { new_password: newPassword },
      })
    },

    // Räume
    listRooms: async (params?: {
      offset?: number
      limit?: number
      search?: string
    }): Promise<MatrixAdminRoomList> => {
      return this.request({
        method: "GET",
        url: "/admin/matrix-admin/rooms",
        params: {
          offset: params?.offset,
          limit: params?.limit,
          search: params?.search,
        },
      })
    },

    getRoom: async (roomId: string): Promise<MatrixRoomDetail> => {
      return this.request({
        method: "GET",
        url: `/admin/matrix-admin/rooms/${encodeURIComponent(roomId)}`,
      })
    },

    inviteToRoom: async (roomId: string, userId: string): Promise<void> => {
      return this.request({
        method: "POST",
        url: `/admin/matrix-admin/rooms/${encodeURIComponent(roomId)}/invite`,
        data: { user_id: userId },
      })
    },

    removeFromRoom: async (roomId: string, userId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/matrix-admin/rooms/${encodeURIComponent(roomId)}/members/${encodeURIComponent(userId)}`,
      })
    },

    // Bots
    createBot: async (data: {
      username: string
      displayname?: string
      roomIds?: string[]
    }): Promise<CreateMatrixBotResult> => {
      return this.request({
        method: "POST",
        url: "/admin/matrix-admin/bots",
        data,
      })
    },

    listBots: async (includeInactive?: boolean): Promise<MatrixBotListResponse> => {
      return this.request({
        method: "GET",
        url: "/admin/matrix-admin/bots",
        params: includeInactive ? { include_inactive: true } : undefined,
      })
    },

    getBot: async (botId: string): Promise<MatrixBotItem> => {
      return this.request({
        method: "GET",
        url: `/admin/matrix-admin/bots/${botId}`,
      })
    },

    updateBot: async (
      botId: string,
      data: { displayname?: string; description?: string; isActive?: boolean }
    ): Promise<MatrixBotItem> => {
      return this.request({
        method: "PUT",
        url: `/admin/matrix-admin/bots/${botId}`,
        data,
      })
    },

    deleteBot: async (botId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/matrix-admin/bots/${botId}`,
      })
    },
  }

  // =========================================================================
  // MATRIX INTEGRATION (Kontakte & Organisationen)
  // =========================================================================

  matrixIntegration = {
    // Kontakt -> Matrix-Benutzer
    getContactMatrixSuggestion: async (contactId: string): Promise<MatrixUserSuggestion> => {
      return this.request({
        method: "GET",
        url: `/contacts/${contactId}/matrix/suggest-username`,
      })
    },

    createContactMatrixUser: async (
      contactId: string,
      data: { username: string; sendPasswordReset?: boolean }
    ): Promise<CreateMatrixUserResult> => {
      return this.request({
        method: "POST",
        url: `/contacts/${contactId}/matrix/create-user`,
        data,
      })
    },

    // Organisation -> Matrix-Space
    getOrganizationMatrixStatus: async (orgId: string): Promise<OrganizationMatrixStatus> => {
      return this.request({
        method: "GET",
        url: `/organizations/${orgId}/matrix/status`,
      })
    },

    createOrganizationSpace: async (orgId: string): Promise<CreateMatrixSpaceResult> => {
      return this.request({
        method: "POST",
        url: `/organizations/${orgId}/matrix/bootstrap`,
      })
    },

    ensureOrganizationOnboardingBot: async (orgId: string): Promise<OrganizationMatrixBotProvisionResult> => {
      return this.request({
        method: "POST",
        url: `/organizations/${orgId}/matrix/bots/onboarding`,
      })
    },

    createOrganizationSubspace: async (
      orgId: string,
      data: { name: string; topic?: string }
    ): Promise<OrganizationMatrixSpace> => {
      return this.request({
        method: "POST",
        url: `/organizations/${orgId}/matrix/subspaces`,
        data,
      })
    },

    // Neuen Raum erstellen
    createOrganizationRoom: async (
      orgId: string,
      data: { name: string; topic?: string; spaceId?: string }
    ): Promise<CreateMatrixRoomResult> => {
      return this.request({
        method: "POST",
        url: `/organizations/${orgId}/matrix/rooms`,
        data,
      })
    },

    getOrganizationMatrixMemberCandidates: async (orgId: string): Promise<OrganizationMatrixMemberCandidate[]> => {
      return this.request({
        method: "GET",
        url: `/organizations/${orgId}/matrix/member-candidates`,
      })
    },

    getOrganizationMatrixSpaceMembers: async (
      orgId: string,
      spaceId: string
    ): Promise<OrganizationMatrixMemberItem[]> => {
      return this.request({
        method: "GET",
        url: `/organizations/${orgId}/matrix/spaces/${spaceId}/members`,
      })
    },

    getOrganizationMatrixRoomMembers: async (
      orgId: string,
      roomId: string
    ): Promise<OrganizationMatrixMemberItem[]> => {
      return this.request({
        method: "GET",
        url: `/organizations/${orgId}/matrix/rooms/${roomId}/members`,
      })
    },

    assignOrganizationMatrixSpaceMember: async (
      orgId: string,
      spaceId: string,
      data: MatrixMemberAssignment
    ): Promise<void> => {
      return this.request({
        method: "POST",
        url: `/organizations/${orgId}/matrix/spaces/${spaceId}/members`,
        data,
      })
    },

    assignOrganizationMatrixRoomMember: async (
      orgId: string,
      roomId: string,
      data: MatrixMemberAssignment
    ): Promise<void> => {
      return this.request({
        method: "POST",
        url: `/organizations/${orgId}/matrix/rooms/${roomId}/members`,
        data,
      })
    },

    removeOrganizationMatrixSpaceMember: async (
      orgId: string,
      spaceId: string,
      data: MatrixMemberAssignment & { includeChildren?: boolean }
    ): Promise<void> => {
      return this.request({
        method: "POST",
        url: `/organizations/${orgId}/matrix/spaces/${spaceId}/members/remove`,
        data,
      })
    },

    removeOrganizationMatrixRoomMember: async (
      orgId: string,
      roomId: string,
      data: MatrixMemberAssignment
    ): Promise<void> => {
      return this.request({
        method: "POST",
        url: `/organizations/${orgId}/matrix/rooms/${roomId}/members/remove`,
        data,
      })
    },

    // Manager-Status
    updateMatrixManagerStatus: async (
      orgId: string,
      contactId: string,
      isMatrixManager: boolean
    ): Promise<void> => {
      return this.request({
        method: "PUT",
        url: `/organizations/${orgId}/contacts/${contactId}/matrix-manager`,
        data: { isMatrixManager },
      })
    },
  }

  opencloudOrganizations = {
    getStatus: async (organizationId: string): Promise<OpenCloudStatusResponse> => {
      return this.request({
        method: "GET",
        url: `/organizations/${organizationId}/opencloud/status`,
      })
    },

    ensureArea: async (organizationId: string): Promise<OpenCloudEnsureResponse> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/opencloud/area`,
      })
    },

    getFiles: async (
      organizationId: string,
      path: string = "",
      area: OpenCloudArea = "portal"
    ): Promise<OpenCloudFileListResponse> => {
      return this.request({
        method: "GET",
        url: `/organizations/${organizationId}/opencloud/files`,
        params: { path, area },
      })
    },

    getFileBlob: async (
      organizationId: string,
      path: string,
      area: OpenCloudArea = "portal"
    ): Promise<Blob> => {
      return this.request({
        method: "GET",
        url: `/organizations/${organizationId}/opencloud/files/download`,
        params: { path, area },
        responseType: "blob",
      })
    },

    openFile: async (
      organizationId: string,
      area: OpenCloudArea,
      path: string
    ): Promise<OpenCloudMutationResponse> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/opencloud/files/open`,
        params: { area },
        data: { path },
      })
    },

    uploadFile: async (
      organizationId: string,
      area: OpenCloudArea,
      path: string,
      file: File
    ): Promise<OpenCloudMutationResponse> => {
      const data = new FormData()
      data.append("file", file)
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/opencloud/files/upload`,
        params: { path, area },
        data,
        headers: { "Content-Type": "multipart/form-data" },
      })
    },

    createFolder: async (
      organizationId: string,
      area: OpenCloudArea,
      path: string,
      name: string
    ): Promise<OpenCloudMutationResponse> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/opencloud/folders`,
        params: { area },
        data: { path, name },
      })
    },

    createDocument: async (
      organizationId: string,
      area: OpenCloudArea,
      path: string,
      name: string,
      documentType: OpenCloudDocumentType = "document"
    ): Promise<OpenCloudMutationResponse> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/opencloud/documents`,
        params: { area },
        data: { path, name, documentType },
      })
    },

    share: async (
      organizationId: string,
      data: { path: string; area?: OpenCloudArea; canEdit?: boolean; password?: string; expiresAt?: string }
    ): Promise<OpenCloudShareResponse> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/opencloud/share`,
        data,
      })
    },
  }

  shortLinks = {
    ensurePublishedPage: async (pageId: string): Promise<ShortLink> => {
      return this.request({
        method: "POST",
        url: `/short-links/pages/${pageId}`,
      })
    },
  }

  // =========================================================================
  // NINOX INTEGRATION
  // =========================================================================

  ninox = {
    // Server CRUD
    listServers: async (organizationId?: string): Promise<NinoxServerListItem[]> => {
      return this.request({
        method: "GET",
        url: "/ninox/servers",
        params: organizationId ? { organization_id: organizationId } : undefined,
      })
    },

    // Teams live vom Ninox-Server (Registry-Zugangsdaten), ohne Extract/Snapshot
    listServerTeams: async (serverId: string): Promise<{ id: string; name: string }[]> => {
      return this.request({
        method: "GET",
        url: `/ninox/servers/${serverId}/teams`,
      })
    },

    getServer: async (serverId: string): Promise<NinoxServer> => {
      return this.request({
        method: "GET",
        url: `/ninox/servers/${serverId}`,
      })
    },

    createServer: async (data: NinoxServerCreate): Promise<NinoxServer> => {
      return this.request({
        method: "POST",
        url: "/ninox/servers",
        data,
      })
    },

    updateServer: async (serverId: string, data: NinoxServerUpdate): Promise<NinoxServer> => {
      return this.request({
        method: "PUT",
        url: `/ninox/servers/${serverId}`,
        data,
      })
    },

    deleteServer: async (serverId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/ninox/servers/${serverId}`,
      })
    },

    // Connection Test
    testConnection: async (serverId: string): Promise<NinoxConnectionTestResult> => {
      return this.request({
        method: "POST",
        url: `/ninox/servers/${serverId}/test`,
      })
    },
  }

  // =========================================================================
  // PROJEKTBASE
  // =========================================================================

  projektbase = {
    // Folders
    getFolderTree: async (organizationId: string, projectId?: string) => {
      const params: any = { organization_id: organizationId }
      if (projectId) params.project_id = projectId
      return this.request({ method: "GET", url: "/projektbase/folders/tree", params })
    },
    createFolder: async (data: any) => {
      return this.request({ method: "POST", url: "/projektbase/folders", data })
    },
    updateFolder: async (folderId: string, data: any) => {
      return this.request({ method: "PUT", url: `/projektbase/folders/${folderId}`, data })
    },
    deleteFolder: async (folderId: string) => {
      return this.request({ method: "DELETE", url: `/projektbase/folders/${folderId}` })
    },
    // Documents
    listDocuments: async (organizationId: string, folderId?: string, projectId?: string) => {
      const params: any = { organization_id: organizationId }
      if (folderId) params.folder_id = folderId
      if (projectId) params.project_id = projectId
      return this.request({ method: "GET", url: "/projektbase/documents", params })
    },
    getDocument: async (docId: string) => {
      return this.request({ method: "GET", url: `/projektbase/documents/${docId}` })
    },
    createDocument: async (data: any) => {
      return this.request({ method: "POST", url: "/projektbase/documents", data })
    },
    updateDocument: async (docId: string, data: any) => {
      return this.request({ method: "PUT", url: `/projektbase/documents/${docId}`, data })
    },
    deleteDocument: async (docId: string) => {
      return this.request({ method: "DELETE", url: `/projektbase/documents/${docId}` })
    },
    // Document Locking
    lockDocument: async (docId: string) => {
      return this.request({ method: "POST", url: `/projektbase/documents/${docId}/lock` })
    },
    unlockDocument: async (docId: string) => {
      return this.request({ method: "DELETE", url: `/projektbase/documents/${docId}/lock` })
    },
    heartbeatDocument: async (docId: string) => {
      return this.request({ method: "POST", url: `/projektbase/documents/${docId}/heartbeat` })
    },
    // Titles (für Autocomplete)
    getDocumentTitles: async (organizationId: string, projectId?: string) => {
      const params: any = { organization_id: organizationId }
      if (projectId) params.project_id = projectId
      return this.request({ method: "GET", url: "/projektbase/documents/titles", params })
    },
    // Backlinks + Graph
    getBacklinks: async (docId: string) => {
      return this.request({ method: "GET", url: `/projektbase/documents/${docId}/backlinks` })
    },
    getGraph: async (organizationId: string, projectId?: string) => {
      const params: any = { organization_id: organizationId }
      if (projectId) params.project_id = projectId
      return this.request({ method: "GET", url: "/projektbase/graph", params })
    },
    ensureProjectBrain: async (projectId: string) => {
      return this.request({ method: "POST", url: `/projektbase/projects/${projectId}/brain/ensure` })
    },
    getProjectBrainManifest: async (projectId: string) => {
      return this.request({ method: "GET", url: `/projektbase/projects/${projectId}/brain/manifest` })
    },
    importProjectBrain: async (projectId: string, data: any) => {
      return this.request({ method: "POST", url: `/projektbase/projects/${projectId}/brain/import`, data })
    },
    uploadProjectSource: async (
      projectId: string,
      file: File,
      area: OpenCloudArea = "internal"
    ): Promise<ProjectBrainSourceUploadResponse> => {
      const formData = new FormData()
      formData.append("file", file)
      return this.request({
        method: "POST",
        url: `/projektbase/projects/${projectId}/sources/upload`,
        params: { area },
        data: formData,
      })
    },
    prepareProjectVault: async (projectId: string): Promise<ProjectBrainVaultPrepareResponse> => {
      return this.request({ method: "POST", url: `/projektbase/projects/${projectId}/vault/prepare` })
    },
    syncProjectVault: async (projectId: string): Promise<ProjectBrainVaultSyncResponse> => {
      return this.request({ method: "POST", url: `/projektbase/projects/${projectId}/vault/sync` })
    },
    // Journal
    getJournalToday: async () => {
      return this.request({ method: "GET", url: "/projektbase/journal/today" })
    },
    getJournalByDate: async (date: string, userId?: string) => {
      const params: any = {}
      if (userId) params.user_id = userId
      return this.request({ method: "GET", url: `/projektbase/journal/date/${date}`, params })
    },
    updateJournal: async (entryId: string, data: any) => {
      return this.request({ method: "PUT", url: `/projektbase/journal/${entryId}`, data })
    },
    listJournalEntries: async (fromDate?: string, toDate?: string, userId?: string) => {
      const params: any = {}
      if (fromDate) params.from_date = fromDate
      if (toDate) params.to_date = toDate
      if (userId) params.user_id = userId
      return this.request({ method: "GET", url: "/projektbase/journal/entries", params })
    },
    // Time Entries
    listTimeEntries: async (date: string, userId?: string) => {
      const params: any = { entry_date: date }
      if (userId) params.user_id = userId
      return this.request({ method: "GET", url: "/projektbase/journal/time-entries", params })
    },
    getTimeEntryBars: async (dateFrom: string, dateTo: string) => {
      return this.request({ method: "GET", url: "/projektbase/journal/time-entry-bars", params: { date_from: dateFrom, date_to: dateTo } })
    },
    createTimeEntry: async (data: any) => {
      return this.request({ method: "POST", url: "/projektbase/journal/time-entries", data })
    },
    updateTimeEntry: async (entryId: string, data: any) => {
      return this.request({ method: "PUT", url: `/projektbase/journal/time-entries/${entryId}`, data })
    },
    deleteTimeEntry: async (entryId: string) => {
      return this.request({ method: "DELETE", url: `/projektbase/journal/time-entries/${entryId}` })
    },
    getTimeSummary: async (fromDate: string, toDate: string, userId?: string) => {
      const params: any = { from_date: fromDate, to_date: toDate }
      if (userId) params.user_id = userId
      return this.request({ method: "GET", url: "/projektbase/journal/time-summary", params })
    },
    // Journal Subpages
    getJournalSubpages: async (entryId: string) => {
      return this.request({ method: "GET", url: `/projektbase/journal/${entryId}/subpages` })
    },
    createJournalSubpage: async (entryId: string, data: any) => {
      return this.request({ method: "POST", url: `/projektbase/journal/${entryId}/subpages`, data })
    },
    getJournalSubpage: async (subpageId: string) => {
      return this.request({ method: "GET", url: `/projektbase/journal/subpages/${subpageId}` })
    },
    updateJournalSubpage: async (subpageId: string, data: any) => {
      return this.request({ method: "PUT", url: `/projektbase/journal/subpages/${subpageId}`, data })
    },
    deleteJournalSubpage: async (subpageId: string) => {
      return this.request({ method: "DELETE", url: `/projektbase/journal/subpages/${subpageId}` })
    },
    getSubpageLinks: async (subpageId: string) => {
      return this.request({ method: "GET", url: `/projektbase/journal/subpages/${subpageId}/links` })
    },
    getSubpageBacklinks: async (subpageId: string) => {
      return this.request({ method: "GET", url: `/projektbase/journal/subpages/${subpageId}/backlinks` })
    },
    // Attachment Upload (Bilder fuer Editoren)
    uploadAttachment: async (formData: FormData, documentId?: string, journalEntryId?: string) => {
      const params: any = {}
      if (documentId) params.document_id = documentId
      if (journalEntryId) params.journal_entry_id = journalEntryId
      return this.request({
        method: "POST",
        url: "/projektbase/attachments/upload",
        data: formData,
        params,
        headers: { "Content-Type": "multipart/form-data" },
      })
    },
    // Templates
    listTemplates: async (organizationId: string) => {
      return this.request({ method: "GET", url: "/projektbase/templates", params: { organization_id: organizationId } })
    },
    createTemplate: async (data: any) => {
      return this.request({ method: "POST", url: "/projektbase/templates", data })
    },
  }

  // =========================================================================
  // LOOKUP TABLES (Admin only)
  // =========================================================================

  addressTypes = {
    list: async (includeInactive: boolean = false): Promise<AddressType[]> => {
      return this.request({
        method: "GET",
        url: "/admin/address-types",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<AddressType> => {
      return this.request({
        method: "GET",
        url: `/admin/address-types/${id}`,
      })
    },

    create: async (data: AddressTypeCreate): Promise<AddressType> => {
      return this.request({
        method: "POST",
        url: "/admin/address-types",
        data,
      })
    },

    update: async (id: string, data: AddressTypeUpdate): Promise<AddressType> => {
      return this.request({
        method: "PUT",
        url: `/admin/address-types/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/address-types/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<AddressType[]> => {
      return this.request({
        method: "POST",
        url: "/admin/address-types/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // communicationTypes wurde durch 4 separate Dimensionen ersetzt:
  // communicationMedia, communicationChannels, communicationContexts, communicationDevices
  // (siehe oben unter "Communication Dimensions (Admin)")

  organizationTypes = {
    list: async (includeInactive: boolean = false): Promise<OrganizationTypeItem[]> => {
      return this.request({
        method: "GET",
        url: "/admin/organization-types",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<OrganizationTypeItem> => {
      return this.request({
        method: "GET",
        url: `/admin/organization-types/${id}`,
      })
    },

    create: async (data: OrganizationTypeCreate): Promise<OrganizationTypeItem> => {
      return this.request({
        method: "POST",
        url: "/admin/organization-types",
        data,
      })
    },

    update: async (id: string, data: OrganizationTypeUpdate): Promise<OrganizationTypeItem> => {
      return this.request({
        method: "PUT",
        url: `/admin/organization-types/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/organization-types/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<OrganizationTypeItem[]> => {
      return this.request({
        method: "POST",
        url: "/admin/organization-types/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  organizationRelationshipTypes = {
    listActive: async (): Promise<OrganizationRelationshipTypeItem[]> => {
      return this.listActiveOrganizationRelationshipTypes()
    },

    list: async (includeInactive: boolean = false): Promise<OrganizationRelationshipTypeItem[]> => {
      return this.request({
        method: "GET",
        url: "/admin/organization-relationship-types",
        params: { include_inactive: includeInactive },
      })
    },

    create: async (data: OrganizationRelationshipTypeCreate): Promise<OrganizationRelationshipTypeItem> => {
      return this.request({
        method: "POST",
        url: "/admin/organization-relationship-types",
        data,
      })
    },

    update: async (
      id: string,
      data: OrganizationRelationshipTypeUpdate
    ): Promise<OrganizationRelationshipTypeItem> => {
      return this.request({
        method: "PUT",
        url: `/admin/organization-relationship-types/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/organization-relationship-types/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<OrganizationRelationshipTypeItem[]> => {
      return this.request({
        method: "POST",
        url: "/admin/organization-relationship-types/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // TAGS (Admin)
  // =========================================================================

  tags = {
    list: async (includeInactive: boolean = false): Promise<Tag[]> => {
      return this.request({
        method: "GET",
        url: "/admin/tags",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<Tag> => {
      return this.request({
        method: "GET",
        url: `/admin/tags/${id}`,
      })
    },

    create: async (data: TagCreate): Promise<Tag> => {
      return this.request({
        method: "POST",
        url: "/admin/tags",
        data,
      })
    },

    update: async (id: string, data: TagUpdate): Promise<Tag> => {
      return this.request({
        method: "PUT",
        url: `/admin/tags/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/tags/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<Tag[]> => {
      return this.request({
        method: "POST",
        url: "/admin/tags/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // COMPANY SIZES (Admin) - Unternehmensgrößen
  // =========================================================================

  companySizes = {
    list: async (includeInactive: boolean = false): Promise<CompanySize[]> => {
      return this.request({
        method: "GET",
        url: "/admin/company-sizes",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<CompanySize> => {
      return this.request({
        method: "GET",
        url: `/admin/company-sizes/${id}`,
      })
    },

    create: async (data: CompanySizeCreate): Promise<CompanySize> => {
      return this.request({
        method: "POST",
        url: "/admin/company-sizes",
        data,
      })
    },

    update: async (id: string, data: CompanySizeUpdate): Promise<CompanySize> => {
      return this.request({
        method: "PUT",
        url: `/admin/company-sizes/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/company-sizes/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<CompanySize[]> => {
      return this.request({
        method: "POST",
        url: "/admin/company-sizes/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // LEGAL FORMS (Admin) - Rechtsformen
  // =========================================================================

  legalForms = {
    list: async (includeInactive: boolean = false): Promise<LegalForm[]> => {
      return this.request({
        method: "GET",
        url: "/admin/legal-forms",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<LegalForm> => {
      return this.request({
        method: "GET",
        url: `/admin/legal-forms/${id}`,
      })
    },

    create: async (data: LegalFormCreate): Promise<LegalForm> => {
      return this.request({
        method: "POST",
        url: "/admin/legal-forms",
        data,
      })
    },

    update: async (id: string, data: LegalFormUpdate): Promise<LegalForm> => {
      return this.request({
        method: "PUT",
        url: `/admin/legal-forms/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/legal-forms/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<LegalForm[]> => {
      return this.request({
        method: "POST",
        url: "/admin/legal-forms/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // INDUSTRIES (Admin) - Branchen
  // =========================================================================

  industries = {
    list: async (includeInactive: boolean = false): Promise<Industry[]> => {
      return this.request({
        method: "GET",
        url: "/admin/industries",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<Industry> => {
      return this.request({
        method: "GET",
        url: `/admin/industries/${id}`,
      })
    },

    create: async (data: IndustryCreate): Promise<Industry> => {
      return this.request({
        method: "POST",
        url: "/admin/industries",
        data,
      })
    },

    update: async (id: string, data: IndustryUpdate): Promise<Industry> => {
      return this.request({
        method: "PUT",
        url: `/admin/industries/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/industries/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<Industry[]> => {
      return this.request({
        method: "POST",
        url: "/admin/industries/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // CONTACT TYPES (Admin) - Kontakttypen
  // =========================================================================

  contactTypes = {
    list: async (includeInactive: boolean = false): Promise<ContactType[]> => {
      return this.request({
        method: "GET",
        url: "/admin/contact-types",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<ContactType> => {
      return this.request({
        method: "GET",
        url: `/admin/contact-types/${id}`,
      })
    },

    create: async (data: ContactTypeCreate): Promise<ContactType> => {
      return this.request({
        method: "POST",
        url: "/admin/contact-types",
        data,
      })
    },

    update: async (id: string, data: ContactTypeUpdate): Promise<ContactType> => {
      return this.request({
        method: "PUT",
        url: `/admin/contact-types/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/contact-types/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<ContactType[]> => {
      return this.request({
        method: "POST",
        url: "/admin/contact-types/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // PROFESSION CATEGORIES (Admin) - Berufskategorien
  // =========================================================================

  professionCategories = {
    list: async (includeInactive: boolean = false): Promise<ProfessionCategory[]> => {
      return this.request({
        method: "GET",
        url: "/admin/profession-categories",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<ProfessionCategory> => {
      return this.request({
        method: "GET",
        url: `/admin/profession-categories/${id}`,
      })
    },

    create: async (data: ProfessionCategoryCreate): Promise<ProfessionCategory> => {
      return this.request({
        method: "POST",
        url: "/admin/profession-categories",
        data,
      })
    },

    update: async (id: string, data: ProfessionCategoryUpdate): Promise<ProfessionCategory> => {
      return this.request({
        method: "PUT",
        url: `/admin/profession-categories/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/profession-categories/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<ProfessionCategory[]> => {
      return this.request({
        method: "POST",
        url: "/admin/profession-categories/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // TITLES (Admin) - Akademische/Berufliche Titel
  // =========================================================================

  titles = {
    list: async (includeInactive: boolean = false): Promise<Title[]> => {
      return this.request({
        method: "GET",
        url: "/admin/titles",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<Title> => {
      return this.request({
        method: "GET",
        url: `/admin/titles/${id}`,
      })
    },

    create: async (data: TitleCreate): Promise<Title> => {
      return this.request({
        method: "POST",
        url: "/admin/titles",
        data,
      })
    },

    update: async (id: string, data: TitleUpdate): Promise<Title> => {
      return this.request({
        method: "PUT",
        url: `/admin/titles/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/titles/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<Title[]> => {
      return this.request({
        method: "POST",
        url: "/admin/titles/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // CONTACT ROLES (Admin) - Kontaktrollen
  // =========================================================================

  contactRoles = {
    list: async (includeInactive: boolean = false): Promise<ContactRole[]> => {
      return this.request({
        method: "GET",
        url: "/admin/contact-roles",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<ContactRole> => {
      return this.request({
        method: "GET",
        url: `/admin/contact-roles/${id}`,
      })
    },

    create: async (data: ContactRoleCreate): Promise<ContactRole> => {
      return this.request({
        method: "POST",
        url: "/admin/contact-roles",
        data,
      })
    },

    update: async (id: string, data: ContactRoleUpdate): Promise<ContactRole> => {
      return this.request({
        method: "PUT",
        url: `/admin/contact-roles/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/contact-roles/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<ContactRole[]> => {
      return this.request({
        method: "POST",
        url: "/admin/contact-roles/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // DEPARTMENT TEMPLATES (Admin) - Standardabteilungen
  // =========================================================================

  departmentTemplates = {
    list: async (includeInactive: boolean = false): Promise<DepartmentTemplate[]> => {
      return this.request({
        method: "GET",
        url: "/admin/department-templates",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<DepartmentTemplate> => {
      return this.request({
        method: "GET",
        url: `/admin/department-templates/${id}`,
      })
    },

    create: async (data: DepartmentTemplateCreate): Promise<DepartmentTemplate> => {
      return this.request({
        method: "POST",
        url: "/admin/department-templates",
        data,
      })
    },

    update: async (id: string, data: DepartmentTemplateUpdate): Promise<DepartmentTemplate> => {
      return this.request({
        method: "PUT",
        url: `/admin/department-templates/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/department-templates/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<DepartmentTemplate[]> => {
      return this.request({
        method: "POST",
        url: "/admin/department-templates/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // FUNCTION TEMPLATES (Admin) - Standardfunktionen
  // =========================================================================

  functionTemplates = {
    list: async (includeInactive: boolean = false): Promise<FunctionTemplate[]> => {
      return this.request({
        method: "GET",
        url: "/admin/function-templates",
        params: { include_inactive: includeInactive },
      })
    },

    get: async (id: string): Promise<FunctionTemplate> => {
      return this.request({
        method: "GET",
        url: `/admin/function-templates/${id}`,
      })
    },

    create: async (data: FunctionTemplateCreate): Promise<FunctionTemplate> => {
      return this.request({
        method: "POST",
        url: "/admin/function-templates",
        data,
      })
    },

    update: async (id: string, data: FunctionTemplateUpdate): Promise<FunctionTemplate> => {
      return this.request({
        method: "PUT",
        url: `/admin/function-templates/${id}`,
        data,
      })
    },

    delete: async (id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/admin/function-templates/${id}`,
        params: { permanent },
      })
    },

    reorder: async (itemIds: string[]): Promise<FunctionTemplate[]> => {
      return this.request({
        method: "POST",
        url: "/admin/function-templates/reorder",
        data: { item_ids: itemIds },
      })
    },
  }

  // =========================================================================
  // ORGANIZATION DEPARTMENTS - Abteilungen pro Organisation
  // =========================================================================

  organizationDepartments = {
    list: async (organizationId: string, includeInactive: boolean = false): Promise<OrganizationDepartment[]> => {
      return this.request({
        method: "GET",
        url: `/organizations/${organizationId}/departments`,
        params: { include_inactive: includeInactive },
      })
    },

    get: async (organizationId: string, id: string): Promise<OrganizationDepartment> => {
      return this.request({
        method: "GET",
        url: `/organizations/${organizationId}/departments/${id}`,
      })
    },

    create: async (organizationId: string, data: Omit<OrganizationDepartmentCreate, "organizationId">): Promise<OrganizationDepartment> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/departments`,
        data,
      })
    },

    update: async (organizationId: string, id: string, data: OrganizationDepartmentUpdate): Promise<OrganizationDepartment> => {
      return this.request({
        method: "PUT",
        url: `/organizations/${organizationId}/departments/${id}`,
        data,
      })
    },

    delete: async (organizationId: string, id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/organizations/${organizationId}/departments/${id}`,
        params: { permanent },
      })
    },

    reorder: async (organizationId: string, itemIds: string[]): Promise<OrganizationDepartment[]> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/departments/reorder`,
        data: { item_ids: itemIds },
      })
    },

    copyFromTemplates: async (organizationId: string): Promise<OrganizationDepartment[]> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/departments/copy-templates`,
      })
    },

  }

  // =========================================================================
  // ORGANIZATION FUNCTIONS - Funktionen pro Organisation
  // =========================================================================

  organizationFunctions = {
    list: async (organizationId: string, includeInactive: boolean = false): Promise<OrganizationFunction[]> => {
      return this.request({
        method: "GET",
        url: `/organizations/${organizationId}/functions`,
        params: { include_inactive: includeInactive },
      })
    },

    get: async (organizationId: string, id: string): Promise<OrganizationFunction> => {
      return this.request({
        method: "GET",
        url: `/organizations/${organizationId}/functions/${id}`,
      })
    },

    create: async (organizationId: string, data: Omit<OrganizationFunctionCreate, "organizationId">): Promise<OrganizationFunction> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/functions`,
        data,
      })
    },

    update: async (organizationId: string, id: string, data: OrganizationFunctionUpdate): Promise<OrganizationFunction> => {
      return this.request({
        method: "PUT",
        url: `/organizations/${organizationId}/functions/${id}`,
        data,
      })
    },

    delete: async (organizationId: string, id: string, permanent: boolean = false): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/organizations/${organizationId}/functions/${id}`,
        params: { permanent },
      })
    },

    reorder: async (organizationId: string, itemIds: string[]): Promise<OrganizationFunction[]> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/functions/reorder`,
        data: { item_ids: itemIds },
      })
    },

    copyFromTemplates: async (organizationId: string): Promise<OrganizationFunction[]> => {
      return this.request({
        method: "POST",
        url: `/organizations/${organizationId}/functions/copy-templates`,
      })
    },
  }

  /**
   * Stellt fehlende Standard-Abteilungen und -Funktionen für eine Organisation wieder her.
   * Bestehende werden nicht überschrieben.
   */
  resetOrganizationDefaults = async (organizationId: string): Promise<{
    departmentsAdded: number
    functionsAdded: number
    message: string
  }> => {
    return this.request({
      method: "POST",
      url: `/organizations/${organizationId}/reset-defaults`,
    })
  }

  // =========================================================================
  // OAUTH2 (Microsoft & kSuite)
  // =========================================================================

  oauth = {
    // Google / Gmail OAuth
    getGoogleStatus: async (): Promise<OAuthConnectionStatus> => {
      return this.request({
        method: "GET",
        url: "/oauth/google/status",
      })
    },

    authorizeGoogle: async (scopes: string[], redirectUri?: string): Promise<OAuthAuthorizationResponse> => {
      return this.request({
        method: "POST",
        url: "/oauth/google/authorize",
        data: { scopes, redirect_uri: redirectUri },
      })
    },

    revokeGoogle: async (): Promise<OAuthRevokeResponse> => {
      return this.request({
        method: "POST",
        url: "/oauth/google/revoke",
      })
    },

    revokeGoogleAccount: async (accountEmail: string): Promise<OAuthRevokeResponse> => {
      return this.request({
        method: "POST",
        url: "/oauth/google/revoke-account",
        data: { account_email: accountEmail },
      })
    },

    // Microsoft OAuth
    getMicrosoftStatus: async (): Promise<OAuthConnectionStatus> => {
      return this.request({
        method: "GET",
        url: "/oauth/microsoft/status",
      })
    },

    authorizeMicrosoft: async (scopes: string[], redirectUri?: string): Promise<OAuthAuthorizationResponse> => {
      return this.request({
        method: "POST",
        url: "/oauth/microsoft/authorize",
        data: { scopes, redirect_uri: redirectUri },
      })
    },

    revokeMicrosoft: async (): Promise<OAuthRevokeResponse> => {
      return this.request({
        method: "POST",
        url: "/oauth/microsoft/revoke",
      })
    },

    // kSuite (Infomaniak) OAuth
    getKsuiteStatus: async (): Promise<OAuthConnectionStatus> => {
      return this.request({
        method: "GET",
        url: "/oauth/ksuite/status",
      })
    },

    authorizeKsuite: async (scopes: string[], redirectUri?: string): Promise<OAuthAuthorizationResponse> => {
      return this.request({
        method: "POST",
        url: "/oauth/ksuite/authorize",
        data: { scopes, redirectUri },
      })
    },

    revokeKsuite: async (): Promise<OAuthRevokeResponse> => {
      return this.request({
        method: "POST",
        url: "/oauth/ksuite/revoke",
      })
    },

    // Common
    getConnections: async (): Promise<OAuthConnectedAccount[]> => {
      return this.request({
        method: "GET",
        url: "/oauth/connections",
      })
    },
  }

  // =========================================================================
  // GOOGLE WORKSPACE (Service Account / Domain-Wide Delegation)
  // =========================================================================

  googleWorkspace = {
    saveServiceAccount: async (data: {
      serviceAccountJson: string
      adminEmail: string
      domain: string
    }): Promise<{ success: boolean; message: string; serviceAccountEmail?: string }> => {
      return this.request({
        method: "POST",
        url: "/google-workspace/service-account",
        data,
      })
    },

    getStatus: async (): Promise<{
      isConfigured: boolean
      adminEmail: string | null
      domain: string | null
      hasServiceAccount: boolean
    }> => {
      return this.request({
        method: "GET",
        url: "/google-workspace/service-account/status",
      })
    },

    testConnection: async (): Promise<{
      success: boolean
      message: string
      userCount: number | null
      sampleUser: string | null
    }> => {
      return this.request({
        method: "POST",
        url: "/google-workspace/service-account/test",
      })
    },

    getUploadDriveStatus: async (): Promise<{
      isConfigured: boolean
      folderId: string | null
      driveName: string | null
      source: string | null
      link: string | null
    }> => {
      return this.request({
        method: "GET",
        url: "/google-workspace/upload-drive/status",
      })
    },

    setupUploadDrive: async (): Promise<{
      folderId: string
      driveName: string
      created: boolean
      saEmail: string
      adminEmail: string
      link: string
    }> => {
      return this.request({
        method: "POST",
        url: "/google-workspace/upload-drive/setup",
      })
    },

    deleteServiceAccount: async (): Promise<{ success: boolean; message: string }> => {
      return this.request({
        method: "DELETE",
        url: "/google-workspace/service-account",
      })
    },

    getScopes: async (): Promise<{
      scopes: Array<{ scope: string; name: string; description: string }>
      adminConsoleUrl: string
    }> => {
      return this.request({
        method: "GET",
        url: "/google-workspace/scopes",
      })
    },

    syncUsers: async (): Promise<{
      success: boolean
      taskId: string | null
      message: string
    }> => {
      return this.request({
        method: "POST",
        url: "/google-workspace/sync/users",
      })
    },

    getSyncStatus: async (
      taskId: string
    ): Promise<{
      taskId: string
      status: string
      result: Record<string, unknown> | null
    }> => {
      return this.request({
        method: "GET",
        url: "/google-workspace/sync/status",
        params: { taskId },
      })
    },

    syncContacts: async (): Promise<{
      success: boolean
      taskId: string | null
      message: string
    }> => {
      return this.request({
        method: "POST",
        url: "/google-workspace/sync/contacts",
      })
    },

    getContactsSyncStatus: async (): Promise<{
      linkedContacts: number
      totalCustomerContacts: number
      lastSyncedAt: string | null
    }> => {
      return this.request({
        method: "GET",
        url: "/google-workspace/sync/contacts/status",
      })
    },

    getUnlinkedContacts: async (): Promise<
      Array<{
        resourceName: string
        firstName: string
        lastName: string
        emails: string[]
        phones: string[]
        organization: string | null
        position: string | null
        matchedContactId: string | null
      }>
    > => {
      return this.request({
        method: "GET",
        url: "/google-workspace/contacts/unlinked",
      })
    },

    importContacts: async (
      resourceNames: string[]
    ): Promise<{
      created: number
      linked: number
      skipped: number
      errors: number
    }> => {
      return this.request({
        method: "POST",
        url: "/google-workspace/contacts/import",
        data: { resourceNames },
      })
    },

    getMeetDefaults: async (): Promise<{
      defaultMeetUrl: string
      meetHostEmail: string
      ringTimeoutSeconds: number
      isConfigured: boolean
    }> => {
      return this.request({
        method: "GET",
        url: "/google-workspace/meet-defaults",
      })
    },

    updateMeetDefaults: async (data: {
      defaultMeetUrl: string
      meetHostEmail: string
      ringTimeoutSeconds: number
    }): Promise<{
      defaultMeetUrl: string
      meetHostEmail: string
      ringTimeoutSeconds: number
      isConfigured: boolean
    }> => {
      return this.request({
        method: "PUT",
        url: "/google-workspace/meet-defaults",
        data,
      })
    },
  }

  // =========================================================================
  // TICKET GMAIL (Google Workspace E-Mail Integration)
  // =========================================================================

  ticketGmail = {
    getThread: async (
      ticketId: string,
      query?: string
    ): Promise<{
      threadId: string
      messages: Array<{
        id: string
        threadId: string
        from: string
        to: string
        cc: string
        subject: string
        date: string
        body: string
        snippet: string
        labelIds: string[]
      }>
      messageCount: number
    }> => {
      return this.request({
        method: "GET",
        url: `/tickets/${ticketId}/gmail/thread`,
        params: query ? { query } : undefined,
      })
    },

    send: async (
      ticketId: string,
      data: {
        to: string
        subject: string
        bodyHtml: string
        cc?: string
        bcc?: string
      }
    ): Promise<{
      success: boolean
      gmailMessageId: string | null
      threadId: string | null
    }> => {
      return this.request({
        method: "POST",
        url: `/tickets/${ticketId}/gmail/send`,
        data,
      })
    },

    reply: async (
      ticketId: string,
      data: {
        threadId: string
        messageId: string
        to: string
        subject: string
        bodyHtml: string
        cc?: string
      }
    ): Promise<{
      success: boolean
      gmailMessageId: string | null
      threadId: string | null
    }> => {
      return this.request({
        method: "POST",
        url: `/tickets/${ticketId}/gmail/reply`,
        data,
      })
    },
  }

  // =========================================================================
  // TICKET CALENDAR (Google Workspace Kalender Integration)
  // =========================================================================

  ticketCalendar = {
    createEvent: async (
      ticketId: string,
      data: {
        summary?: string
        start: string
        end?: string
        description?: string
        attendees?: string[]
        withMeetLink?: boolean
      }
    ): Promise<{
      success: boolean
      eventId: string | null
      htmlLink: string | null
      meetLink: string | null
      summary: string
      start: Record<string, string>
      end: Record<string, string>
    }> => {
      return this.request({
        method: "POST",
        url: `/tickets/${ticketId}/calendar/event`,
        data,
      })
    },

    createMeetLink: async (
      ticketId: string,
      data?: {
        summary?: string
        durationMinutes?: number
        attendees?: string[]
      }
    ): Promise<{
      success: boolean
      meetLink: string
      eventId: string | null
      htmlLink: string | null
    }> => {
      return this.request({
        method: "POST",
        url: `/tickets/${ticketId}/calendar/meet`,
        data: data || {},
      })
    },
  }

  // =========================================================================
  // TICKET DRIVE (Google Workspace Drive Integration)
  // =========================================================================

  ticketDrive = {
    createFolder: async (
      ticketId: string
    ): Promise<{
      folderId: string
      folderName: string
      webViewLink: string
    }> => {
      return this.request({
        method: "POST",
        url: `/tickets/${ticketId}/drive/folder`,
      })
    },

    listFiles: async (
      ticketId: string
    ): Promise<{
      folderId: string
      files: Array<{
        id: string
        name: string
        mimeType: string
        size: number | null
        webViewLink: string | null
        modifiedTime: string | null
      }>
      count: number
    }> => {
      return this.request({
        method: "GET",
        url: `/tickets/${ticketId}/drive/files`,
      })
    },

    shareFolder: async (
      ticketId: string,
      data: { email: string; role?: string }
    ): Promise<{
      success: boolean
      permissionId: string | null
    }> => {
      return this.request({
        method: "POST",
        url: `/tickets/${ticketId}/drive/share`,
        data,
      })
    },
  }

  // =========================================================================
  // EMAIL PROCESSING LOGS
  // =========================================================================

  emailLogs = {
    getList: async (params?: {
      mailbox?: string
      action?: string
      search?: string
      skip?: number
      limit?: number
    }): Promise<{
      items: Array<{
        id: string
        mailboxEmail: string
        fromEmail: string
        fromName: string | null
        subject: string | null
        action: string
        reason: string | null
        ticketId: string | null
        ticketNumber: string | null
        createdAt: string
      }>
      total: number
    }> => {
      return this.request({
        method: "GET",
        url: "/email-logs",
        params,
      })
    },
  }

  // =========================================================================
  // IMAP CREDENTIALS
  // =========================================================================

  imap = {
    getCredentials: async () => {
      return this.request({ method: "GET", url: "/imap/credentials" })
    },
    createCredential: async (data: Record<string, unknown>) => {
      return this.request({ method: "POST", url: "/imap/credentials", data })
    },
    updateCredential: async (id: string, data: Record<string, unknown>) => {
      return this.request({ method: "PUT", url: `/imap/credentials/${id}`, data })
    },
    deleteCredential: async (id: string) => {
      return this.request({ method: "DELETE", url: `/imap/credentials/${id}` })
    },
    testCredential: async (id: string) => {
      return this.request({ method: "POST", url: `/imap/credentials/${id}/test` })
    },
  }

  // =========================================================================
  // NOTIFICATIONS
  // =========================================================================

  notifications = {
    list: async (params?: Record<string, unknown>) => {
      return this.request({ method: "GET", url: "/notifications", params })
    },
    count: async () => {
      return this.request({ method: "GET", url: "/notifications/count" })
    },
    markRead: async (id: string) => {
      return this.request({ method: "PUT", url: `/notifications/${id}/read` })
    },
    markAllRead: async () => {
      return this.request({ method: "PUT", url: "/notifications/read-all" })
    },
  }

  // =========================================================================
  // E-MAIL ZUSAMMENFASSUNGEN (Webhook von Apps Script)
  // =========================================================================

  emailSummaries = {
    status: async (): Promise<EmailSummaryRunStatus> => {
      return this.request({ method: "GET", url: "/email-summaries/status" })
    },
    list: async (limit = 200, includeHidden = false): Promise<EmailSummary[]> => {
      return this.request({ method: "GET", url: "/email-summaries/", params: { limit, includeHidden } })
    },
    listForTicket: async (ticketId: string): Promise<EmailSummary[]> => {
      return this.request({ method: "GET", url: `/email-summaries/ticket/${ticketId}` })
    },
    markRead: async (id: string): Promise<EmailSummary> => {
      return this.request({ method: "POST", url: `/email-summaries/${id}/read` })
    },
    markDone: async (id: string): Promise<EmailSummary> => {
      return this.request({ method: "POST", url: `/email-summaries/${id}/done` })
    },
    reopen: async (id: string): Promise<EmailSummary> => {
      return this.request({ method: "POST", url: `/email-summaries/${id}/reopen` })
    },
    hide: async (id: string): Promise<EmailSummary> => {
      return this.request({ method: "POST", url: `/email-summaries/${id}/hide` })
    },
    unhide: async (id: string): Promise<EmailSummary> => {
      return this.request({ method: "POST", url: `/email-summaries/${id}/unhide` })
    },
    createTicket: async (data: {
      summaryIds: string[]
      ticketId?: string | null
      ticketNumber?: string | null
      hideAfterLink?: boolean
    }): Promise<{
      action: "created" | "linked"
      ticketId: string
      ticketNumber: string
      summaryIds: string[]
      summaries: EmailSummary[]
    }> => {
      return this.request({ method: "POST", url: "/email-summaries/ticket", data })
    },
    draftReply: async (
      id: string,
      data: { instructions: string; recipientMode: "sender" | "reply_all"; contextSummaryIds?: string[] }
    ): Promise<EmailSummaryReplyDraft> => {
      return this.request({ method: "POST", url: `/email-summaries/${id}/reply/draft`, data })
    },
    sendReply: async (
      id: string,
      data: { to: string; cc?: string | null; subject: string; bodyText: string; markDone?: boolean; contextSummaryIds?: string[] }
    ): Promise<EmailSummaryReplySendResult> => {
      return this.request({ method: "POST", url: `/email-summaries/${id}/reply/send`, data })
    },
    delete: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/email-summaries/${id}` })
    },
  }

  // =========================================================================
  // PUBLISHED PAGES
  // =========================================================================

  async getPages(): Promise<{ items: PublishedPageListItem[]; total: number }> {
    const response = await this.client.get("/pages")
    return response.data
  }

  async getPage(id: string): Promise<PublishedPage> {
    const response = await this.client.get(`/pages/${id}`)
    return response.data
  }

  async createPage(data: PublishedPageCreate): Promise<PublishedPage> {
    const response = await this.client.post("/pages", data)
    return response.data
  }

  async updatePage(id: string, data: PublishedPageUpdate): Promise<PublishedPage> {
    const response = await this.client.put(`/pages/${id}`, data)
    return response.data
  }

  async deletePage(id: string): Promise<void> {
    await this.client.delete(`/pages/${id}`)
  }

  async togglePage(id: string): Promise<PublishedPage> {
    const response = await this.client.post(`/pages/${id}/toggle`)
    return response.data
  }

  async listPageFiles(id: string): Promise<PublishedPageFileItem[]> {
    const response = await this.client.get(`/pages/${id}/files`)
    return response.data
  }

  async uploadPageWebDirectory(id: string, file: File): Promise<PublishedPageUploadResponse> {
    const formData = new FormData()
    formData.append("file", file)
    const response = await this.client.post(`/pages/${id}/web-directory`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    return response.data
  }

  async getPagePermissions(id: string): Promise<PublishedPagePermission[]> {
    const response = await this.client.get(`/pages/${id}/permissions`)
    return response.data
  }

  async updatePagePermissions(
    id: string,
    data: { organizationIds?: string[]; contactIds?: string[]; departmentIds?: string[] }
  ): Promise<PublishedPagePermission[]> {
    const response = await this.client.put(`/pages/${id}/permissions`, data)
    return response.data
  }

  // =========================================================================
  // DSGVO ENDPOINTS
  // =========================================================================

  dsgvo = {
    // Dashboard
    getDashboard: async (): Promise<import("@/types/dsgvo").DsgvoDashboardResponse> => {
      return this.request({ method: "GET", url: "/dsgvo/dashboard" })
    },

    // Vendors
    getVendors: async (search?: string): Promise<import("@/types/dsgvo").DsgvoVendor[]> => {
      const res: any = await this.request({ method: "GET", url: "/dsgvo/vendors", params: search ? { search } : undefined })
      return res.items ?? res
    },
    getVendor: async (id: string): Promise<import("@/types/dsgvo").DsgvoVendor> => {
      return this.request({ method: "GET", url: `/dsgvo/vendors/${id}` })
    },
    createVendor: async (data: import("@/types/dsgvo").DsgvoVendorCreate): Promise<import("@/types/dsgvo").DsgvoVendor> => {
      return this.request({ method: "POST", url: "/dsgvo/vendors", data })
    },
    updateVendor: async (id: string, data: import("@/types/dsgvo").DsgvoVendorUpdate): Promise<import("@/types/dsgvo").DsgvoVendor> => {
      return this.request({ method: "PUT", url: `/dsgvo/vendors/${id}`, data })
    },
    deleteVendor: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/dsgvo/vendors/${id}` })
    },

    // AVV Entries
    getAVVEntries: async (): Promise<import("@/types/dsgvo").DsgvoAVVEntry[]> => {
      const res: any = await this.request({ method: "GET", url: "/dsgvo/avv/entries" })
      return res.items ?? res
    },
    createAVVEntry: async (data: import("@/types/dsgvo").DsgvoAVVEntryCreate): Promise<import("@/types/dsgvo").DsgvoAVVEntry> => {
      return this.request({ method: "POST", url: "/dsgvo/avv/entries", data })
    },
    updateAVVEntry: async (id: string, data: import("@/types/dsgvo").DsgvoAVVEntryUpdate): Promise<import("@/types/dsgvo").DsgvoAVVEntry> => {
      return this.request({ method: "PUT", url: `/dsgvo/avv/entries/${id}`, data })
    },
    deleteAVVEntry: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/dsgvo/avv/entries/${id}` })
    },

    // AVV Contracts
    getAVVContracts: async (): Promise<import("@/types/dsgvo").DsgvoAVVContract[]> => {
      const res: any = await this.request({ method: "GET", url: "/dsgvo/avv/contracts" })
      return res.items ?? res
    },
    createAVVContract: async (data: import("@/types/dsgvo").DsgvoAVVContractCreate): Promise<import("@/types/dsgvo").DsgvoAVVContract> => {
      return this.request({ method: "POST", url: "/dsgvo/avv/contracts", data })
    },
    updateAVVContract: async (id: string, data: import("@/types/dsgvo").DsgvoAVVContractUpdate): Promise<import("@/types/dsgvo").DsgvoAVVContract> => {
      return this.request({ method: "PUT", url: `/dsgvo/avv/contracts/${id}`, data })
    },
    deleteAVVContract: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/dsgvo/avv/contracts/${id}` })
    },

    // AVV Profile
    getAVVProfile: async (): Promise<import("@/types/dsgvo").DsgvoAVVProfile> => {
      return this.request({ method: "GET", url: "/dsgvo/avv/profile" })
    },
    updateAVVProfile: async (data: import("@/types/dsgvo").DsgvoAVVProfileUpdate): Promise<import("@/types/dsgvo").DsgvoAVVProfile> => {
      return this.request({ method: "PUT", url: "/dsgvo/avv/profile", data })
    },
    generateContractFromTemplate: async (contractId: string): Promise<any> => {
      return this.request({ method: "POST", url: `/dsgvo/avv/contracts/${contractId}/generate` })
    },

    // AVV Templates
    getTemplates: async (): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/dsgvo/avv/templates" })
      return res.items ?? res
    },
    getTemplate: async (id: string): Promise<any> => {
      return this.request({ method: "GET", url: `/dsgvo/avv/templates/${id}` })
    },
    createTemplate: async (data: any): Promise<any> => {
      return this.request({ method: "POST", url: "/dsgvo/avv/templates", data })
    },
    updateTemplate: async (id: string, data: any): Promise<any> => {
      return this.request({ method: "PUT", url: `/dsgvo/avv/templates/${id}`, data })
    },
    deleteTemplate: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/dsgvo/avv/templates/${id}` })
    },
    getAffectedContracts: async (templateId: string): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: `/dsgvo/avv/templates/${templateId}/affected-contracts` })
      return res.items ?? res
    },
    applyTemplate: async (templateId: string, data: { contractIds: string[]; changeReason?: string }): Promise<any> => {
      return this.request({ method: "POST", url: `/dsgvo/avv/templates/${templateId}/apply`, data })
    },

    // AVV Template Versions
    getTemplateVersions: async (templateId: string): Promise<any[]> => {
      return this.request({ method: "GET", url: `/dsgvo/avv/templates/${templateId}/versions` })
    },
    getTemplateVersionContent: async (templateId: string, versionId: string): Promise<any> => {
      return this.request({ method: "GET", url: `/dsgvo/avv/templates/${templateId}/versions/${versionId}` })
    },
    restoreTemplateVersion: async (templateId: string, versionId: string): Promise<any> => {
      return this.request({ method: "POST", url: `/dsgvo/avv/templates/${templateId}/versions/${versionId}/restore` })
    },

    // AVV Export
    exportTemplatePdf: async (templateId: string, fillPlaceholders = false): Promise<void> => {
      const response = await this.client.get(`/dsgvo/avv/templates/${templateId}/export/pdf`, {
        params: { fill_placeholders: fillPlaceholders },
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      const disposition = response.headers["content-disposition"]
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || "vorlage.pdf"
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    exportTemplateDocx: async (templateId: string, fillPlaceholders = false): Promise<void> => {
      const response = await this.client.get(`/dsgvo/avv/templates/${templateId}/export/docx`, {
        params: { fill_placeholders: fillPlaceholders },
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      const disposition = response.headers["content-disposition"]
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || "vorlage.docx"
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    exportContractPdf: async (contractId: string): Promise<void> => {
      const response = await this.client.get(`/dsgvo/avv/contracts/${contractId}/export/pdf`, {
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      const disposition = response.headers["content-disposition"]
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || "vertrag.pdf"
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    exportContractDocx: async (contractId: string): Promise<void> => {
      const response = await this.client.get(`/dsgvo/avv/contracts/${contractId}/export/docx`, {
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      const disposition = response.headers["content-disposition"]
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || "vertrag.docx"
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    previewContractPdf: async (contractId: string): Promise<void> => {
      const response = await this.client.get(`/dsgvo/avv/contracts/${contractId}/export/pdf`, {
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }))
      window.open(url, "_blank")
    },
    exportContractPackage: async (contractId: string): Promise<void> => {
      const response = await this.client.get(`/dsgvo/avv/contracts/${contractId}/export/package`, {
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      const disposition = response.headers["content-disposition"]
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || "vertragspaket.zip"
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },

    // AVV Subcontractors
    getSubcontractors: async (contractId?: string): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/dsgvo/avv/subcontractors", params: contractId ? { contractId } : undefined })
      return res.items ?? res
    },
    createSubcontractor: async (data: any): Promise<any> => {
      return this.request({ method: "POST", url: "/dsgvo/avv/subcontractors", data })
    },
    updateSubcontractor: async (id: string, data: any): Promise<any> => {
      return this.request({ method: "PUT", url: `/dsgvo/avv/subcontractors/${id}`, data })
    },
    deleteSubcontractor: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/dsgvo/avv/subcontractors/${id}` })
    },

    // AVV Contract Versions
    getContractVersions: async (contractId: string): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: `/dsgvo/avv/contracts/${contractId}/versions` })
      return res.items ?? res
    },

    // VVT (Processing Activities)
    getProcessingActivities: async (search?: string): Promise<import("@/types/dsgvo").DsgvoProcessingActivity[]> => {
      const res: any = await this.request({ method: "GET", url: "/dsgvo/vvt", params: search ? { search } : undefined })
      return res.items ?? res
    },
    getProcessingActivity: async (id: string): Promise<import("@/types/dsgvo").DsgvoProcessingActivity> => {
      return this.request({ method: "GET", url: `/dsgvo/vvt/${id}` })
    },
    createProcessingActivity: async (data: import("@/types/dsgvo").DsgvoProcessingActivityCreate): Promise<import("@/types/dsgvo").DsgvoProcessingActivity> => {
      return this.request({ method: "POST", url: "/dsgvo/vvt", data })
    },
    updateProcessingActivity: async (id: string, data: import("@/types/dsgvo").DsgvoProcessingActivityUpdate): Promise<import("@/types/dsgvo").DsgvoProcessingActivity> => {
      return this.request({ method: "PUT", url: `/dsgvo/vvt/${id}`, data })
    },
    deleteProcessingActivity: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/dsgvo/vvt/${id}` })
    },

    // Incidents
    getIncidents: async (search?: string): Promise<import("@/types/dsgvo").DsgvoIncident[]> => {
      const res: any = await this.request({ method: "GET", url: "/dsgvo/incidents", params: search ? { search } : undefined })
      return res.items ?? res
    },
    getIncident: async (id: string): Promise<import("@/types/dsgvo").DsgvoIncident> => {
      return this.request({ method: "GET", url: `/dsgvo/incidents/${id}` })
    },
    createIncident: async (data: import("@/types/dsgvo").DsgvoIncidentCreate): Promise<import("@/types/dsgvo").DsgvoIncident> => {
      return this.request({ method: "POST", url: "/dsgvo/incidents", data })
    },
    updateIncident: async (id: string, data: import("@/types/dsgvo").DsgvoIncidentUpdate): Promise<import("@/types/dsgvo").DsgvoIncident> => {
      return this.request({ method: "PUT", url: `/dsgvo/incidents/${id}`, data })
    },
    deleteIncident: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/dsgvo/incidents/${id}` })
    },

    // DSR (Data Subject Requests)
    getDSRs: async (search?: string): Promise<import("@/types/dsgvo").DsgvoDataSubjectRequest[]> => {
      const res: any = await this.request({ method: "GET", url: "/dsgvo/dsr", params: search ? { search } : undefined })
      return res.items ?? res
    },
    getDSR: async (id: string): Promise<import("@/types/dsgvo").DsgvoDataSubjectRequest> => {
      return this.request({ method: "GET", url: `/dsgvo/dsr/${id}` })
    },
    createDSR: async (data: import("@/types/dsgvo").DsgvoDataSubjectRequestCreate): Promise<import("@/types/dsgvo").DsgvoDataSubjectRequest> => {
      return this.request({ method: "POST", url: "/dsgvo/dsr", data })
    },
    updateDSR: async (id: string, data: import("@/types/dsgvo").DsgvoDataSubjectRequestUpdate): Promise<import("@/types/dsgvo").DsgvoDataSubjectRequest> => {
      return this.request({ method: "PUT", url: `/dsgvo/dsr/${id}`, data })
    },
    deleteDSR: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/dsgvo/dsr/${id}` })
    },

    // Documents
    getDocuments: async (search?: string): Promise<import("@/types/dsgvo").DsgvoDocument[]> => {
      const res: any = await this.request({ method: "GET", url: "/dsgvo/documents", params: search ? { search } : undefined })
      return res.items ?? res
    },
    getDocument: async (id: string): Promise<import("@/types/dsgvo").DsgvoDocument> => {
      return this.request({ method: "GET", url: `/dsgvo/documents/${id}` })
    },
    createDocument: async (data: import("@/types/dsgvo").DsgvoDocumentCreate): Promise<import("@/types/dsgvo").DsgvoDocument> => {
      return this.request({ method: "POST", url: "/dsgvo/documents", data })
    },
    updateDocument: async (id: string, data: import("@/types/dsgvo").DsgvoDocumentUpdate): Promise<import("@/types/dsgvo").DsgvoDocument> => {
      return this.request({ method: "PUT", url: `/dsgvo/documents/${id}`, data })
    },
    deleteDocument: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/dsgvo/documents/${id}` })
    },

    // Tasks
    getTasks: async (filters?: { status?: string; priority?: string }): Promise<import("@/types/dsgvo").DsgvoTask[]> => {
      const res: any = await this.request({ method: "GET", url: "/dsgvo/tasks", params: filters })
      return res.items ?? res
    },
    getTask: async (id: string): Promise<import("@/types/dsgvo").DsgvoTask> => {
      return this.request({ method: "GET", url: `/dsgvo/tasks/${id}` })
    },
    createTask: async (data: import("@/types/dsgvo").DsgvoTaskCreate): Promise<import("@/types/dsgvo").DsgvoTask> => {
      return this.request({ method: "POST", url: "/dsgvo/tasks", data })
    },
    updateTask: async (id: string, data: import("@/types/dsgvo").DsgvoTaskUpdate): Promise<import("@/types/dsgvo").DsgvoTask> => {
      return this.request({ method: "PUT", url: `/dsgvo/tasks/${id}`, data })
    },
    deleteTask: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/dsgvo/tasks/${id}` })
    },

    // Settings
    getSettings: async (): Promise<import("@/types/dsgvo").DsgvoSettings> => {
      return this.request({ method: "GET", url: "/dsgvo/settings" })
    },
    updateSettings: async (data: import("@/types/dsgvo").DsgvoSettingsUpdate): Promise<import("@/types/dsgvo").DsgvoSettings> => {
      return this.request({ method: "PUT", url: "/dsgvo/settings", data })
    },

    // Notifications
    getNotifications: async (unreadOnly?: boolean): Promise<import("@/types/dsgvo").DsgvoNotification[]> => {
      return this.request({ method: "GET", url: "/dsgvo/notifications", params: unreadOnly ? { unread_only: true } : undefined })
    },
    markNotificationRead: async (id: string): Promise<void> => {
      return this.request({ method: "POST", url: `/dsgvo/notifications/${id}/read` })
    },
    markAllNotificationsRead: async (): Promise<void> => {
      return this.request({ method: "POST", url: "/dsgvo/notifications/read-all" })
    },
  }

  // =========================================================================
  // RECHNUNGEN ENDPOINTS
  // =========================================================================

  rechnungen = {
    // Dashboard
    getDashboard: async (): Promise<import("@/types/rechnungen").RechnungenDashboard> => {
      const res: any = await this.request({ method: "GET", url: "/rechnungen/dashboard/stats" })
      const skontoOpportunities = Array.isArray(res.skontoOpportunities) ? res.skontoOpportunities : []
      const skontoSavings = skontoOpportunities.reduce(
        (sum: number, item: { savings?: number | null }) => sum + Number(item.savings || 0),
        0,
      )
      return {
        openInvoiceCount: res.openInvoiceCount ?? res.invoicesOpen ?? 0,
        openInvoiceAmount: res.openInvoiceAmount ?? res.totalOpenAmount ?? 0,
        overdueInvoiceCount: res.overdueInvoiceCount ?? res.invoicesOverdue ?? 0,
        overdueInvoiceAmount: res.overdueInvoiceAmount ?? res.totalOverdueAmount ?? 0,
        monthlyVolume: res.monthlyVolume ?? 0,
        pendingApprovalCount: res.pendingApprovalCount ?? 0,
        paidThisMonth: res.paidThisMonth ?? res.totalPaidThisMonth ?? 0,
        skontoSavings: res.skontoSavings ?? skontoSavings,
      }
    },

    // Invoices CRUD
    getInvoices: async (params?: Record<string, any>): Promise<import("@/types/rechnungen").InvoiceListItem[]> => {
      const res: any = await this.request({ method: "GET", url: "/rechnungen/invoices", params })
      return res.items ?? res
    },
    getInvoicesPaged: async (
      params?: Record<string, any>,
    ): Promise<{ items: import("@/types/rechnungen").InvoiceListItem[]; total: number }> => {
      const res: any = await this.request({ method: "GET", url: "/rechnungen/invoices", params })
      if (Array.isArray(res)) return { items: res, total: res.length }
      return { items: res.items ?? [], total: res.total ?? (res.items?.length ?? 0) }
    },
    getInvoice: async (id: string): Promise<import("@/types/rechnungen").Invoice> => {
      return this.request({ method: "GET", url: `/rechnungen/invoices/${id}` })
    },
    // Distinkte Rechnungsjahre (fuer die Zeitraum-Filter-Buttons der Liste)
    getInvoiceYears: async (): Promise<number[]> => {
      const res: any = await this.request({ method: "GET", url: "/rechnungen/invoices/facets/years" })
      return Array.isArray(res?.years) ? res.years : []
    },
    createInvoice: async (data: import("@/types/rechnungen").InvoiceCreate): Promise<import("@/types/rechnungen").Invoice> => {
      return this.request({ method: "POST", url: "/rechnungen/invoices", data })
    },
    updateInvoice: async (id: string, data: import("@/types/rechnungen").InvoiceUpdate): Promise<import("@/types/rechnungen").Invoice> => {
      return this.request({ method: "PUT", url: `/rechnungen/invoices/${id}`, data })
    },
    deleteInvoice: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/rechnungen/invoices/${id}` })
    },
    updateInvoiceStatus: async (id: string, status: string): Promise<import("@/types/rechnungen").Invoice> => {
      return this.request({ method: "PATCH", url: `/rechnungen/invoices/${id}/status`, data: { status } })
    },
    cancelInvoice: async (
      id: string,
      reason: string,
      category?: string,
    ): Promise<import("@/types/rechnungen").Invoice> => {
      return this.request({
        method: "POST",
        url: `/rechnungen/invoices/${id}/storno`,
        // Backend erwartet camelCase-Aliasse (cancellationReason/-Category);
        // das fruehere { reason } wurde vom Schema ignoriert -> Grund ging verloren.
        data: { cancellationReason: reason || undefined, cancellationCategory: category },
      })
    },

    // Ausgangsrechnungen (aus Easybill)
    listOutgoing: async (params?: Record<string, any>): Promise<OutgoingInvoiceItem[]> => {
      return this.request({ method: "GET", url: "/rechnungen/outgoing/", params })
    },
    outgoingDashboard: async (): Promise<OutgoingInvoiceDashboard> => {
      return this.request({ method: "GET", url: "/rechnungen/outgoing/dashboard" })
    },
    syncOutgoing: async (): Promise<{ taskId?: string; status?: string; created?: number; updated?: number; total?: number; pages?: number }> => {
      return this.request({ method: "POST", url: "/rechnungen/outgoing/sync" })
    },
    syncOutgoingFull: async (): Promise<{ taskId?: string; status?: string; created?: number; updated?: number; total?: number; pages?: number }> => {
      return this.request({ method: "POST", url: "/rechnungen/outgoing/sync/full" })
    },
    syncOutgoingStatus: async (taskId: string): Promise<{
      taskId: string
      status: string
      ready: boolean
      result?: { created: number; updated: number; total: number; pages: number } | null
      error?: string | null
    }> => {
      return this.request({ method: "GET", url: `/rechnungen/outgoing/sync/status/${taskId}` })
    },
    /**
     * Stammdaten der Rechnung -- liest nur aus der lokalen DB, kein Easybill-Call.
     * Schnelle Antwort, sofort renderbar.
     */
    getOutgoingDetail: async (id: string): Promise<OutgoingInvoiceDetail> => {
      return this.request({
        method: "GET",
        url: `/rechnungen/outgoing/${id}`,
        params: { include_raw: false },
      })
    },
    /**
     * Stammdaten + Easybill-Raw (Positionen, etc.) -- triggert einen synchronen
     * API-Call zu Easybill, deutlich langsamer. In der UI parallel zum
     * Stammdaten-Query verwenden, damit erstere sofort sichtbar sind.
     */
    getOutgoingDetailWithRaw: async (id: string): Promise<OutgoingInvoiceDetail> => {
      return this.request({
        method: "GET",
        url: `/rechnungen/outgoing/${id}`,
        params: { include_raw: true },
      })
    },
    /**
     * Laedt das PDF mit Bearer-Auth via axios. iframe/a-href koennen die
     * Bearer-Header nicht mitschicken, daher als Blob holen und im Aufrufer
     * ueber URL.createObjectURL einbetten.
     */
    outgoingPdfBlob: async (id: string): Promise<Blob> => {
      const response = await this.client.get(`/rechnungen/outgoing/${id}/pdf`, {
        responseType: "blob",
      })
      return new Blob([response.data], { type: "application/pdf" })
    },
    outgoingPdfUrl: (id: string): string => {
      return `/api/v1/rechnungen/outgoing/${id}/pdf`
    },
    getInvoicePdfUrl: async (id: string): Promise<{ url: string; filename?: string | null }> => {
      return this.request({ method: "GET", url: `/rechnungen/invoices/${id}/pdf` })
    },
    getInvoiceXmlUrl: async (id: string): Promise<{ url: string; filename?: string | null }> => {
      return this.request({ method: "GET", url: `/rechnungen/invoices/${id}/xml` })
    },
    reprocessInvoiceOCR: async (
      id: string,
      data: import("@/types/rechnungen").OCRReprocessRequest,
    ): Promise<import("@/types/rechnungen").OCRTaskResponse> => {
      return this.request({ method: "POST", url: `/rechnungen/invoices/${id}/ocr/reprocess`, data })
    },

    setInvoiceRelevance: async (
      id: string,
      relevance: "invoice" | "non_invoice" | "review",
    ): Promise<{ invoiceId: string; relevance: string }> => {
      return this.request({ method: "PUT", url: `/rechnungen/invoices/${id}/relevance`, data: { relevance } })
    },

    findCancellationLink: async (
      id: string,
    ): Promise<{ invoiceId: string; cancellation: import("@/types/rechnungen").CancellationInfo }> => {
      return this.request({ method: "POST", url: `/rechnungen/invoices/${id}/cancellation-link` })
    },

    setCancellationStatus: async (
      id: string,
      cancelled: boolean,
      cancelledAt?: string | null,
    ): Promise<{ invoiceId: string; cancellationDone: import("@/types/rechnungen").CancellationDone | null }> => {
      return this.request({
        method: "PUT",
        url: `/rechnungen/invoices/${id}/cancellation-status`,
        data: { cancelled, cancelledAt: cancelledAt ?? null },
      })
    },

    draftCancellationEmail: async (
      id: string,
      payload?: { reasons?: string[]; note?: string | null },
    ): Promise<{ to: string; toResolvedByAi?: boolean; contactConfidence?: number | null; subject: string; body: string; from?: string | null; hasPdf: boolean }> => {
      return this.request({
        method: "POST",
        url: `/rechnungen/invoices/${id}/cancellation-email/draft`,
        data: { reasons: payload?.reasons ?? [], note: payload?.note ?? null },
      })
    },

    sendCancellationEmail: async (
      id: string,
      payload: { to: string; subject: string; body: string; attachInvoice: boolean },
    ): Promise<{ success: boolean; gmailMessageId?: string | null; cancellationDone: import("@/types/rechnungen").CancellationDone | null }> => {
      return this.request({
        method: "POST",
        url: `/rechnungen/invoices/${id}/cancellation-email/send`,
        data: payload,
      })
    },

    // Positions
    addPosition: async (invoiceId: string, data: import("@/types/rechnungen").InvoicePositionCreate): Promise<import("@/types/rechnungen").InvoicePosition> => {
      return this.request({ method: "POST", url: `/rechnungen/invoices/${invoiceId}/positions`, data })
    },
    updatePosition: async (invoiceId: string, posId: string, data: import("@/types/rechnungen").InvoicePositionCreate): Promise<import("@/types/rechnungen").InvoicePosition> => {
      return this.request({ method: "PUT", url: `/rechnungen/invoices/${invoiceId}/positions/${posId}`, data })
    },
    deletePosition: async (invoiceId: string, posId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/rechnungen/invoices/${invoiceId}/positions/${posId}` })
    },

    // Payment Agreements
    getAgreements: async (invoiceId: string): Promise<import("@/types/rechnungen").PaymentAgreement[]> => {
      const res: any = await this.request({ method: "GET", url: `/rechnungen/invoices/${invoiceId}/agreements` })
      return res.items ?? res
    },
    createAgreement: async (invoiceId: string, data: import("@/types/rechnungen").PaymentAgreementCreate): Promise<import("@/types/rechnungen").PaymentAgreement> => {
      return this.request({ method: "POST", url: `/rechnungen/invoices/${invoiceId}/agreements`, data })
    },

    // Payment Runs
    getPaymentRuns: async (params?: Record<string, any>): Promise<import("@/types/rechnungen").PaymentRun[]> => {
      const res: any = await this.request({ method: "GET", url: "/rechnungen/payment-runs", params })
      return res.items ?? res
    },
    getPaymentRun: async (id: string): Promise<import("@/types/rechnungen").PaymentRun> => {
      return this.request({ method: "GET", url: `/rechnungen/payment-runs/${id}` })
    },
    createPaymentRun: async (data: import("@/types/rechnungen").PaymentRunCreate): Promise<import("@/types/rechnungen").PaymentRun> => {
      return this.request({ method: "POST", url: "/rechnungen/payment-runs", data })
    },
    executePaymentRun: async (id: string): Promise<import("@/types/rechnungen").PaymentRun> => {
      return this.request({ method: "POST", url: `/rechnungen/payment-runs/${id}/execute` })
    },
    markPaymentRunPaid: async (id: string): Promise<import("@/types/rechnungen").PaymentRun> => {
      return this.request({ method: "POST", url: `/rechnungen/payment-runs/${id}/mark-paid` })
    },
    cancelPaymentRun: async (id: string): Promise<import("@/types/rechnungen").PaymentRun> => {
      return this.request({ method: "POST", url: `/rechnungen/payment-runs/${id}/cancel` })
    },
    addPaymentRunItem: async (runId: string, data: import("@/types/rechnungen").PaymentRunItemCreate): Promise<import("@/types/rechnungen").PaymentRunItem> => {
      return this.request({ method: "POST", url: `/rechnungen/payment-runs/${runId}/items`, data })
    },
    removePaymentRunItem: async (runId: string, itemId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/rechnungen/payment-runs/${runId}/items/${itemId}` })
    },
    downloadSepaXml: async (id: string): Promise<void> => {
      const response = await this.client.get(`/rechnungen/payment-runs/${id}/sepa-xml`, {
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      const disposition = response.headers["content-disposition"]
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || "sepa.xml"
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },

    // Bank Accounts
    getBankAccounts: async (orgId: string): Promise<import("@/types/rechnungen").BankAccount[]> => {
      const res: any = await this.request({ method: "GET", url: `/rechnungen/organizations/${orgId}/bank-accounts` })
      return res.items ?? res
    },
    createBankAccount: async (orgId: string, data: import("@/types/rechnungen").BankAccountCreate): Promise<import("@/types/rechnungen").BankAccount> => {
      return this.request({ method: "POST", url: `/rechnungen/organizations/${orgId}/bank-accounts`, data })
    },
    updateBankAccount: async (id: string, data: import("@/types/rechnungen").BankAccountCreate): Promise<import("@/types/rechnungen").BankAccount> => {
      return this.request({ method: "PUT", url: `/rechnungen/bank-accounts/${id}`, data })
    },
    deleteBankAccount: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/rechnungen/bank-accounts/${id}` })
    },

    // Chef-only Banking (eigene Firmenkonten via FinTS)
    getBankingConnections: async (): Promise<import("@/types/rechnungen").BankingConnection[]> => {
      const res: any = await this.request({ method: "GET", url: "/rechnungen/banking/connections" })
      return res.items ?? res
    },
    createBankingConnection: async (data: import("@/types/rechnungen").BankingConnectionCreate): Promise<import("@/types/rechnungen").BankingConnection> => {
      return this.request({ method: "POST", url: "/rechnungen/banking/connections", data })
    },
    updateBankingConnection: async (id: string, data: import("@/types/rechnungen").BankingConnectionUpdate): Promise<import("@/types/rechnungen").BankingConnection> => {
      return this.request({ method: "PUT", url: `/rechnungen/banking/connections/${id}`, data })
    },
    deleteBankingConnection: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/rechnungen/banking/connections/${id}` })
    },
    syncBankingConnection: async (id: string, days = 90): Promise<{ taskId: string; status: string }> => {
      return this.request({ method: "POST", url: `/rechnungen/banking/connections/${id}/sync`, params: { days } })
    },
    getBankingSyncStatus: async (taskId: string): Promise<import("@/types/rechnungen").BankingTaskStatus> => {
      return this.request({ method: "GET", url: `/rechnungen/banking/sync/status/${taskId}` })
    },
    getBankingAccounts: async (connectionId?: string): Promise<import("@/types/rechnungen").BankingOwnAccount[]> => {
      const res: any = await this.request({
        method: "GET",
        url: "/rechnungen/banking/accounts",
        params: connectionId ? { connectionId } : undefined,
      })
      return res.items ?? res
    },
    getBankingTransactions: async (accountId: string, params?: Record<string, any>): Promise<import("@/types/rechnungen").BankingTransaction[]> => {
      const res: any = await this.request({ method: "GET", url: `/rechnungen/banking/accounts/${accountId}/transactions`, params })
      return res.items ?? res
    },
    getBankingSyncLogs: async (): Promise<import("@/types/rechnungen").BankingSyncLog[]> => {
      const res: any = await this.request({ method: "GET", url: "/rechnungen/banking/sync-logs" })
      return res.items ?? res
    },
    getBankingTanSessions: async (): Promise<import("@/types/rechnungen").BankingTanSession[]> => {
      const res: any = await this.request({ method: "GET", url: "/rechnungen/banking/tan-sessions" })
      return res.items ?? res
    },
    submitBankingTan: async (sessionId: string, tan?: string): Promise<{ taskId: string; status: string }> => {
      return this.request({ method: "POST", url: `/rechnungen/banking/tan-sessions/${sessionId}/submit`, data: { tan } })
    },

    // OCR
    uploadForOCR: async (file: File): Promise<any> => {
      const fd = new FormData()
      fd.append("file", file)
      return this.request({
        method: "POST",
        url: "/rechnungen/ocr/upload",
        data: fd,
        headers: { "Content-Type": "multipart/form-data" },
      })
    },
    getProcessingLogs: async (): Promise<import("@/types/rechnungen").ProcessingLog[]> => {
      const res: any = await this.request({ method: "GET", url: "/rechnungen/ocr/processing-logs" })
      return res.items ?? res
    },
  }

  // =========================================================================
  // UI-LAYOUT (anpassbare Karten/Reiter)
  // =========================================================================

  uiLayout = {
    get: async (viewType: string): Promise<import("@/types/uiLayout").CardLayoutResponse> => {
      return this.request({ method: "GET", url: `/ui/card-layout/${viewType}` })
    },
    save: async (
      viewType: string,
      layout: import("@/types/uiLayout").CardLayout,
      scope: "personal" | "tenant" = "personal",
    ): Promise<import("@/types/uiLayout").CardLayoutResponse> => {
      return this.request({ method: "PUT", url: `/ui/card-layout/${viewType}`, params: { scope }, data: { layout } })
    },
    reset: async (
      viewType: string,
      scope: "personal" | "tenant" = "personal",
    ): Promise<import("@/types/uiLayout").CardLayoutResponse> => {
      return this.request({ method: "DELETE", url: `/ui/card-layout/${viewType}`, params: { scope } })
    },
  }

  // =========================================================================
  // HR (PERSONALVERWALTUNG) ENDPOINTS
  // =========================================================================

  hr = {
    // Dashboard
    getDashboard: async (): Promise<import("@/types/hr").HrDashboard> => {
      return this.request({ method: "GET", url: "/hr/dashboard" })
    },

    // Employees
    getEmployees: async (search?: string): Promise<import("@/types/hr").EmployeeListItem[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/employees", params: search ? { search } : undefined })
      return res.items ?? res
    },
    getEmployeesGrid: async (params: {
      skip?: number
      limit?: number
      search?: string
      isActive?: boolean
      sortBy?: string
      sortDir?: "asc" | "desc"
    }): Promise<PaginatedResponse<any>> => {
      return this.request({
        method: "GET",
        url: "/hr/employees/grid",
        params: {
          skip: params.skip,
          limit: params.limit,
          search: params.search,
          is_active: params.isActive,
          sort_by: params.sortBy,
          sort_dir: params.sortDir,
        },
      })
    },
    getEmployee: async (id: string): Promise<import("@/types/hr").Employee> => {
      return this.request({ method: "GET", url: `/hr/employees/${id}` })
    },
    getMyEmployee: async (): Promise<import("@/types/hr").Employee> => {
      return this.request({ method: "GET", url: "/hr/employees/me" })
    },
    setMyEasybillKey: async (apiKey: string | null): Promise<import("@/types/hr").EasybillKeyResult> => {
      return this.request({ method: "PUT", url: "/hr/employees/me/easybill-key", data: { apiKey } })
    },
    setMyEasybillLogin: async (loginId: string): Promise<import("@/types/hr").EasybillKeyResult> => {
      return this.request({ method: "PUT", url: "/hr/employees/me/easybill-login", data: { loginId } })
    },
    getMyTeam: async (): Promise<import("@/types/hr").EmployeeListItem[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/employees/my-team" })
      return res.items ?? res
    },
    getTeamDashboard: async (): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/employees/team-dashboard" })
      return Array.isArray(res) ? res : (res.items ?? [])
    },
    createEmployee: async (data: import("@/types/hr").EmployeeCreate): Promise<import("@/types/hr").Employee> => {
      return this.request({ method: "POST", url: "/hr/employees", data })
    },
    updateEmployee: async (id: string, data: import("@/types/hr").EmployeeUpdate): Promise<import("@/types/hr").Employee> => {
      return this.request({ method: "PUT", url: `/hr/employees/${id}`, data })
    },
    provisionEmployeeMail: async (
      id: string,
      data: import("@/types/hr").EmployeeMailProvisionRequest,
    ): Promise<import("@/types/hr").EmployeeMailProvisionResponse> => {
      return this.request({ method: "POST", url: `/hr/employees/${id}/mail/provision`, data })
    },
    deleteEmployee: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr/employees/${id}` })
    },

    // Time Tracking
    clockIn: async (data?: { notes?: string; isHomeoffice?: boolean }): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/time-entries/clock", data: { clockType: "in", ...data } })
    },
    clockOut: async (data?: { skipBreak1?: boolean; skipBreak2?: boolean }): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/time-entries/clock", data: { clockType: "out", ...data } })
    },
    recordHours: async (data: { date: string; hoursWorked: number; notes?: string; isHomeoffice?: boolean }): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/time-entries/hours", data })
    },
    recordTimeRange: async (data: { date: string; startTime: string; endTime: string; notes?: string; isHomeoffice?: boolean; skipBreak1?: boolean; skipBreak2?: boolean }): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/time-entries/range", data })
    },
    recordFullDay: async (data: { date: string; hours?: number; notes?: string; isHomeoffice?: boolean; skipBreak1?: boolean; skipBreak2?: boolean }): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/time-entries/full-day", data })
    },
    getTimeEntries: async (params?: Record<string, any>): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/time-entries", params })
      return res.items ?? res
    },

    // Projects
    getProjects: async (params?: Record<string, any>): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/projects", params })
      return res.items ?? res
    },
    getProject: async (id: string): Promise<any> => {
      return this.request({ method: "GET", url: `/hr/projects/${id}` })
    },
    createProject: async (data: any): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/projects", data })
    },
    updateProject: async (id: string, data: any): Promise<any> => {
      return this.request({ method: "PUT", url: `/hr/projects/${id}`, data })
    },
    deleteProject: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr/projects/${id}` })
    },

    // Project Time Entries
    getProjectTimeEntries: async (params?: Record<string, any>): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/project-time-entries", params })
      return res.items ?? res
    },
    getProjectDailyBreaks: async (params?: Record<string, any>): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/project-time-entries/breaks", params })
      return res.items ?? res
    },
    createProjectDailyBreak: async (data: any): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/project-time-entries/breaks", data })
    },
    updateProjectDailyBreak: async (id: string, data: any): Promise<any> => {
      return this.request({ method: "PUT", url: `/hr/project-time-entries/breaks/${id}`, data })
    },
    deleteProjectDailyBreak: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr/project-time-entries/breaks/${id}` })
    },
    getMyCalendarEvents: async (startIso: string, endIso: string): Promise<any[]> => {
      return this.request({ method: "GET", url: "/hr/project-time-entries/my-calendar-events", params: { start: startIso, end: endIso } })
    },
    getPlannedMeets: async (startIso: string, endIso: string): Promise<any[]> => {
      return this.request({ method: "GET", url: "/hr/project-time-entries/planned-meets", params: { start: startIso, end: endIso } })
    },
    getPendingMeets: async (): Promise<any[]> => {
      return this.request({ method: "GET", url: "/hr/project-time-entries/planned-meets/pending" })
    },
    markMeetDone: async (planId: string, data: any): Promise<any> => {
      return this.request({ method: "POST", url: `/hr/project-time-entries/planned-meets/${planId}/mark-done`, data })
    },
    cancelMeetPlan: async (planId: string): Promise<void> => {
      return this.request({ method: "POST", url: `/hr/project-time-entries/planned-meets/${planId}/cancel` })
    },
    rescheduleMeet: async (planId: string, startIso: string, endIso: string): Promise<any> => {
      return this.request({
        method: "POST",
        url: `/hr/project-time-entries/planned-meets/${planId}/reschedule`,
        data: { start: startIso, end: endIso },
      })
    },
    getMeetAttendees: async (projectId: string): Promise<{ customer: any[]; projectTeam: any[]; colleagues: any[] }> => {
      return this.request({ method: "GET", url: `/hr/projects/${projectId}/meet-attendees` })
    },
    createPlannedMeet: async (data: { title: string; projectId: string; start: string; end: string; attendees: string[]; description?: string; withMeetLink?: boolean }): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/project-time-entries/planned-meets/create", data })
    },
    createProjectTimeEntry: async (data: any): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/project-time-entries", data })
    },
    updateProjectTimeEntry: async (id: string, data: any): Promise<any> => {
      return this.request({ method: "PUT", url: `/hr/project-time-entries/${id}`, data })
    },
    deleteProjectTimeEntry: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr/project-time-entries/${id}` })
    },
    getProjectSummary: async (projectId: string): Promise<any> => {
      return this.request({ method: "GET", url: `/hr/project-time-entries/summary/${projectId}` })
    },

    // Easybill Project Sync
    syncProjectsFromEasybill: async (): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/projects/sync/easybill" })
    },
    syncTimeEntryToEasybill: async (entryId: string): Promise<any> => {
      return this.request({ method: "POST", url: `/hr/projects/sync/easybill/time-entry/${entryId}` })
    },
    syncProjectEntriesToEasybill: async (projectId: string): Promise<any> => {
      return this.request({ method: "POST", url: `/hr/projects/sync/easybill/project/${projectId}` })
    },
    checkBilledStatus: async (): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/projects/sync/easybill/check-billed" })
    },

    // Projektbaum
    getProjectTree: async (): Promise<import("@/types/hr").ProjectTreeNode[]> => {
      return this.request({ method: "GET", url: "/hr/projects/tree" })
    },

    // Projekt-Kontakte
    getProjectContacts: async (projectId: string): Promise<import("@/types/hr").ProjectContact[]> => {
      return this.request({ method: "GET", url: `/hr/projects/${projectId}/contacts` })
    },
    linkProjectContact: async (projectId: string, data: { contactId: string; role?: string }): Promise<import("@/types/hr").ProjectContact> => {
      return this.request({ method: "POST", url: `/hr/projects/${projectId}/contacts`, data })
    },
    unlinkProjectContact: async (projectId: string, contactId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr/projects/${projectId}/contacts/${contactId}` })
    },

    // Ninox (Server-Registry + Live-API, Konsolidierung N3)
    getProjectNinoxDatabases: async (projectId: string): Promise<any[]> => {
      return this.request({ method: "GET", url: `/hr/projects/${projectId}/ninox-databases` })
    },
    listProjectNinoxServers: async (organizationId?: string): Promise<any[]> => {
      return this.request({
        method: "GET",
        url: "/hr/projects/ninox-options/servers",
        params: organizationId ? { organizationId } : undefined,
      })
    },
    listProjectNinoxTeams: async (serverId: string): Promise<any[]> => {
      return this.request({
        method: "GET",
        url: `/hr/projects/ninox-options/servers/${serverId}/teams`,
      })
    },

    // Textbausteine
    getTextSnippets: async (projectId?: string): Promise<import("@/types/hr").TextSnippet[]> => {
      const params = projectId ? { projectId } : undefined
      const res: any = await this.request({ method: "GET", url: "/hr/text-snippets", params })
      return res.items ?? res
    },
    createTextSnippet: async (data: any): Promise<import("@/types/hr").TextSnippet> => {
      return this.request({ method: "POST", url: "/hr/text-snippets", data })
    },
    updateTextSnippet: async (id: string, data: any): Promise<import("@/types/hr").TextSnippet> => {
      return this.request({ method: "PUT", url: `/hr/text-snippets/${id}`, data })
    },
    deleteTextSnippet: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr/text-snippets/${id}` })
    },
    incrementSnippetUsage: async (id: string): Promise<void> => {
      return this.request({ method: "POST", url: `/hr/text-snippets/${id}/increment-usage` })
    },

    // Taetigkeiten
    getActivities: async (projectId?: string): Promise<import("@/types/hr").Activity[]> => {
      const params = projectId ? { projectId } : undefined
      const res: any = await this.request({ method: "GET", url: "/hr/activities", params })
      return res.items ?? res
    },
    createActivity: async (data: any): Promise<import("@/types/hr").Activity> => {
      return this.request({ method: "POST", url: "/hr/activities", data })
    },
    updateActivity: async (id: string, data: any): Promise<import("@/types/hr").Activity> => {
      return this.request({ method: "PUT", url: `/hr/activities/${id}`, data })
    },
    deleteActivity: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr/activities/${id}` })
    },

    // Leave / Vacation
    getLeaveAccount: async (employeeId: string, year?: number): Promise<import("@/types/hr").LeaveAccount> => {
      return this.request({ method: "GET", url: `/hr/leave-requests/balance/${employeeId}`, params: year ? { year } : undefined })
    },
    getLeaveRequests: async (params?: Record<string, any>): Promise<import("@/types/hr").LeaveRequest[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/leave-requests", params })
      return res.items ?? res
    },
    createLeaveRequest: async (data: import("@/types/hr").LeaveRequestCreate): Promise<import("@/types/hr").LeaveRequest> => {
      return this.request({ method: "POST", url: "/hr/leave-requests", data })
    },
    getPendingLeaveRequests: async (params?: { year?: number }): Promise<import("@/types/hr").LeaveRequest[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/leave-requests/pending", params })
      return res.items ?? res
    },
    approveLeaveRequest: async (id: string, data: { approved: boolean; comment?: string }): Promise<import("@/types/hr").LeaveRequest> => {
      return this.request({ method: "POST", url: `/hr/leave-requests/${id}/approve`, data })
    },
    getTeamOverview: async (year: number, month: number): Promise<any> => {
      return this.request({ method: "GET", url: `/hr/leave-requests/team-overview/${year}/${month}` })
    },
    updateLeaveRequest: async (id: string, data: any): Promise<import("@/types/hr").LeaveRequest> => {
      return this.request({ method: "PUT", url: `/hr/leave-requests/${id}`, data })
    },
    cancelLeaveRequest: async (id: string, data?: { reason?: string }): Promise<import("@/types/hr").LeaveRequest> => {
      return this.request({ method: "POST", url: `/hr/leave-requests/${id}/cancel`, data: data || {} })
    },
    getCalendarData: async (year: number, month: number, employeeId?: string): Promise<any> => {
      return this.request({ method: "GET", url: `/hr/leave-requests/calendar-data/${year}/${month}`, params: employeeId ? { employeeId } : undefined })
    },

    // Calendar
    getCalendars: async (year?: number): Promise<import("@/types/hr").CompanyCalendar[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/calendars", params: year ? { year } : undefined })
      return res.items ?? res
    },
    getCalendar: async (id: string): Promise<import("@/types/hr").CompanyCalendar> => {
      return this.request({ method: "GET", url: `/hr/calendars/${id}` })
    },
    createCalendar: async (data: { year: number; federalState: string; name: string; isDefault?: boolean }): Promise<import("@/types/hr").CompanyCalendar> => {
      return this.request({ method: "POST", url: "/hr/calendars", data })
    },
    generateHolidays: async (data: import("@/types/hr").GenerateHolidaysRequest): Promise<import("@/types/hr").CompanyCalendar> => {
      return this.request({ method: "POST", url: "/hr/calendars/generate-holidays", data })
    },
    addCalendarEntry: async (calendarId: string, data: import("@/types/hr").CalendarEntryCreate): Promise<import("@/types/hr").CalendarEntry> => {
      return this.request({ method: "POST", url: `/hr/calendars/${calendarId}/entries`, data })
    },
    addClosureRange: async (calendarId: string, data: { startDate: string; endDate: string; name: string; isHalfDay?: boolean; notes?: string }): Promise<import("@/types/hr").CalendarEntry[]> => {
      return this.request({ method: "POST", url: `/hr/calendars/${calendarId}/closure-range`, data })
    },
    deleteCalendar: async (calendarId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr/calendars/${calendarId}` })
    },
    deleteCalendarEntry: async (calendarId: string, entryId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr/calendars/${calendarId}/entries/${entryId}` })
    },
    previewSmartClosure: async (data: { startDate: string; endDate: string; name?: string }): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/calendars/smart-closure/preview", data })
    },
    executeSmartClosure: async (data: { days: any[]; notes?: string; affectsDepartmentId?: string | null }): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/calendars/smart-closure/execute", data })
    },
    getBridgeDays: async (year: number): Promise<any[]> => {
      return this.request({ method: "GET", url: `/hr/calendars/bridge-days/${year}` })
    },
    syncClosures: async (year: number): Promise<any> => {
      return this.request({ method: "POST", url: `/hr/calendars/sync-closures/${year}` })
    },

    // Users (für Mitarbeiter-Zuordnung)
    getUsers: async (): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/users" })
      return res.items ?? res
    },

    // Sick Leave
    getSickLeaves: async (params?: Record<string, any>): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/sick-leaves", params })
      return res.items ?? res
    },
    createSickLeave: async (data: any): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/sick-leaves", data })
    },
    updateSickLeave: async (id: string, data: any): Promise<any> => {
      return this.request({ method: "PUT", url: `/hr/sick-leaves/${id}`, data })
    },

    // Time Accounts
    getTimeAccountBalance: async (employeeId?: string): Promise<any> => {
      if (employeeId) {
        return this.request({ method: "GET", url: `/hr/time-accounts/balance/${employeeId}` })
      }
      return this.request({ method: "GET", url: "/hr/time-accounts/balance" })
    },
    getTimeAccountEntries: async (params: { startDate: string; endDate: string; employeeId?: string }): Promise<any[]> => {
      const query = new URLSearchParams({ start_date: params.startDate, end_date: params.endDate })
      if (params.employeeId) query.set("employee_id", params.employeeId)
      return this.request({ method: "GET", url: `/hr/time-accounts/entries?${query}` })
    },

    getDailyBreakdown: async (params: { startDate: string; endDate: string; employeeId?: string }): Promise<any> => {
      const query = new URLSearchParams({ start_date: params.startDate, end_date: params.endDate })
      if (params.employeeId) query.set("employee_id", params.employeeId)
      return this.request({ method: "GET", url: `/hr/time-accounts/daily-breakdown?${query}` })
    },

    // Journal
    getMyJournal: async (page: number = 1, pageSize: number = 20): Promise<any> => {
      return this.request({ method: "GET", url: "/hr/journal/my", params: { page, page_size: pageSize } })
    },
    getJournal: async (params?: { page?: number; pageSize?: number; employeeId?: string; eventType?: string; year?: number; month?: number }): Promise<any> => {
      const query: any = { page: params?.page || 1, page_size: params?.pageSize || 20 }
      if (params?.employeeId) query.employee_id = params.employeeId
      if (params?.eventType) query.event_type = params.eventType
      if (params?.year) query.year = params.year
      if (params?.month) query.month = params.month
      return this.request({ method: "GET", url: "/hr/journal", params: query })
    },

    // Travel Expenses (Reisekosten)
    getMyTravelExpenses: async (page: number = 1, pageSize: number = 20): Promise<any> => {
      return this.request({ method: "GET", url: "/hr/travel-expenses/my", params: { page, page_size: pageSize } })
    },
    getTravelExpenses: async (params?: { page?: number; pageSize?: number; employeeId?: string; status?: string }): Promise<any> => {
      const query: any = { page: params?.page || 1, page_size: params?.pageSize || 20 }
      if (params?.employeeId) query.employeeId = params.employeeId
      if (params?.status) query.status = params.status
      return this.request({ method: "GET", url: "/hr/travel-expenses", params: query })
    },
    getTravelExpense: async (id: string): Promise<any> => {
      return this.request({ method: "GET", url: `/hr/travel-expenses/${id}` })
    },
    createTravelExpense: async (data: any): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/travel-expenses", data })
    },
    updateTravelExpense: async (id: string, data: any): Promise<any> => {
      return this.request({ method: "PUT", url: `/hr/travel-expenses/${id}`, data })
    },
    submitTravelExpense: async (id: string): Promise<any> => {
      return this.request({ method: "POST", url: `/hr/travel-expenses/${id}/submit` })
    },
    approveTravelExpense: async (id: string): Promise<any> => {
      return this.request({ method: "POST", url: `/hr/travel-expenses/${id}/approve` })
    },
    rejectTravelExpense: async (id: string, reason: string): Promise<any> => {
      return this.request({ method: "POST", url: `/hr/travel-expenses/${id}/reject`, params: { reason } })
    },
    markTravelExpensePaid: async (id: string): Promise<any> => {
      return this.request({ method: "POST", url: `/hr/travel-expenses/${id}/paid` })
    },
    uploadTravelReceipt: async (expenseId: string, itemId: string, file: File): Promise<any> => {
      const formData = new FormData()
      formData.append("file", file)
      return this.request({ method: "POST", url: `/hr/travel-expenses/${expenseId}/items/${itemId}/receipt`, data: formData, headers: { "Content-Type": "multipart/form-data" } })
    },
    getTravelReceiptUrl: async (expenseId: string, itemId: string): Promise<any> => {
      return this.request({ method: "GET", url: `/hr/travel-expenses/${expenseId}/items/${itemId}/receipt` })
    },
    getTravelExpenseSettings: async (): Promise<any> => {
      return this.request({ method: "GET", url: "/hr/travel-expense-settings" })
    },
    updateTravelExpenseSettings: async (data: any): Promise<any> => {
      return this.request({ method: "PUT", url: "/hr/travel-expense-settings", data })
    },

    // Payout Requests
    createPayoutRequest: async (data: { hours: number; reason?: string }): Promise<import("@/types/hr").PayoutRequest> => {
      return this.request({ method: "POST", url: "/hr/payout-requests", data })
    },
    getPayoutRequests: async (): Promise<import("@/types/hr").PayoutRequest[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/payout-requests" })
      return res.items ?? res
    },
    getPendingPayoutRequests: async (): Promise<import("@/types/hr").PayoutRequest[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/payout-requests/pending" })
      return res.items ?? res
    },
    approvePayoutRequest: async (id: string, data: { approved: boolean; payoutAmountEur?: number; comment?: string }): Promise<import("@/types/hr").PayoutRequest> => {
      return this.request({ method: "POST", url: `/hr/payout-requests/${id}/approve`, data })
    },
    markPayoutPaid: async (id: string, data?: { payoutAmountEur?: number }): Promise<import("@/types/hr").PayoutRequest> => {
      return this.request({ method: "POST", url: `/hr/payout-requests/${id}/paid`, data: data || {} })
    },

    // Work Time Models
    getWorkTimeModels: async (): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/hr/work-time-models" })
      return res.items ?? res
    },
    createWorkTimeModel: async (data: any): Promise<any> => {
      return this.request({ method: "POST", url: "/hr/work-time-models", data })
    },
    updateWorkTimeModel: async (id: string, data: any): Promise<any> => {
      return this.request({ method: "PUT", url: `/hr/work-time-models/${id}`, data })
    },
  }

  // Aufgaben-Modul (Forgejo-Issue-Sync), Konzept: docs/FORGEJO-AUFGABEN-KONZEPT.md
  taskBoards = {
    getBoards: async (includeArchived = false): Promise<TaskBoardItem[]> => {
      return this.request({
        method: "GET",
        url: "/task-boards",
        params: { include_archived: includeArchived },
      })
    },
    createBoard: async (data: {
      name: string
      description?: string | null
      color?: string
      organizationId?: string | null
    }): Promise<TaskBoardItem> => {
      return this.request({ method: "POST", url: "/task-boards", data })
    },
    updateBoard: async (
      boardId: string,
      data: Partial<Pick<TaskBoardItem, "name" | "description" | "color" | "organizationId" | "defaultColumnId" | "doneColumnId" | "isArchived">>
    ): Promise<TaskBoardItem> => {
      return this.request({ method: "PATCH", url: `/task-boards/${boardId}`, data })
    },
    getKanban: async (boardId: string): Promise<TaskKanban> => {
      return this.request({ method: "GET", url: `/task-boards/${boardId}/kanban` })
    },
    createColumn: async (
      boardId: string,
      data: { name: string; color?: string | null; position?: number | null; isDoneColumn?: boolean; wipLimit?: number | null }
    ): Promise<TaskBoardColumn> => {
      return this.request({ method: "POST", url: `/task-boards/${boardId}/columns`, data })
    },
    updateColumn: async (
      boardId: string,
      columnId: string,
      data: { name?: string; color?: string | null; position?: number | null; isDoneColumn?: boolean; wipLimit?: number | null }
    ): Promise<TaskBoardColumn> => {
      return this.request({ method: "PATCH", url: `/task-boards/${boardId}/columns/${columnId}`, data })
    },
    deleteColumn: async (boardId: string, columnId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/task-boards/${boardId}/columns/${columnId}` })
    },
    getTask: async (taskId: string): Promise<TaskBoardDetail> => {
      return this.request({ method: "GET", url: `/task-boards/tasks/${taskId}` })
    },
    refreshTaskComments: async (taskId: string): Promise<TaskBoardComment[]> => {
      return this.request({ method: "POST", url: `/task-boards/tasks/${taskId}/refresh-comments` })
    },
    createTask: async (boardId: string, data: TaskCreatePayload): Promise<TaskBoardCard> => {
      return this.request({ method: "POST", url: `/task-boards/${boardId}/tasks`, data })
    },
    updateTask: async (taskId: string, data: TaskUpdatePayload): Promise<TaskBoardCard> => {
      return this.request({ method: "PATCH", url: `/task-boards/tasks/${taskId}`, data })
    },
    deleteTask: async (taskId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/task-boards/tasks/${taskId}` })
    },
    reorderTasks: async (
      boardId: string,
      data: { columnId: string; taskIds: string[] }
    ): Promise<{ success: boolean }> => {
      return this.request({ method: "POST", url: `/task-boards/${boardId}/reorder`, data })
    },
    // Uebernahme-Fluesse (Schritt 3): Quelle -> Aufgabe
    takeoverFromSummary: async (summaryId: string, data: TaskTakeoverPayload): Promise<TaskTakeoverResult> => {
      return this.request({ method: "POST", url: `/email-summaries/${summaryId}/task`, data })
    },
    takeoverFromTicket: async (ticketId: string, data: TaskTakeoverPayload): Promise<TaskTakeoverResult> => {
      return this.request({ method: "POST", url: `/tickets/${ticketId}/task`, data })
    },
    takeoverFromMeldung: async (
      data: TaskTakeoverPayload & { instanzSlug: string; meldungId: number; detailUrl?: string | null }
    ): Promise<TaskTakeoverResult> => {
      return this.request({ method: "POST", url: "/tester/meldungen/task", data })
    },
    getTicketTasks: async (ticketId: string): Promise<LinkedTask[]> => {
      return this.request({ method: "GET", url: `/tickets/${ticketId}/tasks` })
    },
    createProspect: async (
      ticketId: string,
      data: { organizationName: string; firstName: string; lastName: string; email?: string | null }
    ): Promise<{ organizationId: string; organizationName: string; contactId: string; organizationExisted: boolean }> => {
      return this.request({ method: "POST", url: `/tickets/${ticketId}/prospect`, data })
    },
    // Forgejo-Admin
    forgejoTestConnection: async (): Promise<ForgejoConnectionTest> => {
      return this.request({ method: "POST", url: "/forgejo/test-connection" })
    },
    forgejoFullSync: async (): Promise<{ status: string }> => {
      return this.request({ method: "POST", url: "/forgejo/sync" })
    },
  }

  // E-Mail-Verlauf je Kontakt/Organisation (Sichtbarkeit pro E-Mail)
  emailActivities = {
    forOrganization: async (
      organizationId: string,
      params?: { skip?: number; limit?: number }
    ): Promise<PaginatedEmailActivities> => {
      return this.request({ method: "GET", url: `/organizations/${organizationId}/emails`, params })
    },
    forContact: async (
      contactId: string,
      params?: { skip?: number; limit?: number }
    ): Promise<PaginatedEmailActivities> => {
      return this.request({ method: "GET", url: `/contacts/${contactId}/emails`, params })
    },
    setVisibility: async (
      activityId: string,
      visibility: "team" | "private"
    ): Promise<EmailActivityItem> => {
      return this.request({ method: "PATCH", url: `/email-activities/${activityId}`, data: { visibility } })
    },
    deleteActivity: async (activityId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/email-activities/${activityId}` })
    },
  }

  tickets = {
    // List tickets (paginated, filterable)
    getTickets: async (params?: Record<string, any>): Promise<TicketListItem[]> => {
      const res: any = await this.request({ method: "GET", url: "/tickets", params })
      return res.items ?? res
    },
    // Get single ticket with messages
    getTicket: async (id: string): Promise<Ticket> => {
      return this.request({ method: "GET", url: `/tickets/${id}` })
    },
    // Create ticket
    createTicket: async (data: TicketCreate): Promise<Ticket> => {
      return this.request({ method: "POST", url: "/tickets", data })
    },
    // Aktive Ninox-Server aus der Registry (N3, ersetzt /ninox/servers hier)
    listNinoxContextServers: async (organizationId?: string): Promise<Array<{
      id: string
      name: string
      hostUrl: string
      organizationId: string
      isActive: boolean
    }>> => {
      return this.request({
        method: "GET",
        url: "/tickets/ninox-context/servers",
        params: organizationId ? { organizationId } : undefined,
      })
    },
    resolveNinoxContext: async (data: { organizationId: string; ninoxUrl: string }): Promise<{
      organizationId: string
      organizationName: string
      serverId: string | null
      serverName: string | null
      serverUrl: string | null
      teamId: string | null
      teamName: string | null
      ninoxTeamId: string | null
      matchedDatabaseId: string | null
      matchedDatabaseName: string | null
      databases: Array<{
        id: string
        name: string
        ninoxDatabaseId: string
        quickLink: string | null
        workspaceId: string
        workspaceName: string
        tables: Array<{
          id: string
          caption: string
          ninoxTableId: string
          kind: string
          fieldsCount: number
          isHidden: boolean
        }>
      }>
    }> => {
      return this.request({ method: "POST", url: "/tickets/ninox-context/resolve", data })
    },
    // Update ticket
    updateTicket: async (id: string, data: TicketUpdate): Promise<Ticket> => {
      return this.request({ method: "PUT", url: `/tickets/${id}`, data })
    },
    // Delete ticket (soft)
    deleteTicket: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/tickets/${id}` })
    },
    // Assign ticket
    assignTicket: async (id: string, assignedTo: string | null): Promise<Ticket> => {
      return this.request({ method: "POST", url: `/tickets/${id}/assign`, data: { assignedTo } })
    },
    // Change responsible creator
    updateTicketCreator: async (id: string, createdBy: string): Promise<Ticket> => {
      return this.request({ method: "POST", url: `/tickets/${id}/creator`, data: { createdBy } })
    },
    getAssignees: async (): Promise<Array<{
      id: string
      email: string
      firstName?: string | null
      lastName?: string | null
      fullName: string
    }>> => {
      return this.request({ method: "GET", url: "/tickets/assignees" })
    },
    // Change status
    updateTicketStatus: async (id: string, status: string): Promise<Ticket> => {
      return this.request({ method: "POST", url: `/tickets/${id}/status`, data: { status } })
    },
    // Get messages
    getMessages: async (ticketId: string): Promise<TicketMessage[]> => {
      return this.request({ method: "GET", url: `/tickets/${ticketId}/messages` })
    },
    // Send message
    sendMessage: async (ticketId: string, data: TicketMessageCreate): Promise<TicketMessage> => {
      return this.request({ method: "POST", url: `/tickets/${ticketId}/messages`, data })
    },
    // Ticket protocol
    getLogs: async (ticketId: string): Promise<TicketLogEntry[]> => {
      return this.request({ method: "GET", url: `/tickets/${ticketId}/logs` })
    },
    // Mark message as read
    markRead: async (ticketId: string, messageId: string): Promise<void> => {
      return this.request({ method: "POST", url: `/tickets/${ticketId}/messages/${messageId}/read` })
    },
    // Kanban board data
    getKanban: async (): Promise<KanbanData> => {
      return this.request({ method: "GET", url: "/tickets/kanban" })
    },
    // Manuelle Reihenfolge (und Zielspalte) einer Kanban-Spalte setzen
    reorderBoard: async (status: string, ticketIds: string[]): Promise<{ success: boolean }> => {
      return this.request({
        method: "POST",
        url: "/tickets/board/reorder",
        data: { status, ticketIds },
      })
    },
    // Unread count
    getUnreadCount: async (): Promise<UnreadCount> => {
      return this.request({ method: "GET", url: "/tickets/unread-count" })
    },
    // Upload attachment (returns s3Url for embedding in document)
    uploadAttachment: async (ticketId: string, formData: FormData): Promise<any> => {
      return this.request({
        method: "POST",
        url: `/tickets/${ticketId}/attachments`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      })
    },
    // Slash command resolver (returns rendered HTML for ticket-specific commands)
    resolveSlashCommand: async (ticketId: string, command: string, params?: Record<string, any>): Promise<{ html: string }> => {
      return this.request({ method: "POST", url: `/tickets/${ticketId}/slash-command`, data: { command, params: params || {} } })
    },
    // Get available document templates
    getTemplates: async (): Promise<any[]> => {
      return this.request({ method: "GET", url: "/tickets/templates" })
    },

    // Send document content as email
    sendDocumentEmail: async (ticketId: string, data: {
      htmlContent: string
      subject?: string
      to?: string[]
      cc?: string[]
      bcc?: string[]
      attachPdf?: boolean
      individual?: boolean
      recipientEmail?: string
    }): Promise<{ success: boolean; message: string; sentCount?: number }> => {
      return this.request({ method: "POST", url: `/tickets/${ticketId}/send-email`, data })
    },
    getTicketEmailRecipients: async (ticketId: string): Promise<{
      recipients: Array<{ email: string; name?: string | null; source: string }>
    }> => {
      return this.request({ method: "GET", url: `/tickets/${ticketId}/email-recipients` })
    },

    // Version history
    getVersions: async (ticketId: string): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: `/tickets/${ticketId}/versions` })
      return res.items ?? res
    },
    createVersion: async (ticketId: string, description?: string): Promise<any> => {
      return this.request({ method: "POST", url: `/tickets/${ticketId}/versions`, data: { description } })
    },
    getVersion: async (ticketId: string, versionId: string): Promise<any> => {
      return this.request({ method: "GET", url: `/tickets/${ticketId}/versions/${versionId}` })
    },
    restoreVersion: async (ticketId: string, versionId: string): Promise<any> => {
      return this.request({ method: "POST", url: `/tickets/${ticketId}/versions/${versionId}/restore` })
    },
    // Poll for pending content (e-mail injections into the collaborative document)
    getPendingContent: async (ticketId: string): Promise<{ pendingContent: string | null }> => {
      return this.request({ method: "GET", url: `/tickets/${ticketId}/pending-content` })
    },

    // Time entries
    getTimeEntries: async (ticketId: string, date: string): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: `/tickets/${ticketId}/time-entries`, params: { date } })
      return res.items ?? res
    },
    createTimeEntry: async (ticketId: string, data: any): Promise<any> => {
      return this.request({ method: "POST", url: `/tickets/${ticketId}/time-entries`, data })
    },
    updateTimeEntry: async (ticketId: string, entryId: string, data: any): Promise<any> => {
      return this.request({ method: "PUT", url: `/tickets/${ticketId}/time-entries/${entryId}`, data })
    },
    deleteTimeEntry: async (ticketId: string, entryId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/tickets/${ticketId}/time-entries/${entryId}` })
    },
    getDayTimeEntries: async (date: string): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: "/tickets/time-overview/day", params: { date } })
      return res.items ?? res
    },
    getTimeEntrySummary: async (ticketId: string): Promise<any[]> => {
      const res: any = await this.request({ method: "GET", url: `/tickets/${ticketId}/time-entries/summary` })
      return res.items ?? res
    },
    // Document changes check (unseen changes since last visit)
    checkDocumentChanges: async (ticketId: string): Promise<any> => {
      return this.request({ method: "GET", url: `/tickets/${ticketId}/document-changes` })
    },
    // Update ticket group
    updateTicketGroup: async (ticketId: string, ticketGroupId: string | null): Promise<any> => {
      return this.request({ method: "PUT", url: `/tickets/${ticketId}/group`, data: { ticketGroupId } })
    },
  }

  // =========================================================================
  // USER GROUPS (Admin only)
  // =========================================================================

  userGroups = {
    list: async (): Promise<{ items: any[]; total: number }> => {
      return this.request({ method: "GET", url: "/user-groups" })
    },
    get: async (id: string): Promise<any> => {
      return this.request({ method: "GET", url: `/user-groups/${id}` })
    },
    create: async (data: { name: string; description?: string; color?: string }): Promise<any> => {
      return this.request({ method: "POST", url: "/user-groups", data })
    },
    update: async (id: string, data: { name?: string; description?: string; color?: string }): Promise<any> => {
      return this.request({ method: "PUT", url: `/user-groups/${id}`, data })
    },
    delete: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/user-groups/${id}` })
    },
    setMembers: async (id: string, userIds: string[]): Promise<any> => {
      return this.request({ method: "PUT", url: `/user-groups/${id}/members`, data: { userIds } })
    },
  }

  // =========================================================================
  // TICKET GROUPS (Admin only)
  // =========================================================================

  ticketGroups = {
    list: async (): Promise<{ items: any[]; total: number }> => {
      return this.request({ method: "GET", url: "/ticket-groups" })
    },
    get: async (id: string): Promise<any> => {
      return this.request({ method: "GET", url: `/ticket-groups/${id}` })
    },
    create: async (data: { name: string; description?: string; color?: string }): Promise<any> => {
      return this.request({ method: "POST", url: "/ticket-groups", data })
    },
    update: async (id: string, data: { name?: string; description?: string; color?: string }): Promise<any> => {
      return this.request({ method: "PUT", url: `/ticket-groups/${id}`, data })
    },
    delete: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/ticket-groups/${id}` })
    },
    assignUserGroups: async (id: string, userGroupIds: string[]): Promise<any> => {
      return this.request({ method: "PUT", url: `/ticket-groups/${id}/user-groups`, data: { userGroupIds } })
    },
  }

  // =========================================================================
  // MATTERMOST INTEGRATION
  // =========================================================================

  mattermost = {
    getStatus: async (): Promise<{
      connected: boolean
      status?: string
      error?: string
      teamsCount: number
      botsCount: number
      teams: Array<{ id: string; name: string; displayName: string }>
    }> => {
      return this.request({ method: "GET", url: "/mattermost/status" })
    },

    setup: async (): Promise<{
      teamId: string
      teamName: string
      message: string
    }> => {
      return this.request({ method: "POST", url: "/mattermost/setup" })
    },

    provisionUser: async (userId: string): Promise<{
      mattermostUserId: string
      username?: string
      alreadyExists: boolean
    }> => {
      return this.request({
        method: "POST",
        url: "/mattermost/users/provision",
        params: { user_id: userId },
      })
    },

    syncPassword: async (password: string): Promise<any> => {
      return this.request({ method: "POST", url: "/mattermost/sync-password", data: { password } })
    },

    provisionAllUsers: async (): Promise<{
      total: number
      created: number
      existing: number
      errors: number
      message: string
    }> => {
      return this.request({ method: "POST", url: "/mattermost/users/provision-all" })
    },

    provisionContact: async (contactId: string): Promise<{
      mattermostUserId: string
      alreadyExists: boolean
    }> => {
      return this.request({
        method: "POST",
        url: `/mattermost/contacts/${contactId}/provision`,
      })
    },

    createOrgChannel: async (organizationId: string): Promise<{
      channelId: string
      channelName?: string
      message: string
    }> => {
      return this.request({
        method: "POST",
        url: `/mattermost/organizations/${organizationId}/channel`,
      })
    },

    createTicketChannel: async (ticketId: string): Promise<{
      channelId: string
      channelName?: string
      message: string
    }> => {
      return this.request({
        method: "POST",
        url: `/mattermost/tickets/${ticketId}/channel`,
      })
    },

    sync: async (): Promise<{
      total: number
      created: number
      existing: number
      errors: number
      message: string
    }> => {
      return this.request({ method: "POST", url: "/mattermost/sync" })
    },

    syncChannelMembers: async (organizationId: string): Promise<{
      added: number
      alreadyMember: number
      errors: number
    }> => {
      return this.request({
        method: "POST",
        url: `/mattermost/organizations/${organizationId}/sync-members`,
      })
    },

    deactivateContact: async (contactId: string): Promise<{
      deactivatedMattermostUserId: string
      message: string
    }> => {
      return this.request({
        method: "POST",
        url: `/mattermost/contacts/${contactId}/deactivate`,
      })
    },

    deactivateUser: async (userId: string): Promise<{
      deactivatedMattermostUserId: string
      message: string
    }> => {
      return this.request({
        method: "POST",
        url: `/mattermost/users/${userId}/deactivate`,
      })
    },
  }
}

// =========================================================================
// OAuth2 Types
// =========================================================================

export interface OAuthConnectionStatus {
  provider: string
  isConnected: boolean
  isConfigured: boolean
  accountEmail: string | null
  scopes: string[]
}

export interface OAuthAuthorizationResponse {
  authorizationUrl: string
}

export interface OAuthRevokeResponse {
  success: boolean
  message: string
}

export interface OAuthConnectedAccount {
  provider: string
  accountEmail: string | null
  scopes: string[]
  connectedAt: string
  lastUsed: string | null
}

// =========================================================================
// Provider Settings Types
// =========================================================================

export interface OutgoingInvoiceItem {
  id: string
  easybillId: number
  invoiceNumber: string | null
  customerName: string | null
  organizationId: string | null
  invoiceDate: string | null
  dueDate: string | null
  grossAmount: number | null
  paidAmount: number | null
  currency: string
  status: string | null
  isPaid: boolean
}

export interface OutgoingInvoiceDashboard {
  openTotal: number
  openCount: number
  overdueTotal: number
  overdueCount: number
  totalCount: number
  revenueCurrentMonth: number
  revenueCurrentYear: number
}

export interface OutgoingInvoiceDetail {
  id: string
  easybillId: number
  invoiceNumber: string | null
  customerName: string | null
  organizationId: string | null
  easybillCustomerId: number | null
  invoiceDate: string | null
  dueDate: string | null
  documentType: string | null
  grossAmount: number | null
  netAmount: number | null
  paidAmount: number | null
  currency: string
  status: string | null
  isPaid: boolean
  isDraft: boolean
  isArchive: boolean
  cancelId: number | null
  lastSyncedAt: string | null
  easybillRaw: Record<string, any> | null
  easybillUrl: string | null
}

export interface EmailSummaryAttachment {
  filename: string
  url: string
  mimeType: string | null
  size: number | null
  googleFileId: string | null
  messageDate: string | null
}

export interface EmailSummaryMeetingLink {
  provider: string
  url: string
  label: string | null
  context: string | null
  messageSender: string | null
  messageDate: string | null
  messageSubject: string | null
}

export interface EmailSummary {
  id: string
  threadId: string
  sender: string | null
  senderName: string | null
  senderEmail: string | null
  recipients: string[]
  subject: string | null
  emailDate: string | null
  emailLink: string | null
  summary: string
  priority: string | null
  actionItems: string[]
  attachments: EmailSummaryAttachment[]
  meetingLinks: EmailSummaryMeetingLink[]
  status: "open" | "done" | "ticket" | string
  isRead: boolean
  isHidden: boolean
  hiddenAt: string | null
  linkedTicketId: string | null
  linkedTicketNumber: string | null
  linkedTaskId: string | null
  linkedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface EmailSummaryRunStatus {
  lastCheckedAt: string | null
  lastDeliveredAt: string | null
  searchQuery: string | null
  threadsFound: number
  processed: number
  skipped: number
  failed: number
  openCount: number
  unreadCount: number
}

export interface EmailSummaryReplyDraft {
  to: string
  cc: string | null
  subject: string
  bodyText: string
  provider: string | null
  model: string | null
}

export interface EmailSummaryReplySendResult {
  success: boolean
  gmailMessageId: string | null
  threadId: string | null
  summary: EmailSummary
}

export interface ModuleStatus {
  slug: string
  label: string
  isEnabled: boolean
  canDisable: boolean
  category: string
}

export interface ProviderSettings {
  category: string
  provider: string
  name: string
  isActive: boolean
  isConfigured: boolean
  priority: number
  settings: Record<string, {
    value: string
    isEncrypted: boolean
    isConfigured: boolean
  }>
}

export interface ProviderSettingsUpdate {
  settings?: Record<string, string>
  isActive?: boolean
  priority?: number
}

// --- Fabrik-OS-Tester-Integration ---
export interface TesterInstanz {
  slug: string
  displayName: string
  baseUrl: string
  isActive: boolean
  tokenConfigured: boolean
  forgejoRepo: string
  organizationId: string
}

export interface TesterInstanzCreate {
  slug: string
  displayName: string
  baseUrl: string
  serviceToken: string
  isActive: boolean
  forgejoRepo?: string
  organizationId?: string
}

export interface TesterInstanzUpdate {
  displayName: string
  baseUrl: string
  serviceToken?: string
  isActive: boolean
  forgejoRepo?: string
  organizationId?: string
}

export interface TesterTestResult {
  ok: boolean
  version?: string | null
  gesamt?: number | null
  error?: string | null
}

export interface TesterMeldung {
  id: number
  titel: string
  status: string
  typ: string | null
  prioritaet: string
  applicationName: string
  forgejoIssueNr: number | null
  forgejoIssueUrl: string | null
  createdAt: string
  updatedAt: string
  instanzSlug: string
  instanzName: string
  detailUrl: string
  instanzForgejoRepo?: string | null
}

export interface TesterInstanzStatus {
  slug: string
  name: string
  ok: boolean
  error?: string | null
  statistik?: {
    version: string
    gesamt: number
    jeStatus: Record<string, number>
    jeTyp: Record<string, number>
    jePrioritaet: Record<string, number>
    jeAnwendung: { applicationId: number; applicationName: string; anzahl: number }[]
    zeitreihe: { datum: string; anzahl: number }[]
    durchlaufzeit: { anzahl: number; schnittTage?: number | null; medianTage?: number | null }
  }
}

export interface TesterDashboard {
  instanzen: TesterInstanzStatus[]
  gesamt: {
    gesamt: number
    jeStatus: Record<string, number>
    jeTyp: Record<string, number>
    jePrioritaet: Record<string, number>
    instanzenOk: number
    instanzenGesamt: number
  }
}

export interface TesterMeldungenPage {
  data: TesterMeldung[]
  total: number
  instanzen: { slug: string; name: string; ok: boolean; error?: string | null }[]
}

export interface InvoiceGmailPollingSettings {
  enabled: boolean
  mailbox: string
  label: string
  queryExtra: string
  maxResults: number
  markRead: boolean
  pollIntervalSeconds: number
  effectiveQuery: string
}

export type InvoiceGmailPollingSettingsUpdate = Omit<
  InvoiceGmailPollingSettings,
  "effectiveQuery"
>

export interface InvoiceGmailPollLastResult {
  status?: string | null
  reason?: string | null
  phase?: string | null
  mailbox?: string | null
  label?: string | null
  labelId?: string | null
  query?: string | null
  actualQuery?: string | null
  actualLabelIds: string[]
  effectiveQuery?: string | null
  gmailSearchAttempts: Array<{
    mode: string
    query: string
    labelIds: string[]
    messages: number
  }>
  messages: number
  processedMessages: number
  invoicesQueued: number
  duplicates: number
  skipped: number
  errors: number
  markedRead: number
  labelRemoved: number
  pollIntervalSeconds?: number | null
  secondsUntilNext?: number | null
  taskId?: string | null
  manual: boolean
  startedAt?: string | null
  finishedAt?: string | null
}

export interface InvoiceGmailPollStatus {
  enabled: boolean
  mailbox: string
  label: string
  effectiveQuery: string
  pollIntervalSeconds: number
  secondsUntilNext?: number | null
  nextRunAt?: string | null
  lastResult?: InvoiceGmailPollLastResult | null
}

export interface InvoiceGmailLabelItem {
  id: string
  name: string
  type?: string | null
  messageListVisibility?: string | null
  labelListVisibility?: string | null
  messagesTotal?: number | null
  messagesUnread?: number | null
  threadsTotal?: number | null
  threadsUnread?: number | null
}

export interface InvoiceGmailLabelsResponse {
  mailbox: string
  labels: InvoiceGmailLabelItem[]
}

export interface InvoiceGmailPollTriggerResponse {
  taskId: string
  status: string
  message: string
}

export interface ModelOption {
  value: string
  label: string
}

export interface EngineOption {
  value: string
  label: string
  models?: ModelOption[]
}

export interface InvoiceOcrSettings {
  extractionPrompt: string
  extractionEngine: string
  extractionModel?: string
  availableEngines?: EngineOption[]
}

export interface InvoiceOcrSettingsUpdate {
  extractionPrompt: string
  extractionEngine?: string
  extractionModel?: string
}

export interface OcrPromptProfile {
  id: string
  name: string
  engine: string
  model: string
  prompt: string
  isActive: boolean
}

export interface OcrPromptProfilesResponse {
  profiles: OcrPromptProfile[]
  availableEngines: EngineOption[]
  activeEngine: string
}

export interface OcrPromptProfileCreate {
  name: string
  engine: string
  model?: string
  prompt: string
  isActive?: boolean
}

export interface OcrPromptProfileUpdate {
  name?: string
  engine?: string
  model?: string
  prompt?: string
}

export interface OcrTestResult {
  engine: string
  model: string
  structured: boolean
  message?: string | null
  confidence?: number | null
  mappedFields: Record<string, unknown>
  rawJson: Record<string, unknown>
}

export interface OcrCatalogModel {
  id: string
  label: string
}

export interface OcrCatalogEngine {
  key: string
  label: string
  kind: string
  enabled: boolean
  defaultModel: string
  models: OcrCatalogModel[]
}

export interface OcrEngineCatalog {
  defaultEngine: string
  engines: OcrCatalogEngine[]
  availableKinds?: ModelOption[]
}

export interface BrandingColors {
  primary: string
  accent: string
  background: string
  foreground: string
  sidebarBackground: string
  sidebarForeground: string
  cardBackground: string
}

export interface BrandingLogo {
  key: string
  label: string
  description: string
  storageKey: string | null
  url: string | null
  defaultUrl: string | null
  isDisabled: boolean
}

export interface BrandingSyncTarget {
  key: string
  label: string
  status: string
  message: string
  supportsAutomaticSync: boolean
}

export interface BrandingConfig {
  productName: string
  organizationName: string
  slogan: string
  colors: BrandingColors
  logos: Record<string, BrandingLogo>
  syncTargets: BrandingSyncTarget[]
}

export interface BrandingUpdate {
  productName?: string
  organizationName?: string
  slogan?: string
  colors?: BrandingColors
}

export interface BrandingSyncResponse {
  targets: BrandingSyncTarget[]
}

// =========================================================================
// Easybill Types
// =========================================================================

export interface EasybillSyncResponse {
  success: boolean
  message: string
  syncLogId?: string
  entityId?: string
  easybillId?: number
  status?: string
}

export interface EasybillInvoiceSyncResponse {
  created: number
  updated: number
  total: number
  pages: number
}

export interface EasybillCustomerSearchMatch {
  matchType: "name" | "email_domain" | "both"
  nameSimilarity: number
  emailDomainMatch: boolean
  matchedEmails: string[]
}

export interface EasybillCustomerSearchResult {
  id: number
  number?: string
  companyName?: string
  firstName?: string
  lastName?: string
  city?: string
  emails: string[]
  similarity: number
  isArchived?: boolean
  matchDetails?: EasybillCustomerSearchMatch
}

export interface EasybillUnlinkedOrganization {
  id: string
  name: string
  website?: string
}

export interface EasybillUnlinkedCustomer {
  id: number
  number?: string
  companyName?: string
  firstName?: string
  lastName?: string
}

export interface EasybillMatchingData {
  organizations: EasybillUnlinkedOrganization[]
  customers: EasybillUnlinkedCustomer[]
}

export interface EasybillConflict {
  organizationId: string
  organizationName: string
  easybillCustomerId?: number
  easybillCustomerNumber?: string
}

export interface EasybillSyncLog {
  id: string
  entityType: string
  entityId: string
  easybillId?: number
  direction: string
  status: string
  changes?: Record<string, unknown>
  errorMessage?: string
  createdAt: string
}

// =========================================================================
// Lookup Table Types
// =========================================================================

export interface AddressType {
  id: string
  name: string
  description?: string
  icon?: string
  isActive: boolean
  sortOrder: number
}

export interface AddressTypeCreate {
  name: string
  description?: string
  icon?: string
  isActive?: boolean
  sortOrder?: number
}

export interface AddressTypeUpdate {
  name?: string
  description?: string
  icon?: string
  isActive?: boolean
  sortOrder?: number
}

// CommunicationType wurde durch 4 Dimensionen ersetzt:
// CommunicationMedium, CommunicationChannel, CommunicationContext, CommunicationDevice
// (siehe @/types/index.ts)

export interface OrganizationTypeItem {
  id: string
  name: string
  description?: string
  icon?: string
  color?: string
  isActive: boolean
  sortOrder: number
}

export interface OrganizationTypeCreate {
  name: string
  description?: string
  icon?: string
  color?: string
  isActive?: boolean
  sortOrder?: number
}

export interface OrganizationTypeUpdate {
  name?: string
  description?: string
  icon?: string
  color?: string
  isActive?: boolean
  sortOrder?: number
}

// Scraping Types
export interface ScrapingResult {
  success: boolean
  sourceUrl?: string
  sourceType: string
  organization?: ExtractedOrganization
  contacts: ExtractedContact[]
  rawText?: string
  aiProvider?: string
  confidence: number
  errors: string[]
  existingOrganization?: ExistingOrganizationMatch | null
  comparisons?: ImportComparison[]
}

export interface ExistingOrganizationMatch {
  id: string
  name: string
  website: string
  description: string
  matchType: string
  confidence: number
  matchedFields: string[]
  communications: Array<{
    mediumName: string
    value: string
    label: string
    isPrimary: boolean
  }>
  addresses: Array<{
    street: string
    houseNumber: string
    postalCode: string
    city: string
    country: string
    isPrimary: boolean
  }>
}

export interface ImportComparison {
  field: string
  label: string
  importedValue: string
  existingValue: string
  status: "same" | "different" | "missing_existing" | "missing_import"
}

export interface ExtractedOrganization {
  name: string
  type: string
  website?: string
  description?: string
  addresses: ExtractedAddress[]
  communications: ExtractedCommunication[]
  contacts: ExtractedContact[]
}

export interface ExtractedContact {
  salutation?: string
  title?: string
  firstName?: string
  lastName?: string
  position?: string
  department?: string
  communications: ExtractedCommunication[]
}

export interface ExtractedAddress {
  street?: string
  houseNumber?: string
  postalCode?: string
  city?: string
  country?: string
  addressType?: string
  // HERE API Validierungsdaten
  latitude?: number
  longitude?: number
  herePlaceId?: string
  isValidated?: boolean
  formattedAddress?: string
}

export interface ExtractedCommunication {
  type: string
  value: string
  label?: string
}

export interface AddressSuggestion {
  title: string
  address: string
  placeId: string
  latitude?: number
  longitude?: number
}

export interface AddressLookupResult {
  street?: string
  houseNumber?: string
  postalCode?: string
  city?: string
  // Verwaltungsdaten
  district?: string
  county?: string
  state?: string
  stateCode?: string
  // Land
  country?: string
  countryCode?: string
  countryCodeAlpha3?: string
  // Geodaten
  latitude: number
  longitude: number
  formattedAddress: string
  placeId: string
}

export interface AddressValidationResult {
  isValid: boolean
  confidence: number
  message?: string
  formattedAddress?: string
  latitude?: number
  longitude?: number
  herePlaceId?: string
  corrections?: Record<string, string>
  countryCode?: string
  countryCodeAlpha3?: string
  countryName?: string
}

export interface AIProvidersInfo {
  primary: string
  fallback: string
  providers: { name: string; configured: boolean }[]
}

// =========================================================================
// Matrix Integration Types
// =========================================================================

export interface MatrixUserSuggestion {
  email: string | null
  suggestedUsername: string | null
  canCreate: boolean
  reason: string | null
  serverName: string | null
}

export interface CreateMatrixUserResult {
  success: boolean
  matrixUserId: string | null
  temporaryPassword: string | null
  passwordLoginEnabled: boolean
  emailSent: boolean
  error: string | null
}

export interface MatrixOnboardingStatus {
  configured: boolean
  matrixUserId: string | null
  serverName: string | null
  elementMobileUrl: string | null
  elementDownloadUrl: string
  cinnyWebUrl: string
  canActivate: boolean
  reason: string | null
}

export interface PortalMatrixStatus extends MatrixOnboardingStatus {
  enabled: boolean
  recoveryKeyStored: boolean
  recoveryKeySavedAt: string | null
  recoveryKeyMissing: boolean
}

export interface OrganizationMatrixSpace {
  id: string
  organizationId: string
  parentSpaceId: string | null
  matrixRoomId: string
  name: string
  topic: string | null
  kind: string
  managedBy: string
  isLocked: boolean
  isActive: boolean
  createdByContactId: string | null
}

export interface OrganizationMatrixRoom {
  id: string
  organizationId: string
  spaceId: string
  matrixRoomId: string
  name: string
  topic: string | null
  kind: string
  managedBy: string
  isLocked: boolean
  isActive: boolean
  createdByContactId: string | null
}

export interface OrganizationMatrixBot {
  id: string
  matrixUserId: string
  username: string
  displayname: string | null
  description: string | null
  organizationId: string | null
  botType: string
  isActive: boolean
  invitedRoomIds: string[] | null
}

export interface OrganizationMatrixStatus {
  hasSpace: boolean
  spaceId: string | null
  allgemeinRoomId: string | null
  supportRoomId: string | null
  onboardingBotUserId: string | null
  contactsWithMatrix: number
  managersCount: number
  spaces: OrganizationMatrixSpace[]
  rooms: OrganizationMatrixRoom[]
  onboardingBot: OrganizationMatrixBot | null
}

export interface CreateMatrixSpaceResult {
  success: boolean
  spaceId: string | null
  allgemeinRoomId: string | null
  supportRoomId: string | null
  onboardingBotUserId: string | null
  actions: string[]
  errors: string[] | null
  error: string | null
  spaces: OrganizationMatrixSpace[]
  rooms: OrganizationMatrixRoom[]
  onboardingBot: OrganizationMatrixBot | null
}

export interface OrganizationMatrixBotProvisionResult {
  success: boolean
  bot: OrganizationMatrixBot | null
  actions: string[]
  errors: string[] | null
  error: string | null
}

export interface CreateMatrixRoomResult {
  success: boolean
  roomId: string | null
  room: OrganizationMatrixRoom | null
  error: string | null
}

export interface MatrixMemberAssignment {
  portalCustomerId?: string
  contactId?: string
  matrixUserId?: string
}

export interface OrganizationMatrixMemberCandidate {
  id: string
  label: string
  detail: string | null
  source: string
  sourceId: string | null
  matrixUserId: string
  organizationId: string | null
  organizationName: string | null
  portalCustomerId: string | null
  contactId: string | null
}

export interface OrganizationMatrixMemberItem {
  matrixUserId: string
  displayName: string
  detail: string | null
  source: string
  sourceId: string | null
  organizationId: string | null
  organizationName: string | null
  portalCustomerId: string | null
  contactId: string | null
}

export interface PortalMatrixSpace {
  id: string
  parentSpaceId: string | null
  matrixRoomId: string
  name: string
  topic: string | null
  kind: string
  managedBy: string
  isLocked: boolean
}

export interface PortalMatrixRoom {
  id: string
  spaceId: string
  matrixRoomId: string
  name: string
  topic: string | null
  kind: string
  managedBy: string
  isLocked: boolean
}

export interface PortalMatrixStructure {
  spaces: PortalMatrixSpace[]
  rooms: PortalMatrixRoom[]
  onboardingBot: {
    matrixUserId: string
    displayname: string | null
  } | null
}

export interface CreateMatrixBotResult {
  success: boolean
  userId: string | null
  accessToken: string | null
  invitedRooms: string[]
  error: string | null
  errors: string[] | null
}

export interface MatrixBotItem {
  id: string
  matrixUserId: string
  username: string
  displayname: string | null
  accessToken: string
  description: string | null
  isActive: boolean
  invitedRoomIds: string[] | null
  createdAt: string
}

export interface MatrixBotListResponse {
  bots: MatrixBotItem[]
  total: number
}

// =========================================================================
// User Profile Types
// =========================================================================

export interface UserProfile {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  isActive: boolean
  isVerified: boolean
  lastLogin: string | null
  timezone: string | null
  preferences: UserPreferences | null
  gmailMonitoringEnabled: boolean
  meetUrl: string | null
  createdAt: string
  updatedAt: string
}

export interface UserEmailAccount {
  id: string
  email: string
  domain: string
  provider: string | null
  accountType: string
  isLoginEmail: boolean
  isPrimary: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface ProjectbaseSshKey {
  id: string
  label: string
  keyType: string
  fingerprint: string
  revokedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ProjectbaseSshAccess {
  enabled: boolean
  linuxUsername: string
  sshHost: string
  sshPort: number
  sshTarget: string
  workspacePath: string
  activeKeyCount: number
  keys: ProjectbaseSshKey[]
}

export interface ProjectbaseSshAccessCheck {
  name: string
  ok: boolean
  message: string
}

export interface ProjectbaseSshAccessTest {
  ok: boolean
  sshTarget: string
  sshPort: number
  workspacePath: string
  activeKeyCount: number
  checks: ProjectbaseSshAccessCheck[]
  hints: string[]
}

export interface ApiTokenItem {
  id: string
  name: string
  tokenPrefix: string
  scopes: string[]
  lastUsedAt: string | null
  expiresAt: string | null
  revokedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ApiTokenCreateResponse extends ApiTokenItem {
  token: string
}

export interface EmailSummaryGoogleSetupStatus {
  configured: boolean
  googleConnected: boolean
  missingScopes: string[]
  spreadsheetId: string | null
  spreadsheetUrl: string | null
  scriptId: string | null
  scriptUrl: string | null
  webhookTokenSuffix: string | null
  scriptError: string | null
  updatedAt: string | null
}

export interface MailAppPasswordItem {
  id: string
  description: string
  createdAt: string | null
  expiresAt: string | null
}

export interface MailAppPasswordStatus {
  isAvailable: boolean
  accountExists: boolean
  accountEmail: string | null
  username: string | null
  imapHost: string | null
  imapPort: number
  imapSecurity: string
  smtpHost: string | null
  smtpPort: number
  smtpSecurity: string
  appPasswords: MailAppPasswordItem[]
  message: string | null
}

export interface MailAppPasswordCreateResponse {
  id: string
  description: string
  secret: string
  accountEmail: string
  username: string
  imapHost: string
  imapPort: number
  imapSecurity: string
  smtpHost: string
  smtpPort: number
  smtpSecurity: string
}

// FilterState wird in den einzelnen Komponenten definiert
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FilterState = Record<string, any>

export interface GridView {
  name: string
  filters?: FilterState
  search?: string
  sorting?: { id: string; desc: boolean }[]
  columnVisibility?: Record<string, boolean>
  columnOrder?: string[]
  columnSizing?: Record<string, number>
  pageSize?: number
}

export interface UserPreferences {
  filters?: Record<string, FilterState>
  sidebarFavorites?: string[]
  gridViews?: Record<string, GridView[]>
}

// =========================================================================
// Meet-Ring Types
// =========================================================================

export interface MeetRingToken {
  id: string
  userId: string
  organizationId: string
  token: string
  ringUrl: string
  ringCount: number
  lastRungAt: string | null
  lastRungByName: string | null
  createdAt: string
}

export interface MeetRingContactSuggestion {
  id: string
  name: string
}

export interface MeetRingInfo {
  calleeName: string
  organizationName: string
  contacts: MeetRingContactSuggestion[]
  meetUrlSet: boolean
}

export interface MeetRingCallResponse {
  meetUrl: string | null
  message: string
}

export interface MeetCallParticipant {
  id: string
  userId: string
  name: string
  email: string
  role: "primary" | "participant" | string
  joinedAt: string
}

export interface MeetCall {
  id: string
  tenantId: string
  organizationId: string | null
  organizationName: string | null
  portalCustomerId: string | null
  contactId: string | null
  callerName: string
  callerEmail: string | null
  meetUrl: string
  note: string | null
  status: "ringing" | "accepted" | string
  acceptedByUserId: string | null
  acceptedByName: string | null
  acceptedAt: string | null
  expiresAt: string
  createdAt: string
  participants: MeetCallParticipant[]
}

export interface PublicMeetCallParticipant {
  id: string
  name: string
  role: "primary" | "participant" | string
  joinedAt: string
}

export interface PublicMeetCall {
  id: string
  organizationName: string | null
  callerName: string
  note: string | null
  status: "ringing" | "accepted" | string
  acceptedByName: string | null
  acceptedAt: string | null
  expiresAt: string
  createdAt: string
  participants: PublicMeetCallParticipant[]
}

export interface PortalMeetCallResponse {
  id: string
  status: string
  organizationName: string | null
  meetUrl: string
  expiresAt: string
  message: string
}

export interface OpenCloudDepartmentInfo {
  departmentId: string
  departmentName: string
  opencloudFolderPath: string | null
}

export type OpenCloudArea = "internal" | "portal"
export type OpenCloudDocumentType = "document" | "spreadsheet" | "presentation"

export interface OpenCloudStatusResponse {
  isConfigured: boolean
  hasArea: boolean
  hasPortalArea: boolean
  rootPath: string | null
  portalRootPath: string | null
  webUrl: string | null
  portalWebUrl: string | null
  departments: OpenCloudDepartmentInfo[]
  error: string | null
}

export interface OpenCloudEnsureResponse {
  success: boolean
  rootPath: string | null
  portalRootPath: string | null
  webUrl: string | null
  portalWebUrl: string | null
  departmentsCreated: number
  error: string | null
}

export interface OpenCloudFileItem {
  name: string
  path: string
  isDir: boolean
  size: number
  modified: string | null
  contentType: string | null
}

export interface OpenCloudFileListResponse {
  success: boolean
  hasArea: boolean
  area: OpenCloudArea
  path: string
  basePath: string
  webUrl: string | null
  files: OpenCloudFileItem[]
  error: string | null
}

export interface OpenCloudMutationResponse {
  success: boolean
  path: string | null
  webUrl: string | null
  error: string | null
}

export interface OpenCloudLinkResponse {
  success: boolean
  url: string | null
  error: string | null
}

export interface OpenCloudShareResponse {
  success: boolean
  shareId: string | null
  shareUrl: string | null
  path: string
  error: string | null
}

export interface ProjectBrainSourceUploadResponse {
  documentId: string
  folderId: string | null
  title: string
  filename: string
  size: number
  contentType: string | null
  opencloudArea: OpenCloudArea
  opencloudPath: string
  opencloudWebUrl: string | null
  vaultPrepared: boolean
  workspacePath: string | null
  vaultSourcePath: string | null
  vaultSourceWorkspacePath: string | null
  vaultWarnings: string[]
}

export interface ProjectBrainVaultPrepareResponse {
  projectId: string
  organizationId: string
  vaultRoot: string
  projectPath: string
  mainPath: string
  workspacePath: string
  userWorkspace: string
  brainFoldersCreated: number
  directoriesCreated: number
  filesWritten: number
  filesSkipped: number
  warnings: string[]
}

export interface ProjectBrainVaultSyncItemResponse {
  path: string
  action: string
  documentId: string | null
  folderId: string | null
  title: string | null
  contentHash: string | null
  reason: string | null
}

export interface ProjectBrainVaultSyncResponse {
  projectId: string
  organizationId: string
  workspacePath: string
  scanned: number
  created: number
  updated: number
  unchanged: number
  conflicts: number
  skipped: number
  warnings: string[]
  items: ProjectBrainVaultSyncItemResponse[]
}

export interface ShortLink {
  id: string
  code: string
  targetUrl: string
  shortUrl: string
  title: string | null
  organizationId: string | null
  isActive: boolean
  expiresAt: string | null
  clickCount: number
  lastClickedAt: string | null
  createdAt: string
  updatedAt: string
}

// Singleton instance
export interface SecureShareConfig {
  publicEnabled: boolean
  publicRateLimit: number
  publicRateWindowMinutes: number
  publicMaxSecretKb: number
  defaultTtlHours: number
  maxTtlHours: number
  defaultMaxViews: number
  maxViewsLimit: number
  maxFileCount: number
  maxFileSizeMb: number
  passphraseMode: string
  passphraseMaxAttempts: number
  cleanupIntervalMinutes: number
  burnGraceSeconds: number
  invitesEnabled: boolean
  inviteDefaultTtlHours: number
  inviteMaxTtlHours: number
  inviteAllowFiles: boolean
  hardMaxFileSizeMb: number
}

export interface SecureInvite {
  id: string
  label?: string | null
  recipientEmail?: string | null
  expiresAt: string
  maxUses?: number | null
  useCount: number
  allowFiles: boolean
  revoked: boolean
  active: boolean
  createdAt: string
}

export interface DokubaseKatalogEintrag {
  id: number
  name: string
  slug: string
}

export interface DokubaseFreigaben {
  freigaben: { typ: "regal" | "buch"; itemId: number; itemSlug: string; itemName: string }[]
  regale: DokubaseKatalogEintrag[]
  buecher: DokubaseKatalogEintrag[]
  konfiguriert: boolean
  warnings: string[]
  recoveryUrl?: string | null
  emailSent?: boolean | null
}

export interface UploadVorgangInfo {
  id: string
  status: string
  fehler: string | null
  driveFolderLink: string | null
  createdAt: string | null
  dateien: { name: string; groesse: number; status: string }[]
}

export interface UploadLinkInfo {
  id: string
  aktiv: boolean
  email: string
  expiresAt: string
  revokedAt: string | null
  createdAt: string | null
  createdBy: string
  vorgaenge: UploadVorgangInfo[]
}

export const api = new ApiService()

// React Query hooks helper
export function getQueryKey(
  resource: string,
  ...args: (string | Record<string, unknown>)[]
): (string | Record<string, unknown>)[] {
  return [resource, ...args]
}

export interface EmailActivityItem {
  id: string
  direction: "in" | "out" | string
  visibility: "team" | "private" | string
  source: string
  fromEmail: string
  fromName?: string | null
  toEmail: string
  subject?: string | null
  snippet?: string | null
  receivedAt: string
  contactId?: string | null
  organizationId?: string | null
  ticketId?: string | null
  ticketNumber?: string | null
  userId: string
  isOwner: boolean
  gmailUrl?: string | null
}

export interface PaginatedEmailActivities {
  items: EmailActivityItem[]
  total: number
  skip: number
  limit: number
}
